import type { RawRoute, TravelMode } from "@/api/routing";
import { applyMinutesRule, compareMeasuredScenery, measuredSceneryGainPct } from "@/utils/minutesRule";
import { getRouteCopy } from "@/locales/routeExplanations";
import type { OsmFeatures, OsmFeaturePoints, OsmEnvironment } from "@/api/osm";
import type { PoiHit } from "@/api/places";
import type { GooglePlaceHit } from "@/api/googlePlaces";
import type { CommunityTag } from "@/api/community";
import { recencyWeight } from "@/api/community";
import { bbox, cumulativeDistances, haversine, nearestDistance, nearestPointOnPolyline, samplePolyline, totalDistance, type LngLat } from "@/utils/polyline";
import { makesCoherentProgress, hasTightKnot, retracedMeters } from "@/utils/routeShape";
import { routesAreSameRoad } from "@/utils/routeTwins";
import { curatedRoadsTravelled, CURATED_WINS_SCENIC_SLOT, avoidDrivePenalty } from "@/utils/scenicRoads";
import { hudsonCoverage, hudsonScoreAdjustment } from "@/utils/hudsonRoadIndex";
import { regionRoadCoverage, regionRoadScoreAdjustment } from "@/utils/regionRoadIndex";
import { getRouteSource } from "@/lib/routeDiagnostics";
import { seasonFor, natureMultipliers, type Season } from "@/utils/seasonal";
import { SHIPPED_JULY_14_ROUTING } from "@/utils/routingBaseline";
import { fmtDistance } from "@/utils/units";

/**
 * WHO BUILT THE ROUTE (rule 16) — ONE definition, for every place that asks.
 *
 * A curated source is a road a human chose: Morgan's curation, or a thread
 * constructed from it. Everything else is a machine's guess, however good its
 * score. Rule 16 says provenance first, shape second; rule 13 protects these
 * same sources in the ratchet.
 *
 * This lived as a LOCAL const inside one of the two scenic pickers, so the
 * other picker — the one that decides the displayed card on default-tolerance
 * passes — couldn't see it and ranked candidates on `scores.scenic` alone.
 * A curated scenic-road therefore lost the Scenic slot to an anonymous
 * mapbox-pleasant whenever the machine's route happened to score higher.
 *
 * That is rule 20 in miniature: "the two layers must ask the question with the
 * SAME predicate — the engine and the display asking it differently is what
 * emptied the card." So it is defined once, here, and imported by both.
 */
export const CURATED_SOURCES = new Set([
  "scenic-road", "coastal-pch", "scenic-corridor", "calm-corridor",
]);

export const isCuratedSource = (s: { route: RawRoute }): boolean =>
  CURATED_SOURCES.has(getRouteSource(s.route) ?? "");

/**
 * The Pleasant time budget — the MOST a Pleasant (middle-ground) route may run
 * over the fastest route. It's a ceiling, never a minimum: a Pleasant route is
 * happily the SAME time as Direct (0 extra), just nicer. Morgan's rule:
 * proportional to the drive, CAPPED at +40 min — "nobody wants to spend an extra
 * hour going cross-country." Anything beyond this ceiling is the Scenic "long
 * way", not Pleasant.
 *
 * Ratio is 1:2 (half the drive), not 1:3 — real-drive evidence (Briarcliff→
 * Rhinebeck): a genuinely-pleasant ~78-min route ran +38 min, well inside "within
 * 40 minutes", but the old 1:3 ratio capped this trip at +26 min and shut it out.
 * Half-the-drive (capped at 40) lets that route qualify while still keeping short
 * city hops tight.
 */
export function pleasantBudgetSeconds(fastestDurationSec: number): number {
  const dur = Number.isFinite(fastestDurationSec) && fastestDurationSec > 0 ? fastestDurationSec : 0;
  // Pleasant rule (Morgan): up to +24% over the fastest. The ABSOLUTE ceiling
  // used to be a flat +40 min, which made road trips far too stingy — on an
  // 18-hour drive the "for you" Pleasant pick could add only 40 minutes, so a
  // genuinely lovely +2h coast/great-town detour got buried in the Scenic slot
  // and could never be the recommended route. (Morgan: "people take road trips
  // and want to stop on the way at pretty places… hug the coast or go through
  // great towns.")
  //
  // So the ceiling now SCALES with trip length: city hops stay tight (+40 min
  // for anything up to ~3h, unchanged), then the ceiling grows by ~20 min per
  // additional hour of driving, up to a road-trip-sized +4h. The 24%
  // proportional limit still applies, so the budget is min(scaled ceiling, 24%).
  // Looser budget (Morgan): 24% was far too stingy on short drives — a 39-min
  // I-95 hop gave only ~9 min of slack, so a genuinely nicer back-roads route
  // (inherently slower) fell out of the Pleasant slot and a near-direct main-
  // road route grabbed it. Bumped to 75% so a real calm alternative can be the
  // recommended Pleasant pick — e.g. the 61-min NY→Rowayton back-roads corridor
  // vs a 38-min I-95 fastest (+60%) was missing a ~61-min budget threshold by
  // seconds, which collapsed the fastest route into the Pleasant slot.
  // recommended Pleasant pick. The absolute ceiling (below) still caps how long
  // it can run, so this only loosens SHORT trips — long road trips remain
  // governed by the scaling ceiling.
  const proportional = 0.75 * dur;
  const hours = dur / 3600;
  // MORE ROOM ON LONGER TRIPS (Morgan, Aug 2: "the quality of the route is
  // what matters here"). The ceiling used to hold flat at +40 min until 3h,
  // which pinched exactly the trips people write in about — a 2.5h drive got
  // 40 min of slack, nowhere near enough to reach a coast or a great road.
  // Now it starts growing at 1.5h, 30 min per additional hour, up to +5h.
  // City hops are untouched: under 1.5h is +40 min, same as before.
  const ceilingMin = hours <= 1.5 ? 40 : Math.min(40 + (hours - 1.5) * 30, 300);
  return Math.min(ceilingMin * 60, proportional);
}
import { ABLATION } from "./ablation";
import { recordStage, recordRejection, recordMeasurement, attritionEnabled,
         setAttritionPass, roadsOfSteps } from "./attrition";

/** Kind of each nonlinear rule, for the shadow invariant: a ceiling may only
 *  lower a score, a floor may only raise it. Kept beside scoreTerms.ts's
 *  NONLINEAR_RULES; if the two ever disagree the shadow test fails. */
const __NONLINEAR_KIND: Record<string, "floor" | "ceiling" | "envelope"> = {
  // Subtracts up to -60 AND clamps at 0 — a penalty wearing a floor, so
  // neither invariant applies. Classified "envelope" to exempt it.
  communityAvoidPenalty: "envelope",
  avoidHighwaysCeiling: "ceiling",
  namedArterialCeiling: "ceiling",
  walkBorderlineCeiling: "ceiling",
  bikeUnsafeCeiling: "ceiling",
  brokenPromiseCeiling: "ceiling",
  epicHighwayLift: "floor",
  componentFloor: "floor",
  envelope0to100: "envelope",
};
import {
  segmentRoute,
  aggregateRouteDimensions,
  profileMatchScore,
  buildEvidence,
  type SegmentedRoute,
  type Dim,
  boxOf,
  cosMinOf,
  distanceLowerBoundM,
  buildLatStripIndex,
  latStripCandidates,
  distancePrefilterOn,
  DISTANCE_PRECHECK_SLACK_M,
  type LngLatBox,
} from "@/utils/segments";
import type { PleasantnessProfile } from "@/utils/profile";
import { prettyNeighborhoodCoverage } from "@/utils/prettyNeighborhoods";
import { cachedNominationsAsPretty } from "@/api/nominations";
import { timeAwareAdjustment } from "@/utils/timeAware";
import { niceTownBonus } from "@/utils/niceTowns";
import { cachedSegmentsInBbox, polylineSegmentKey } from "@/api/roadSegments";
import { mapPoisToSegments, type PoiSegmentMatch } from "@/utils/poiSegmentMap";
import type { ScenicSummary, NamedFeature } from "@/utils/greenPhrase";

/** The share of a route on motorway/trunk/primary at which our own copy
 *  starts calling it "Mostly on main roads" (routeReason.ts). ONE number,
 *  shared, because two of them drifted apart and the drift was visible on a
 *  card: Morgan, Aug 8, San Jose → Flagstaff — a Scenic card reading "Mostly
 *  on main roads". The copy said main roads at 45%; the SCENIC HONESTY gate
 *  only dropped the card at 60%. Anything in between wore the Scenic badge
 *  while describing itself as a highway route.
 *
 *  Rule 2: Scenic never IS a highway route. So the rule is simply: if we would
 *  DESCRIBE it as mostly main roads, it cannot be the Scenic card. */
export const MOSTLY_MAIN_ROADS_HWY_PCT = 45;

export type Preference =
  | "parks" | "coffee" | "shops" | "architecture"
  | "historic" | "monuments" | "quiet" | "no-highways"
  | "no-traffic" | "scenic" | "coastal" | "lively-streets";

export const PREF_LABELS: Record<Preference, string> = {
  // Order matters — PreferenceSelector renders this as a 2-col grid, so the
  // first 4 entries become the top two prominent rows:
  //   row 1: Avoid highways · Scenic roads
  //   row 2: Coastal views  · Coffee shops
  "no-highways": "Avoid highways",
  "scenic": "Scenic roads",
  "coastal": "Coastal views",
  "coffee": "Coffee shops",
  "shops": "Restaurants",
  "parks": "Parks / greenery",
  "architecture": "Architecture",
  "historic": "Historic streets",
  "lively-streets": "Lively streets",
  "monuments": "Monuments / landmarks",
  "quiet": "Quiet streets",
  "no-traffic": "Avoid heavy traffic",
};

/** Tunable weights for the "coastal" preference. Surfaced as named
 *  constants so we can A/B-tune how aggressively the scorer rewards
 *  genuinely waterside / ocean-side routes without hunting through inline
 *  magic numbers. All weights apply ONLY when the user opted into coastal. */
export const COASTAL_WEIGHTS = {
  /** preferenceMatchScore: pts per 100 m of OSM-confirmed coastline adjacency. */
  coastlinePtsPer100m: 1.2,
  /** preferenceMatchScore: pts per 100 m of any waterside (river/lake/canal). */
  watersidePtsPer100m: 0.5,
  /** preferenceMatchScore: bonus pts when a named ocean appears on the route. */
  namedOceanBonus: 25,
  /** scenicScore: pts per 100 m of coastline adjacency (additive). */
  scenicCoastlinePtsPer100m: 0.6,
  /** scenicScore: pts per 100 m of any waterside adjacency (additive). */
  scenicWatersidePtsPer100m: 0.25,
  /** pleasantScore: cap on the "I asked for the ocean and got it" bonus. */
  pleasantCoastalCap: 22,
};

export interface FeatureSummary {
  parks: number;
  cafes: number;
  shops: number;
  landmarks: number;
  waterPct: number;
  highwayPct: number;
  /** % of corridor length on busy non-highway arterials (secondary/tertiary).
   *  Used to penalize walks on roads like N Broadway that aren't motorways
   *  but are loud/scary on foot. */
  majorRoadPct: number;
  residentialPct: number;
  bikeLaneKm: number;
  /** Subset of bikeLaneKm: physically-separated cycleway infrastructure
   *  (track, separated lane, off-street path, dedicated cycleway).
   *  Optional — defaults to undefined when OSM enrichment didn't run. */
  protectedBikeKm?: number;
  /** Subset of bikeLaneKm: painted-only lanes (cycleway=lane / shared). */
  paintedBikeKm?: number;
  /** Km of bike-friendly path with smooth surface (asphalt/paved/concrete +
   *  smoothness good/excellent). Small micro-boost for biking. */
  smoothBikeKm?: number;
  /** Walk Score (0-100) averaged along the route. External-data source
   *  (Walk Score API). Walking-mode only. Optional — undefined when the
   *  Walk Score API key isn't configured or the lookup failed. */
  walkScore?: number;
  /** Number of OSM historic-district / historic-quarter polygons whose
   *  centroid falls in the route corridor. Small-lever bonus on
   *  Pleasant + Scenic — Boston Back Bay, Charleston, Savannah, Santa
   *  Fe core, Greenwich Village. Optional for backward compatibility. */
  historicDistrictCount?: number;
  /** Km of route corridor adjacent to tree-lined OSM features
   *  (natural=tree_row + natural=wood + landuse=forest). Free OSM
   *  proxy for "tree canopy" — biggest lever on walking, where shade
   *  matters most. Optional for backward compatibility. */
  treeLinedKm?: number;
  /** Median home value (USD) for the trip's centroid area, looked up
   *  from the embedded snapshot in src/data/medianHomeValue.ts. Small
   *  lever proxy for "well-maintained neighborhood character" — see
   *  PLEASANT_RULES.md Part 8 for the honest framing. Captures fancy-
   *  but-treeless neighborhoods (Santa Fe, Palm Beach, Carmel) that
   *  tree-canopy and OSM tags alone miss. Optional. */
  medianHomeValueUsd?: number;
  /** Km of route corridor on officially-designated scenic byways.
   *  OSM tag-based — Blue Ridge Parkway, Mohawk Trail, regional
   *  state scenic byways. Driving-mode small lever (#108). */
  scenicBywayKm?: number;
  /** Km of route on slow streets (maxspeed ≤30mph or residential).
   *  Driving signal — a 25mph residential street feels qualitatively
   *  better than a 45mph stroad even with similar OSM tags (#108). */
  slowStreetKm?: number;
  /** Km of route on fast arterials (maxspeed ≥40mph). Driving
   *  penalty beyond highway tracking — catches stroads (#108). */
  fastArterialKm?: number;
  /** Km of route on wide 4+-lane roads (OSM lanes ≥ 4). Driving penalty
   *  — a wide arterial is unpleasant even at moderate speed, and the
   *  penalty is scaled UP on short drives (Morgan). */
  fourLaneKm?: number;
  /** Corridor-bbox tallies. Context only — never route-specific scoring. */
  corridorSlowStreetKm?: number;
  corridorFastArterialKm?: number;
  corridorFourLaneKm?: number;
  /** Total elevation gain (meters) along the route. Biking +
   *  walking penalty signal (#109) — flat routes feel easier and
   *  more pleasant. Optional. */
  totalAscentM?: number;
  /** Steepest sustained grade % along the route. Biking + walking
   *  extra penalty above thresholds (#109). Optional. */
  maxGradePct?: number;
  /** Current precipitation rate at the trip centroid (mm/hr).
   *  All-modes penalty signal (#110). Optional. */
  precipitationMmHr?: number;
  /** Current temperature at the trip centroid (°C). Walking + biking
   *  penalty when extreme (#110). Optional. */
  temperatureC?: number;
  /** Current wind speed at the trip centroid (km/h). Biking-specific
   *  penalty when high (#110). Optional. */
  windKmh?: number;
  /** Current US EPA AQI (0-500) at the trip centroid. Walking +
   *  biking penalty above 100 (#111). Optional. */
  usAqi?: number;
  /** Sun altitude (degrees above horizon) at trip start. Walking
   *  mode shade bonus when sun is high + warm + tree-lined (#113).
   *  Optional. */
  sunAltitudeDeg?: number;
  /** Km of route along streets explicitly tagged as lit. Used at
   *  night to boost well-lit routes and penalize dark ones —
   *  walking strongest, biking moderate, driving small (#114).
   *  Optional. */
  litStreetKm?: number;
  /** People for Bikes City Rating (0-100) for the trip's centroid city,
   *  if it matches one in our embedded snapshot. Biking-mode-only
   *  small-lever signal — captures network connectivity / safety that
   *  per-segment OSM tags don't (e.g., Davis CA scores 95 even on
   *  unmarked residential streets because the network as a whole is
   *  cyclist-friendly). Optional. */
  peopleForBikesScore?: number;
  /** Walk Score's Bike Score (0-100) averaged along the route, from the same
   *  Walk Score lookup (bike=1). Biking-mode-only small-lever signal that
   *  complements the per-segment OSM lanes + the People for Bikes city rating.
   *  Optional — null/undefined when Walk Score isn't configured or US/Canada. */
  bikeScore?: number;
  /** Walk-friendly km (footway/path/pedestrian + roads with sidewalk≠no). */
  sidewalkKm: number;
  /** Roads explicitly tagged sidewalk=no — penalized for walking. */
  noSidewalkKm: number;
  /** Car-free km: dedicated footways / park trails / greenways / paths where
   *  cars aren't allowed (walk + bike). Set during walk scoring; lets the card
   *  recognize a genuinely calm, away-from-traffic route. */
  carFreeKm?: number;
  /** Walking only: points awarded by the MAIN STREET PREMIUM (shops/cafés per
   *  km), so the card and the fate log can say why a street won. */
  mainStreetBoost?: number;
  /** Optional named POIs / natural features along the route (best-effort, for storytelling). */
  parkNames?: string[];
  /** Parks/preserves the route VERIFIABLY runs THROUGH (Overpass `is_in`
   *  point-in-area containment). Unlike parkNames (which can include a park the
   *  route merely passes near), these are safe to claim as "through {park}". */
  throughParkNames?: string[];
  cafeNames?: string[];
  landmarkNames?: string[];
  /** Named lakes / reservoirs / rivers / canals / coastline the route runs near. */
  waterNames?: string[];
  /** Geometry-derived: meters of route polyline that run within ~80 m of an
   *  OSM water feature (coastline, river, canal, lake/reservoir/bay). This
   *  is the truthful "is this route actually waterside" signal — preferred
   *  over the count-based `waterPct` when callers want to claim a route is
   *  coastal/riverside without depending on epic-highway labels. */
  waterAdjacentMeters?: number;
  /** Subset of `waterAdjacentMeters` that runs along an ocean coastline
   *  (OSM `kind=coastline`). Lets the UI say "by the coast" only when the
   *  route is genuinely ocean-side, not just along an inland river. */
  coastlineAdjacentMeters?: number;
  /** Water features (with kind) the route runs along, ordered by the length
   *  of route adjacent to each. Lets the UI lead with the most prominent
   *  body of water — e.g. "Pacific Ocean" over a tiny creek. */
  waterAdjacency?: Array<{ name: string; kind: "coastline" | "river" | "canal" | "lake"; meters: number }>;
  /** WHAT KIND OF GREEN, from our own scenery layer rather than Overpass.
   *
   *  `parks` above is a single number that folds farmland, meadow, orchard,
   *  vineyard, forest and actual parks together (see src/api/osm.ts), which is
   *  why every green drive reaches the user as the same word. This carries the
   *  breakdown so the card can say "open farmland and orchards" where that is
   *  what the drive is (Morgan, Aug 13: "i want farmland to rank high
   *  everywhere").
   *
   *  `coveredFraction` is NOT decoration. Zeroes with a covered route mean
   *  "genuinely nothing here"; zeroes with an uncovered one mean "we have
   *  never looked". Anything reading `scenicLayer` must branch on it or it
   *  rebuilds rule 20's defect in the copy. Undefined = the lookup did not run
   *  (old cached route, or the layer was unreachable) — also not zero. */
  scenicLayer?: {
    summary: ScenicSummary | null;
    coveredFraction: number;
    region?: string;
    /** Features the route may honestly NAME. Gated in the database, not here:
     *  2km beside or inside a thing before along/through, distance for
     *  landmarks, named only. Absent = the lookup did not run. */
    named?: NamedFeature[];
  };
  /** Named woods / forests / nature reserves / farmland / meadows the route passes. */
  natureNames?: string[];
  /** Named historic sites / attractions near the route. */
  historicNames?: string[];
  /** Each named POI/feature snapped to its nearest polyline segment so the
   *  UI can show "this stretch of the route produced this recommendation".
   *  Sorted by distance-along the route. Optional — populated by
   *  summarizeFeatures whenever we have geometry to snap against. */
  poiSegments?: import("@/utils/poiSegmentMap").PoiSegmentMatch[];
}

export interface ScoredRoute {
  route: RawRoute;
  features: FeatureSummary;
  scores: {
    preferenceMatch: number;
    scenic: number;
    efficiency: number;
    calm: number;
    community: number;
    /** Display + eligibility value. componentFloor applied, clamped 0-100. */
    pleasant: number;
    /** Ordering value. Pre-componentFloor, unclamped below 0. Sorting only. */
    pleasantPreFloor?: number;
    /** New: 0..100 match against the user's selected pleasantness profile. */
    profileMatch: number;
  };
  /** Where the pleasant score came from, term by term. Observation only —
   *  no selection logic reads it. See scoreTerms.ts for what each label
   *  ultimately measures, and `unexplained` for anything unaccounted for. */
  breakdown?: {
    base: number;
    baseParts: { scenic: number; calm: number; community: number; blendedWithProfMatch: boolean };
    terms: Array<{ label: string; delta: number; applied: boolean }>;
    final: number;
    unexplained: number;
  };
  /** Per-dimension distance-weighted aggregate over the whole route. */
  dimensions: Record<Dim, number>;
  /** Human-readable evidence chips (e.g. "0.6 mi along waterfront"). */
  evidence: string[];
  explanation: string;
  /** Underlying segmented route (handy for richer UI later). */
  segmented?: SegmentedRoute;
  /** Highly-rated Google places sampled along the route — used by the
   *  map to drop clickable pins for cafés/shops/etc. */
  googlePlaces?: GooglePlaceHit[];
  /** Marked true when this route is physically unsafe in the route's
   *  travel mode (#124) — walking on a motorway shoulder, biking on a
   *  55mph trunk with no protection, etc. Unsafe routes are dropped
   *  from every category's selection pool, never surfaced as a card. */
  unsafe?: boolean;
}

export type Classification = "fastest" | "scenic" | "pleasant";

const BUSY_WALK_ROAD_KINDS = new Set(["motorway", "trunk", "primary", "secondary", "tertiary", "motorway_link", "trunk_link", "primary_link", "secondary_link", "tertiary_link"]);
const MAJOR_WALK_ROAD_KINDS = new Set(["secondary", "tertiary", "secondary_link", "tertiary_link"]);
const FOOT_WALK_KINDS = new Set(["path", "pedestrian", "living_street", "cycleway"]);
// SLOW STREETS, PER ROUTE (Morgan, Aug 22).
//
// NOT a new rule. osm.ts's own tally already treated `residential` and
// `living_street` as slow streets whenever a maxspeed tag was absent
// (src/api/osm.ts, the `else if (t.highway === "residential" || ...)` arms).
// This reuses that definition against the route's OWN road classes instead of
// every way in the corridor bbox. No threshold and no weight is touched.
const SLOW_STREET_ROAD_KINDS = new Set(["residential", "living_street"]);

/**
 * DEV ASSERTION: no single road-class distance may materially exceed the route.
 * The corridor-vs-route bug produced 36.0mi of "slow street" on a 3.8mi route
 * and nothing objected, because the only downstream guard was a
 * `Math.min(100, ...)` that turned 947% into a plausible-looking 100%. This
 * refuses to let that class of error be silent again. Dev-only; never throws.
 */
function assertClassDistanceSane(label: string, classKm: number | undefined, routeKm: number): void {
  if (!import.meta.env?.DEV) return;
  if (classKm == null || !Number.isFinite(classKm) || routeKm <= 0) return;
  if (classKm > routeKm * 1.05) {
    // eslint-disable-next-line no-console
    console.warn(
      `[features] IMPLAUSIBLE ${label}: ${classKm.toFixed(2)}km on a ${routeKm.toFixed(2)}km route ` +
      `(${(classKm / routeKm).toFixed(1)}x). A per-route distance cannot exceed the route.`,
    );
  }
}
function namedArterialMeters(route: RawRoute): number {
  return route.steps.reduce((sum, step) =>
    step.highTrafficName ? sum + step.distance : sum,
  0);
}

function routeKmNearKinds(route: RawRoute, env: OsmEnvironment | undefined, kinds: Set<string>, maxMeters: number): number {
  if (!env || route.coordinates.length < 2) return 0;
  const lines = env.lines.filter((l) => kinds.has(l.kind));
  if (lines.length === 0) return 0;
  // PLE-6: `nearestPointOnPolyline` takes an optional precomputed `cum`
  // (cumulative-distance) array specifically so a caller that re-tests the
  // SAME line many times — which this is, once per route segment — doesn't
  // rebuild it from scratch every call. Every other caller in this codebase
  // (poiSegmentMap.ts, scenicRoads.ts, NavigationView.tsx) already passes it;
  // this one didn't, so it was recomputing cumulativeDistances(l.line) on
  // every one of route.coordinates.length calls per line. `cum` only feeds
  // `along`/the hint tie-break inside nearestPointOnPolyline — neither of
  // which this function reads (no hintAlong is passed) — so `distance` here
  // is bit-for-bit unchanged; this is a cache, not a behavior change.
  // Measured on a Maine/Boston-scale synthetic route: ~10x faster.
  const lineCums = lines.map((l) => cumulativeDistances(l.line));
  let meters = 0;
  for (let i = 1; i < route.coordinates.length; i++) {
    const a = route.coordinates[i - 1];
    const b = route.coordinates[i];
    const mid: LngLat = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const onKind = lines.some((l, li) => nearestPointOnPolyline(mid, l.line, lineCums[li]).distance <= maxMeters);
    if (onKind) meters += Math.hypot((b[0] - a[0]) * 90000, (b[1] - a[1]) * 111000);
  }
  return meters / 1000;
}

/**
 * THE ORDERING VALUE (Morgan, Aug 22).
 *
 * componentFloor may only ever RAISE a score, so applying it before ordering
 * compresses candidates onto one number and hands the decision to whatever the
 * sort does with ties. Measured on four real corridors after the NaN,
 * road-class and deadline fixes were in:
 *
 *   Pleasantville  7 candidates, raw spread 41.2 pts -> all 55.0
 *   Ossining       5 candidates, raw spread 99.0 pts -> all 55.0
 *   White Plains   3 candidates, raw spread 71.1 pts -> all 39.0
 *   Katonah        5 candidates, raw spread 79.1 pts -> all 41.0
 *
 * and on Pleasantville it inverted the winner: raw 38.4 lost to raw -2.8.
 *
 * SORTING, WINNER SELECTION AND RANKING COMPARISONS ONLY. Eligibility and
 * safety thresholds still read `scores.pleasant`, which is unchanged - those
 * constants are tuned against the floored scale and migrating them is a
 * separate, audited step.
 *
 * Falls back to `pleasant` when `pleasantPreFloor` is absent, so a ScoredRoute built
 * by an older path still orders sanely instead of collapsing to 0.
 */
/**
 * THE PLEASANT SELECTOR'S OWN RECORD (Morgan, Aug 23).
 *
 * The shadow must mirror the EXACT comparison the selector made, not rebuild an
 * approximation of it — a reconstruction drifts the moment either side changes,
 * and this file has already been bitten by measuring the body of a thing rather
 * than the thing. So the selector writes down its contender set and the values
 * it actually compared, and the shadow only prints them.
 *
 * Note the sort has THREE keys, not one: runsScenicParkway is a DOMINANT
 * pre-key, then effective (= rankScore - directnessPenalty), then highwayPct
 * ascending as a tiebreak. A parkway route legitimately wins with a lower
 * effective value, so a verdict that looked only at `effective` would report a
 * false ordering failure.
 */
export interface PleasantContender {
  /**
   * The candidate's own route OBJECT, for identity comparison.
   *
   * A stamped property was the first attempt and it was wrong: the Pleasant
   * card is rebuilt as `{ ...s, classification }` in classifyThreeCards, and a
   * marker attached to the pool object does not reliably survive that copy —
   * which made an unstamped card look like a downstream override when the
   * selector and the door were in fact agreeing. The spread copies the
   * REFERENCE to the same route, so `card.route === contender.route` survives
   * every re-wrap. Never read for any decision.
   */
  route: unknown;
  roads: string;
  preFloor?: number;
  display: number;
  directness: number;
  effective: number;
  scenicParkway: boolean;
  highwayPct: number;
  durationMin: number;
}
export interface PleasantSelection {
  /** Monotonic within a search. Stamped onto every contender as __selSeq. */
  seq: number;
  /** Which classifyRoutes invocation: derived from the tolerance it was given. */
  pass: string;
  contenders: PleasantContender[];
  top: string | null;
  poolSize: number;
}
let SEL_SEQ = 0;
const PLEASANT_SELECTIONS: PleasantSelection[] = [];

/** Every Pleasant selection this search made, oldest first. */
export function pleasantSelections(): PleasantSelection[] { return PLEASANT_SELECTIONS; }

/**
 * THE RANKED CALM POOL, FOR THE DISPLAY (Morgan, Aug 23 — the eight-corridor set).
 *
 * The display re-derives its own Pleasant pick, and it was doing so from the
 * three cards this function returns. On every long corridor that is the wrong
 * set: the engine ranked 4-5 calm, in-budget candidates, handed back three
 * cards, and the display then chose Pleasant from `cards minus the Fast anchor`
 * — which is the direct route (a highway route on any 40-mile trip) plus the
 * Scenic long way (over the Pleasant budget by construction). Two candidates,
 * both correctly rejected, `pleasant=none` on six of eight.
 *
 *   Stamford  pool=10 → eligible=7 → calm=4   display saw 2
 *   SF        pool=11 → eligible=7 → calm=5   display saw 2
 *   Denver    pool=9  → eligible=8 → calm=5   display saw 2
 *
 * No thresholds move: `isHighwayHeavy`, the time cap and calmEnough all still
 * judge each candidate exactly as before. This only lets the display SEE the
 * candidates the engine already ranked, instead of reconstructing the choice
 * from three seated cards.
 *
 * `cards` is recorded alongside so a reader can tell whether the pool belongs
 * to the render it is looking at. Route generation races (rule 18), so a
 * consumer must check that at least one recorded card is present in what it is
 * displaying and otherwise ignore the pool — stale is worse than absent.
 */
export interface PleasantPoolSnapshot {
  /** Calm, in-budget, measured candidates, best first by rankScore. */
  pool: ScoredRoute[];
  /** The cards the same call returned — the freshness key. */
  cards: ClassifiedRoute[];
}
let PLEASANT_POOL_SNAPSHOT: PleasantPoolSnapshot | null = null;
export function pleasantPoolSnapshot(): PleasantPoolSnapshot | null {
  return PLEASANT_POOL_SNAPSHOT;
}
export function recordPleasantPool(pool: ScoredRoute[]): void {
  PLEASANT_POOL_SNAPSHOT = { pool: pool.slice(), cards: [] };
}
export function stampPleasantPoolCards(cards: ClassifiedRoute[]): void {
  if (PLEASANT_POOL_SNAPSHOT) PLEASANT_POOL_SNAPSHOT.cards = cards.slice();
}
export function clearPleasantSelections(): void { PLEASANT_SELECTIONS.length = 0; SEL_SEQ = 0; }
/**
 * The selection a given classified card came from, by ROUTE-OBJECT IDENTITY.
 *
 * `{ ...scoredRoute, classification }` copies the reference to the same `route`
 * object, so this holds across every re-wrap in classifyThreeCards. The earlier
 * property-stamp approach did not, and produced a false "downstream override"
 * reading on 23 Aug.
 */
export function selectionForCard(card: unknown): PleasantSelection | undefined {
  const r = (card as { route?: unknown } | undefined)?.route;
  if (r === undefined) return undefined;
  return PLEASANT_SELECTIONS.find((x) => x.contenders.some((c) => c.route === r));
}

let RANK_FALLBACKS = 0;
// Published from the start, so 0 is readable as "instrumented, none happened"
// rather than being indistinguishable from "the counter never ran".
try {
  (globalThis as unknown as Record<string, unknown>).__rankFallbacks = 0;
} catch { /* non-browser host */ }

/**
 * How many times rankScore() had to fall back to the FLOORED `pleasant` value
 * because a candidate carried no `pleasantPreFloor`. On a clean run this is
 * zero: every candidate is scored by scoreRoute, which always sets the field.
 * A non-zero count means some path is constructing ScoredRoutes another way,
 * and those candidates are being ordered on a floored number — i.e. the
 * separation is not actually in force for them.
 */
export function rankScoreFallbackCount(): number { return RANK_FALLBACKS; }
export function resetRankScoreFallbackCount(): void { RANK_FALLBACKS = 0; }

function rankScore(s: { scores: { pleasant: number; pleasantPreFloor?: number } }): number {
  const r = s.scores.pleasantPreFloor;
  if (Number.isFinite(r)) return r as number;
  RANK_FALLBACKS++;
  if (RANK_FALLBACKS === 1) {
    // Once, not per candidate — the count is the signal, the first one just
    // says the condition exists at all.
    // eslint-disable-next-line no-console
    console.warn(
      "[scoring] rankScore FELL BACK to the floored `pleasant` — this candidate " +
      "has no pleasantPreFloor, so componentFloor is still able to affect its " +
      "ordering. Expected count on a clean run: 0.",
    );
  }
  try {
    (globalThis as unknown as Record<string, unknown>).__rankFallbacks = RANK_FALLBACKS;
  } catch { /* non-browser host — the exported getter still works */ }
  return s.scores.pleasant ?? 0;
}

function routeClassKm(route: RawRoute, classes: Set<string>): number {
  const metersByClass = route.roadClassMeters ?? {};
  let meters = 0;
  for (const c of classes) meters += metersByClass[c] ?? 0;
  return meters / 1000;
}

export interface ClassifiedRoute extends ScoredRoute {
  classification: Classification;
  /** True when the "pleasant" pick is geometrically the same as the
   *  fastest route — i.e. the fastest route is already the most pleasant
   *  one we could find. The UI uses this to render a small note instead
   *  of pretending two distinct options exist. */
  sameAsFastest?: boolean;
  /** When `sameAsFastest` is true, a structured explanation of WHY the
   *  pleasant pick collapsed onto the fastest route. Drives the
   *  "Why same as Fastest?" panel in RouteCard so users can tell whether
   *  they're seeing a real degenerate case (no alternatives in the
   *  routing engine, all alternatives shared geometry, every option failed
   *  the pleasantness bar, or filters like avoid-highways excluded them).
   *  `details` carries diagnostic numbers used by the UI. */
  sameAsFastestReason?: {
    code:
      | "no_alternatives"   // routing engine returned only one route
      | "polyline_match"    // top pleasant candidate ≥80% same geometry as fastest
      | "filtered_out"      // alternatives existed but were excluded (avoid-highways/duration cap)
      | "score_degenerate"  // alternatives existed but none cleared the pleasantness bar
      | "unknown";
    /** Number of distinct geometries Mapbox returned for this trip. */
    candidateCount: number;
    /** Top pleasant candidate's pleasant score (when one was evaluated). */
    bestAltPleasantScore?: number;
    /** Score the candidate had to beat to qualify as a pleasant pick. */
    minPleasantScore?: number;
    /** Geometry overlap (Jaccard 0..1) between top alternative and fastest. */
    bestAltOverlap?: number;
  };
  /** (#151 / #152) When a slot collapses because there's no genuinely
   *  different option — Pleasant has no pleasantness edge over Direct,
   *  or Scenic's polyline ≥85% overlaps Pleasant's — set this field
   *  to the OTHER slot's classification. The UI renders "Same as the
   *  [Fastest / Pleasant] route" copy so the user understands why a
   *  card is showing the same line as another. Distinct from
   *  `sameAsFastest`, which is the pre-existing diagnostic flag for
   *  the pleasant-vs-engine-fastest comparison. */
  sameAsClass?: "fastest" | "pleasant";
  /** (#155) Cheap-path-only flag: Scenic collapsed because scenic
   *  anchors (parks, water, scenic byways from OSM) hadn't loaded yet.
   *  The card renders "Searching scenic..." copy instead of the
   *  generic "Same as the Pleasant route" copy. Once background
   *  enrichment finishes and feeds real anchors into the next
   *  classifyThreeCards call, this flag is no longer set. */
  scenicSearching?: boolean;
  /** Optional descriptive label for THIS pleasant variant when we surface
   *  multiple distinct pleasant routes over long distances (e.g.
   *  "Ocean view", "Mountain pass", "Forest drive"). The UI displays this
   *  in place of the generic "Pleasant" label so users can choose between
   *  flavors of pleasantness. */
  variantLabel?: string;
  /** One-line description that goes under the variant label. */
  variantSub?: string;
  /** CANDIDATE-LIST METADATA, NOT A CARD DECISION (Morgan, Aug 23).
   *
   *  classifyRoutes hands the SECOND entry of its pleasant and scenic lists an
   *  "Alternate" label, which is meaningful while you are looking at a list of
   *  candidates and meaningless once one of them is seated as the primary card.
   *  It used to ride along: after the Direct slot was corrected, Neperan's
   *  Pleasant card arrived carrying "Alternate / A different pleasant path"
   *  beside a Scenic card saying "Alternative", and neither card said what it
   *  actually was.
   *
   *  This flag marks a label as list metadata so the primary card can drop it
   *  WITHOUT touching labels a rule set on purpose — the borderline
   *  "Alternative / Another good way" Pleasant, or the displaced card that
   *  rules 0-2 move to Scenic. Matching on the copy string would not tell
   *  those apart; this does. */
  candidateListLabel?: boolean;
}

export const BUFFER_BY_MODE: Record<TravelMode, number> = {
  walking: 150, biking: 250, driving: 500,
};

function clamp(n: number, lo = 0, hi = 100): number {
  return Math.max(lo, Math.min(hi, n));
}

export function summarizeFeatures(
  route: RawRoute,
  osm: OsmFeatures,
  pois: { cafes: PoiHit[]; shops: PoiHit[]; landmarks: PoiHit[] },
  buffer: number,
  extras?: {
    osmPoints?: OsmFeaturePoints;
    osmEnv?: OsmEnvironment;
  },
): FeatureSummary {
  const sampled = samplePolyline(route.coordinates, 250);
  const km = route.distance / 1000;
  // Unknown stays unknown: with no roadClassMeters at all we cannot say a route
  // has zero slow streets, only that nobody measured it.
  const routeSlowStreetKm = route.roadClassMeters
    ? routeClassKm(route, SLOW_STREET_ROAD_KINDS)
    : undefined;
  assertClassDistanceSane("slowStreetKm", routeSlowStreetKm, km);
  // Filter POIs to those near the route line; capture both count and names.
  const nearList = (hits: PoiHit[]) =>
    hits.filter((h) =>
      sampled.some((s) =>
        Math.hypot((h.center[0] - s[0]) * 90000, (h.center[1] - s[1]) * 111000) < buffer,
      ),
    );

  // STRICT buffer for "you're right at this landmark" claims. The general
  // buffer (150m walking) is too loose for famous landmarks: a route along
  // the south bank of a river will catch a landmark on the north bank
  // ("routed past the Louvre") even though they're separated by water.
  // Cap landmark/historic naming much tighter so we only claim a famous
  // place when the route is genuinely on the same block.
  const landmarkBuf = Math.min(buffer, 70);
  const nearStrict = (hits: PoiHit[]) =>
    hits.filter((h) =>
      sampled.some((s) =>
        Math.hypot((h.center[0] - s[0]) * 90000, (h.center[1] - s[1]) * 111000) < landmarkBuf,
      ),
    );

  const nearCafes = nearList(pois.cafes);
  const nearShops = nearList(pois.shops);
  // Landmarks: count uses the loose buffer (signals neighborhood density),
  // but the *names* we surface to the user use the strict buffer.
  const nearLandmarks = nearList(pois.landmarks);
  const nearLandmarksStrict = nearStrict(pois.landmarks);

  // Highway % from the *actual route* (Mapbox step classes) when available.
  //
  // (#139) RULE FIX: "highway" for Pleasant scoring is ONLY motorway + trunk
  // (and their entrance/exit ramps). `primary` roads are NOT highways —
  // they're normal connector roads. Bedford Rd in Sleepy Hollow is tagged
  // primary but is perfectly nice; the Taconic is tagged motorway/trunk
  // and is genuinely unpleasant.
  //
  // The OLD rule lumped primary in with motorway + trunk, which made
  // Pleasant penalize Bedford Rd identically to the Taconic — a real-drive
  // bug Morgan flagged 2026-06-02.
  //
  // SPECIFIC NAMED bad arterials (Albany Post Road, Saw Mill Parkway,
  // Taconic, Saw Mill River Road, named Parkways) are still penalized
  // via `namedArterialMeters` — they're tagged primary in OSM but they
  // ARE high-traffic stoplight-heavy corridors. Catch them by name, not
  // by class.
  const totalRoadKm = osm.residentialKm + osm.highwayKm + osm.majorRoadKm + 0.001;
  const motorwayKm = (route.motorwayMeters ?? 0) / 1000;
  const motorwayTrunkKm = (
    (route.roadClassMeters?.motorway ?? 0) +
    (route.roadClassMeters?.motorway_link ?? 0) +
    (route.roadClassMeters?.trunk ?? 0) +
    (route.roadClassMeters?.trunk_link ?? 0)
  ) / 1000;
  const namedArterialKm = namedArterialMeters(route) / 1000;
  // Per-route data when available; fall back to motorway-only and then
  // to OSM aggregate. Aggregate fallback over-counts (it includes
  // primary) but is rare in practice — almost every route ships with
  // roadClassMeters populated.
  // AN EMPTY MAP IS NOT A MEASUREMENT (Morgan, Aug 9 — she called the shot:
  // "unknown highway percentage and 0% highway are completely different
  // facts").
  //
  // Google's carrier ships `roadClassMeters: {}` — an empty object, which is
  // NOT null. So `!= null` passed, the numerator was computed over nothing,
  // and highwayPct came out a confident 0. Every ranking that reads it then
  // saw the calmest route in the pool:
  //
  //   [pleasant] Direct reference is unmeasured (google-direct)   ← we KNEW
  //   [diag]     pleasant:google-direct/76m/hwy0/com0/pl55        ← and said 0%
  //
  // The comment that used to sit here assumed the problem away — "almost every
  // route ships with roadClassMeters populated". Google doesn't, and it has
  // been in the pool since July 17.
  //
  // Same for the motorwayMeters branch: Google sends 0, which is a default and
  // not an observation. Road-class evidence means a populated map or steps that
  // actually carry classes — the identical predicate the engine and the display
  // now use for "was this measured" (rule 20: both layers, one question).
  //
  // NOT the complete fix. highwayPct is typed `number`, so the honest value —
  // null — can't be expressed here yet; unmeasured routes now fall through to
  // the OSM corridor aggregate instead of fabricating a zero. Making the field
  // nullable is the real repair and touches every reader of it.
  const hasRoadClassEvidence =
    Object.keys(route.roadClassMeters ?? {}).length > 0 ||
    (route.steps ?? []).some((s) => (s.classes ?? []).length > 0);
  const routeHighwayPct = km > 0 && hasRoadClassEvidence
    ? clamp((Math.max(motorwayTrunkKm, namedArterialKm) / km) * 100)
    : km > 0 && (route.motorwayMeters ?? 0) > 0
      ? clamp((Math.max(motorwayKm, namedArterialKm) / km) * 100)
      : clamp((osm.highwayKm / totalRoadKm) * 100);
  const majorRoadPct = clamp((osm.majorRoadKm / totalRoadKm) * 100);

  // ---- Named natural features near the route line ----
  // Use a generous buffer (1.5x) for naming since these are big areas
  // (a lake centroid can be 500m from the road that hugs it).
  const nameBuf = buffer * 1.5;
  const nearNamedPoint = (pts?: { lng: number; lat: number; name?: string }[]) =>
    (pts ?? [])
      .filter((p) => p.name)
      .filter((p) =>
        sampled.some(
          (s) => Math.hypot((p.lng - s[0]) * 90000, (p.lat - s[1]) * 111000) < nameBuf,
        ),
      )
      .map((p) => p.name as string);

  // STRICT named-point lookup for landmarks/historic sites — same idea as
  // nearStrict above. Only name a famous place if the route is essentially
  // on the same block (not across a river or a major road).
  const strictNamedPoint = (pts?: { lng: number; lat: number; name?: string }[]) =>
    (pts ?? [])
      .filter((p) => p.name)
      .filter((p) =>
        sampled.some(
          (s) => Math.hypot((p.lng - s[0]) * 90000, (p.lat - s[1]) * 111000) < landmarkBuf,
        ),
      )
      .map((p) => p.name as string);

  const dedupe = (arr: string[], n: number) =>
    Array.from(new Set(arr.map((s) => s.trim()).filter(Boolean))).slice(0, n);

  // Water: from osmPoints.water (lakes/ponds/reservoirs/bays) +
  // any named "water" area centroids in osmEnv.points (covers cases the
  // dedicated water query missed) + named river/canal/coastline lines.
  // We use a MUCH wider buffer for water naming (3x the mode buffer) because
  // lake/reservoir polygons are stored as a single centroid that can sit
  // hundreds of meters from the road that hugs the shore. The user wants
  // every body of water the route drives by to be NAMED.
  const waterBuf = buffer * 3;
  const nearWaterPoint = (pts?: { lng: number; lat: number; name?: string }[]) =>
    (pts ?? [])
      .filter((p) => p.name)
      .filter((p) =>
        sampled.some(
          (s) => Math.hypot((p.lng - s[0]) * 90000, (p.lat - s[1]) * 111000) < waterBuf,
        ),
      )
      .map((p) => p.name as string);
  const waterPointNames = nearWaterPoint(extras?.osmPoints?.water);
  const waterEnvPointNames = nearWaterPoint(
    (extras?.osmEnv?.points ?? []).filter((p) => p.kind === "water"),
  );
  const waterLineNames = (extras?.osmEnv?.lines ?? [])
    .filter((l) => l.name && (l.kind === "river" || l.kind === "canal" || l.kind === "coastline"))
    .filter((l) =>
      l.line.some((p) =>
        sampled.some((s) => Math.hypot((p[0] - s[0]) * 90000, (p[1] - s[1]) * 111000) < waterBuf),
      ),
    )
    .map((l) => l.name as string);
  const waterNames = dedupe([...waterPointNames, ...waterEnvPointNames, ...waterLineNames], 6);

  // Nature: woods/forests/reserves/meadows/farmland from osmEnv points.
  const natureKinds = new Set([
    "wood", "forest", "nature_reserve", "meadow", "farmland", "orchard", "open_green", "viewpoint",
  ]);
  const natureNames = dedupe(
    (extras?.osmEnv?.points ?? [])
      .filter((p) => p.name && natureKinds.has(p.kind))
      .filter((p) =>
        sampled.some((s) => Math.hypot((p.lng - s[0]) * 90000, (p.lat - s[1]) * 111000) < nameBuf),
      )
      .map((p) => p.name as string),
    4,
  );

  // Historic: from osmPoints.historic (already has names).
  const historicNames = dedupe(strictNamedPoint(extras?.osmPoints?.historic), 4);

  // Parks: union of (osm as any).parkNames (legacy) and named park points filtered to route.
  const legacyParkNames: string[] = ((osm as any).parkNames ?? []) as string[];
  const filteredParkNames = nearNamedPoint(extras?.osmPoints?.parks);
  const parkNames = dedupe([...filteredParkNames, ...legacyParkNames], 4);

  // ---- Snap each named POI / feature onto its nearest route segment ----
  // Drives the "this stretch produced this recommendation" UI. Pulls from
  // the same near-route POI sets we just computed so the snap stays
  // consistent with what we're already counting/scoring.
  const poiInputs: Parameters<typeof mapPoisToSegments>[1] = [];
  for (const c of nearCafes) {
    if (c.name) poiInputs.push({ name: c.name, point: c.center, kind: "cafe", source: "preferenceMatch" });
  }
  for (const s of nearShops) {
    if (s.name) poiInputs.push({ name: s.name, point: s.center, kind: "shop", source: "preferenceMatch" });
  }
  for (const l of nearLandmarksStrict) {
    if (l.name) poiInputs.push({ name: l.name, point: l.center, kind: "landmark", source: "preferenceMatch" });
  }
  for (const p of (extras?.osmPoints?.parks ?? [])) {
    if (p.name) poiInputs.push({ name: p.name, point: [p.lng, p.lat], kind: "park", source: "scenic" });
  }
  for (const p of (extras?.osmPoints?.water ?? [])) {
    if (p.name) poiInputs.push({ name: p.name, point: [p.lng, p.lat], kind: "water", source: "scenic" });
  }
  for (const p of (extras?.osmPoints?.historic ?? [])) {
    if (p.name) poiInputs.push({ name: p.name, point: [p.lng, p.lat], kind: "historic", source: "scenic" });
  }
  const natureKindsForSnap = new Set(["wood", "forest", "nature_reserve", "meadow", "viewpoint", "open_green"]);
  for (const p of (extras?.osmEnv?.points ?? [])) {
    if (!p.name) continue;
    if (!natureKindsForSnap.has(p.kind)) continue;
    const kind: PoiSegmentMatch["kind"] = p.kind === "viewpoint" ? "viewpoint" : "nature";
    poiInputs.push({ name: p.name, point: [p.lng, p.lat], kind, source: "scenic" });
  }
  // Snap radius scales with the corridor buffer so we keep the matches
  // we already considered "near the route" but stay tighter than the
  // loose naming buffer (which can be 3× the mode buffer for water).
  const poiSegments = mapPoisToSegments(route.coordinates, poiInputs, Math.max(80, buffer * 1.4));

  // ---- Geometry-derived water adjacency ----
  // Walk the route polyline once and, for each ~250m sample step, measure
  // its distance to nearby OSM water features (coastline, river, canal,
  // and lake/reservoir/bay points). Tally per-feature meters so we can:
  //   1) tell the UI exactly how much of the route is genuinely waterside,
  //   2) distinguish ocean coastline from inland river so wording is honest,
  //   3) name the most prominent body of water (longest adjacency).
  const ADJ_LINE_M = 90;   // route is "along" a coastline/river within 90 m
  const ADJ_POINT_M = 220; // lake centroids can sit far from the shore road
  const stepM = (route.distance > 0 && route.coordinates.length > 1)
    ? route.distance / (route.coordinates.length - 1)
    : 0;
  // Include lake/reservoir shorelines — they're emitted as lines from the
  // OSM extractor specifically so we can measure distance-to-shore the same
  // way we measure distance-to-river. Without this a route hugging a
  // reservoir registered 0 water-adjacent meters because the centroid sat
  // far from the road.
  const waterLines = (extras?.osmEnv?.lines ?? []).filter(
    (l) => l.kind === "coastline" || l.kind === "river" || l.kind === "canal" || l.kind === "water",
  );
  const waterPoints = (extras?.osmPoints?.water ?? []);
  // PLE-6 ("iOS Crashes"): every iteration of the route-segment loop below
  // re-tests the SAME waterLines, but `nearestPointOnPolyline` wasn't given
  // the `cum` (cumulative-distance) array it accepts for exactly this case —
  // so it was rebuilding cumulativeDistances(l.line) from scratch on every
  // call, i.e. route.coordinates.length times per line. For a short local
  // trip this is noise; measured on a Maine/Boston-scale synthetic route
  // (~6,000 route points x 80 water lines) this loop alone took 43.5s —
  // long enough, run synchronously with no `await` inside it, to explain
  // Morgan's Oct 2 report of taps freezing and
  // `WKDeferringGestureRecognizer ... blocking its subgraph` during long
  // trips. `cum` only feeds `along`/the hint tie-break inside
  // nearestPointOnPolyline, and nothing below passes `hintAlong`, so
  // `distance` is bit-for-bit unchanged — this is a cache, not a behavior
  // change. Caching it measured ~10x faster (43.5s -> 4.0s) on that same
  // synthetic route.
  const waterLineCums = waterLines.map((l) => cumulativeDistances(l.line));
  // PLE-6, second pass. Profiled after the cum fix: this loop still ran about
  // 2.65 billion projection steps on one Maine route, because every route
  // point was measured against every water line in the download. A line can
  // only count if it is within ADJ_LINE_M (about 295 feet), so lines are
  // visited through the latitude-strip index and skipped when a lower bound
  // on their distance already exceeds ADJ_LINE_M (see segments.ts for the
  // proof). Neither can change a result. 1-point lines take
  // nearestPointOnPolyline's haversine path, so they are always visited.
  const waterLineBoxes: LngLatBox[] = waterLines.map((l) => boxOf(l.line));
  const waterLineIdx = buildLatStripIndex(waterLineBoxes, (i) => waterLines[i].line.length < 2);
  const prefilterWater = distancePrefilterOn();
  const allWaterLineIdx = waterLines.map((_, i) => i);
  const adjByName = new Map<string, { kind: "coastline" | "river" | "canal" | "lake"; meters: number }>();
  let waterAdjacentMeters = 0;
  let coastlineAdjacentMeters = 0;
  if (route.coordinates.length >= 2 && (waterLines.length || waterPoints.length)) {
    for (let i = 1; i < route.coordinates.length; i++) {
      const a = route.coordinates[i - 1];
      const b = route.coordinates[i];
      const mid: LngLat = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const segM = stepM > 0 ? stepM : Math.hypot((b[0] - a[0]) * 90000, (b[1] - a[1]) * 111000);
      let bestName: string | undefined;
      let bestKind: "coastline" | "river" | "canal" | "lake" | undefined;
      let bestDist = Infinity;
      let nearCoast = false;
      const midBox: LngLatBox = { minLng: mid[0], maxLng: mid[0], minLat: mid[1], maxLat: mid[1] };
      const midCos = cosMinOf(midBox);
      const lineIdxs = prefilterWater
        ? latStripCandidates(waterLineIdx, mid[1], mid[1], ADJ_LINE_M)
        : allWaterLineIdx;
      for (const li of lineIdxs) {
        const l = waterLines[li];
        if (
          prefilterWater && l.line.length >= 2 &&
          distanceLowerBoundM(midBox, waterLineBoxes[li], midCos) > ADJ_LINE_M + DISTANCE_PRECHECK_SLACK_M
        ) continue;
        const d = nearestPointOnPolyline(mid, l.line, waterLineCums[li]).distance;
        if (d <= ADJ_LINE_M && d < bestDist) {
          bestDist = d;
          bestName = l.name;
          // Display label, not the scoring kind: a `water` shoreline is still
          // "lake" in the copy a person reads.
          bestKind = l.kind === "water" ? "lake" : (l.kind as "coastline" | "river" | "canal");
        }
        if (l.kind === "coastline" && d <= ADJ_LINE_M) nearCoast = true;
      }
      for (const p of waterPoints) {
        const d = Math.hypot((p.lng - mid[0]) * 90000, (p.lat - mid[1]) * 111000);
        if (d <= ADJ_POINT_M && d < bestDist) {
          bestDist = d;
          bestName = p.name;
          bestKind = "lake";
        }
      }
      if (bestKind) {
        waterAdjacentMeters += segM;
        if (nearCoast) coastlineAdjacentMeters += segM;
        const key = bestName ?? `__unnamed_${bestKind}`;
        const prev = adjByName.get(key);
        adjByName.set(key, {
          kind: bestKind,
          meters: (prev?.meters ?? 0) + segM,
        });
      }
    }
  }
  const waterAdjacency = Array.from(adjByName.entries())
    .map(([name, v]) => ({ name: name.startsWith("__unnamed_") ? "" : name, kind: v.kind, meters: Math.round(v.meters) }))
    .filter((a) => a.meters >= 50)
    .sort((a, b) => b.meters - a.meters)
    .slice(0, 4);

  return {
    parks: osm.parks,
    cafes: nearCafes.length,
    shops: nearShops.length,
    landmarks: nearLandmarks.length,
    waterPct: clamp((osm.waterBodies / Math.max(1, km)) * 25, 0, 100),
    highwayPct: routeHighwayPct,
    majorRoadPct,
    residentialPct: clamp((osm.residentialKm / totalRoadKm) * 100),
    bikeLaneKm: osm.bikeLaneKm,
    protectedBikeKm: osm.protectedBikeKm,
    paintedBikeKm: osm.paintedBikeKm,
    smoothBikeKm: osm.smoothBikeKm,
    sidewalkKm: osm.sidewalkKm,
    noSidewalkKm: osm.noSidewalkKm,
    historicDistrictCount: osm.historicDistrictCount,
    treeLinedKm: osm.treeLinedKm,
    scenicBywayKm: osm.scenicBywayKm,
    // ROUTE-LEVEL, NOT CORRIDOR-LEVEL (Morgan, Aug 22).
    //
    // These three were the last road-class levers still reading osm.*, which is
    // a tally of every OSM way inside the route's padded BBOX — full way
    // lengths, no proximity filter. On a 3.8mi route it reported 36.0mi of slow
    // street and 8.7mi of fast arterial, so slowPct/fastPct pinned at their
    // Math.min(100,...) caps for every candidate and both levers became
    // constant offsets with zero discriminating power.
    //
    // slowStreetKm now comes from the route's own roadClassMeters.
    //
    // fastArterialKm and fourLaneKm are UNMEASURED, not zero. The bbox tally
    // derived them from `maxspeed` and `lanes` tags, and roadClassMeters holds
    // neither — there is no honest per-route equivalent, and inventing a
    // class-to-speed mapping would be inventing a routing rule. Rule 20: a
    // measurement nobody took is not a zero, so they are left undefined and the
    // levers that read them stand down (see the read sites, which no longer
    // `?? 0`). The corridor numbers are preserved below for any consumer that
    // genuinely wants corridor context — they must not feed route scoring.
    slowStreetKm: routeSlowStreetKm,
    fastArterialKm: undefined,
    fourLaneKm: undefined,
    corridorSlowStreetKm: osm.slowStreetKm,
    corridorFastArterialKm: osm.fastArterialKm,
    corridorFourLaneKm: (osm as { fourLaneKm?: number }).fourLaneKm,
    litStreetKm: osm.litStreetKm,
    cafeNames: nearCafes.map((c) => c.name).filter(Boolean).slice(0, 3),
    landmarkNames: nearLandmarksStrict.map((c) => c.name).filter(Boolean).slice(0, 3),
    parkNames: parkNames.length ? parkNames : undefined,
    waterNames: waterNames.length ? waterNames : undefined,
    waterAdjacentMeters: waterAdjacentMeters > 0 ? Math.round(waterAdjacentMeters) : undefined,
    coastlineAdjacentMeters: coastlineAdjacentMeters > 0 ? Math.round(coastlineAdjacentMeters) : undefined,
    waterAdjacency: waterAdjacency.length ? waterAdjacency : undefined,
    natureNames: natureNames.length ? natureNames : undefined,
    historicNames: historicNames.length ? historicNames : undefined,
    poiSegments: poiSegments.length ? poiSegments : undefined,
  };
}

export function preferenceMatchScore(f: FeatureSummary, prefs: Preference[]): number {
  if (prefs.length === 0) return 50;
  let total = 0;
  for (const p of prefs) {
    switch (p) {
      case "parks": total += clamp(f.parks * 12); break;
      case "coffee": total += clamp(f.cafes * 10); break;
      case "shops": total += clamp(f.shops * 8); break;
      // "Lively streets" — reward dense mixes of cafés, shops, and
      // landmarks (the same signals the `lively` dimension uses), with a
      // bonus when several cluster together so a single stray café doesn't
      // win it.
      case "lively-streets": {
        const mix = f.cafes * 6 + f.shops * 5 + f.landmarks * 4;
        const cluster = (f.cafes + f.shops) >= 3 ? 12 : 0;
        total += clamp(mix + cluster);
        break;
      }
      case "architecture":
      case "historic":
      case "monuments": total += clamp(f.landmarks * 15); break;
      case "quiet": total += f.residentialPct; break;
      case "no-highways": total += clamp(100 - f.highwayPct); break;
      case "no-traffic": total += clamp(100 - f.highwayPct * 0.8); break;
      case "scenic": total += clamp(f.parks * 8 + f.waterPct * 0.6 + f.landmarks * 6); break;
      case "coastal": {
        // Geometry-first: reward actual ocean / waterside meters. Falls
        // back to the count-based waterPct when no OSM geometry was
        // available, so corridors without coastline data still score.
        const coastM = f.coastlineAdjacentMeters ?? 0;
        const waterM = f.waterAdjacentMeters ?? 0;
        const namedOcean = (f.waterAdjacency ?? []).some(
          (a) => a.kind === "coastline" && a.name,
        );
        const geom =
          (coastM / 100) * COASTAL_WEIGHTS.coastlinePtsPer100m +
          (waterM / 100) * COASTAL_WEIGHTS.watersidePtsPer100m +
          (namedOcean ? COASTAL_WEIGHTS.namedOceanBonus : 0);
        const fallback = f.waterAdjacentMeters == null ? f.waterPct * 0.8 : 0;
        total += clamp(geom + fallback);
        break;
      }
    }
  }
  return clamp(total / prefs.length);
}

/** Base scenic score (always computed, no preference required). When the
 *  user opted into "coastal", the scoreRoute pipeline layers
 *  COASTAL_WEIGHTS on top of this — see `coastalBoost` in scoreRoute. */
export function scenicScore(f: FeatureSummary, season?: Season): number {
  // Seasonal nudge (Morgan): waterfront/greenery lean higher in spring/summer,
  // foliage in fall, both damped in winter. Identity (1.0) when no season given,
  // so every existing caller/test is unaffected.
  const sm = natureMultipliers(season);
  // waterAdjacentMeters is a geometry-true measure of how much of the route
  // physically runs next to water (reservoirs, rivers, lakes, ocean). The
  // legacy `waterPct` count-based metric undercounts reservoirs that come
  // from polygon centroids — a real reservoir-side drive could score
  // waterPct = 0 even with hundreds of meters of true water adjacency. Use
  // both signals: take the stronger of the two so we never penalise a
  // genuinely scenic route just because OSM tagged the water as a polygon
  // rather than a counted point.
  // (#178) Nature weights boosted ~50% per Morgan: "going through nature
  // would be better in this case [than community-loved town center]."
  // Was parks * 10, water meters * 4, water count * 0.5.
  // Now parks * 15, water meters * 6, water count * 0.75. The 50%
  // boost gives nature features enough weight to outscore community-
  // loved town routes when both exist in the pool, without losing the
  // community signal's relevance for genuinely community-rich corridors.
  const waterMetersScore = ((f.waterAdjacentMeters ?? 0) / 100) * 6; // 100m = 6 pts, 1km = 60 pts
  const waterCountScore = f.waterPct * 0.75;
  const waterContribution = Math.max(waterMetersScore, waterCountScore) * sm.water;
  // landmarks + shops are season-agnostic; only nature (water + greenery) flexes.
  return clamp(f.parks * 15 * sm.greenery + waterContribution + f.landmarks * 8 + f.shops * 2);
}

/** Additional pts to add to scenic/pleasant ONLY when the user opted into
 *  the "coastal" preference. Geometry-truthful: based on actual OSM-
 *  confirmed coastline / waterside meters along the route. */
export function coastalBoost(f: FeatureSummary, prefs: Preference[]): number {
  if (!prefs.includes("coastal")) return 0;
  const coastM = f.coastlineAdjacentMeters ?? 0;
  const waterM = f.waterAdjacentMeters ?? 0;
  const boost =
    (coastM / 100) * COASTAL_WEIGHTS.scenicCoastlinePtsPer100m +
    (waterM / 100) * COASTAL_WEIGHTS.scenicWatersidePtsPer100m;
  return Math.min(COASTAL_WEIGHTS.pleasantCoastalCap, boost);
}

export function calmScore(f: FeatureSummary): number {
  // The FIRST of five places highwayPct is charged. Ablation holds this term
  // at its maximum instead of removing it, so the scale is unchanged and only
  // the discrimination disappears.
  const hwyTerm = ABLATION.calmHighwayTermOff ? 100 * 0.2 : (100 - f.highwayPct) * 0.2;
  return clamp(f.residentialPct * 0.8 + hwyTerm);
}

export function efficiencyScore(routeDuration: number, fastestDuration: number): number {
  if (fastestDuration <= 0) return 100;
  const ratio = routeDuration / fastestDuration;
  // 1.0 → 100, 1.35 → 50, 2.0 → 0
  return clamp(100 - (ratio - 1) * 200);
}

/**
 * Community score blends two signals along the route (within `buffer` meters):
 *  - Quick 0-3 in-trip ratings (`pleasantness`): centered at 1.5 (neutral).
 *  - Legacy positive/negative tags (no rating).
 * Both are recency-weighted. Returns 0..100 (50 = neutral / no data).
 */
export function communityScore(
  route: RawRoute,
  tags: CommunityTag[],
  buffer: number,
): number {
  let ratingSum = 0, ratingWeight = 0;
  let pos = 0, neg = 0;
  for (const t of tags) {
    const dist = nearestDistance([t.lng, t.lat], route.coordinates);
    if (dist > buffer) continue;
    // Combine recency with reporter trust weight (default 0.2 for anonymous).
    const tExtra = t as { reporter_weight?: number; pleasantness?: number | null };
    const reporterW = typeof tExtra.reporter_weight === "number"
      ? tExtra.reporter_weight
      : 0.2;
    const w = recencyWeight(t.created_at) * Math.max(0.05, reporterW);
    const p = tExtra.pleasantness as number | null | undefined;
    if (typeof p === "number") {
      // Map 0..3 → -1.5..+1.5, then weight.
      ratingSum += (p - 1.5) * w;
      ratingWeight += w;
    } else {
      if (t.sentiment === "positive") pos += w;
      else neg += w;
    }
  }
  // Ratings: average centered score, scaled to ±30 around the 50 baseline.
  const ratingsContribution =
    ratingWeight > 0 ? (ratingSum / ratingWeight) * 20 : 0;
  // Tags: each net positive moves ~5 points (legacy behavior).
  const tagsContribution = (pos - neg) * 5;

  // Aggregated road-segment layer (proprietary). Reads cached segments
  // along the route and adds a separated positive/negative contribution.
  // Pulled lazily to avoid a circular import at module load.
  //
  // Two match tiers:
  //   1) EXACT polyline-edge match (segment_key starts with "pl:" and equals
  //      a key built from this route's own consecutive coords). These
  //      contributions were attributed to *this exact stretch* of road, so
  //      they bypass the proximity threshold and carry a 2× weight boost.
  //   2) Proximity match (legacy point grid + any pl: rows whose midpoint
  //      happens to fall within `buffer` m of the route).
  let segmentContribution = 0;
  let hasSegmentData = false;
  try {
    const b = bbox(route.coordinates, 0.002);
    const segs = cachedSegmentsInBbox({
      minLat: b.minLat, maxLat: b.maxLat, minLng: b.minLng, maxLng: b.maxLng,
    });

    // Build the set of exact polyline-edge keys for this route's own
    // consecutive coordinate pairs. Direction-agnostic by construction.
    const exactKeys = new Set<string>();
    for (let i = 1; i < route.coordinates.length; i++) {
      exactKeys.add(polylineSegmentKey(route.coordinates[i - 1], route.coordinates[i]));
    }

    let segPos = 0, segNeg = 0;
    const consumed = new Set<string>(); // dedupe: don't double-count exact + proximity
    // Pass 1: exact polyline-edge matches (highest priority).
    for (const s of segs) {
      if (!exactKeys.has(s.segment_key)) continue;
      const trust = Math.min(1, Math.log2(1 + s.contributor_count) / 3);
      // 2× boost for exact matches — these ratings were given AT this edge.
      segPos += s.positive_score * (0.5 + trust) * 2;
      segNeg += s.negative_score * (0.5 + trust) * 2;
      consumed.add(s.segment_key);
      hasSegmentData = true;
    }
    // Pass 2: proximity matches (skip anything already counted exactly).
    for (const s of segs) {
      if (consumed.has(s.segment_key)) continue;
      const dist = nearestDistance([s.lng, s.lat], route.coordinates);
      if (dist > buffer) continue;
      const trust = Math.min(1, Math.log2(1 + s.contributor_count) / 3);
      segPos += s.positive_score * (0.5 + trust);
      segNeg += s.negative_score * (0.5 + trust);
      hasSegmentData = true;
    }
    // Asymmetric: negative signals matter more (people complain less than they enjoy).
    segmentContribution = segPos * 4 - segNeg * 6;
    // Cap so a few segments don't dominate.
    segmentContribution = Math.max(-25, Math.min(25, segmentContribution));
  } catch {
    /* roadSegments module not available — skip */
  }

  if (tags.length === 0 && !hasSegmentData) return 50;
  return clamp(50 + ratingsContribution + tagsContribution + segmentContribution);
}


/**
 * HEAVY, approval-gated penalty for roads users marked "Avoid" (0) or "Not nice"
 * (1) on the in-drive pleasant scale. Unlike `communityScore` (a 0-100 blend
 * that's then weighted only ~6% into the total), this is subtracted STRAIGHT off
 * the pleasant score, so a single recent credible "avoid" is enough to make the
 * engine route around that road. Negatives dominate; positives are ignored here
 * (they stay in the soft community signal). The caller passes approved-only tags
 * (the bbox RPC filters status='rejected'), so a veto instantly removes a road.
 *
 * Tunable: AVOID_BASE / NOT_NICE_BASE set the single-report severity.
 */
const AVOID_BUFFER_M = 40;   // tight: target the actual road, not parallels
const AVOID_BASE = 42;       // one recent "Avoid" → heavy hit (well below the pleasant bar)
const NOT_NICE_BASE = 24;    // "Not nice" is roughly half as severe
const AVOID_MAX = 60;
export function avoidPenalty(route: RawRoute, tags: CommunityTag[], bufferM = AVOID_BUFFER_M): number {
  let avoidW = 0;
  let notNiceW = 0;
  for (const t of tags) {
    const tExtra = t as { reporter_weight?: number; pleasantness?: number | null };
    const p = tExtra.pleasantness;
    const isAvoid = p === 0 || (p == null && t.sentiment === "negative");
    const isNotNice = p === 1;
    if (!isAvoid && !isNotNice) continue;
    if (nearestDistance([t.lng, t.lat], route.coordinates) > bufferM) continue;
    const reporterW = typeof tExtra.reporter_weight === "number" ? tExtra.reporter_weight : 0.2;
    const w = recencyWeight(t.created_at) * Math.max(0.2, reporterW);
    if (isAvoid) avoidW += w; else notNiceW += w;
  }
  if (avoidW <= 0 && notNiceW <= 0) return 0;
  // Presence of a single recent report already lands the base hit; more or
  // closer reports pile on, capped so it can't run away.
  const penalty = avoidW > 0
    ? AVOID_BASE + Math.min(15, avoidW * 12)
    : NOT_NICE_BASE + Math.min(10, notNiceW * 10);
  return Math.min(AVOID_MAX, penalty);
}

export function scoreRoute(args: {
  route: RawRoute;
  features: FeatureSummary;
  fastestDuration: number;
  prefs: Preference[];
  community: number;
  /** When true, weight the community signal more heavily in the final score. */
  prioritizeCommunity?: boolean;
  /** New (optional): inputs for segment-based per-profile scoring.
   *  When all are present we use the new engine; otherwise we fall back
   *  to the corridor-aggregate scorer for back-compat. */
  profile?: PleasantnessProfile;
  osm?: OsmFeatures;
  osmPoints?: OsmFeaturePoints;
  pois?: { cafes: PoiHit[]; shops: PoiHit[]; landmarks: PoiHit[] };
  /** Highly-rated Google places sampled along this specific route. */
  googlePlaces?: GooglePlaceHit[];
  /** Rich OSM environment (points + lines) for per-segment env scoring. */
  osmEnv?: OsmEnvironment;
  communityTags?: CommunityTag[];
  buffer?: number;
  /** Detour tolerance multiplier (1.10 fast, 1.15 balanced, 1.35 scenic).
   *  Used as a SOFT time-cost: routes that consume more of their detour
   *  budget lose points; "fast" weights efficiency higher than "scenic". */
  toleranceMultiplier?: number;
  /** Travel mode — drives walking-/biking-specific infrastructure scoring. */
  mode?: TravelMode;
  /** Override clock for the time-aware adjustment. Production callers
   *  leave this undefined and we read `new Date()`; tests pass a fixed
   *  Date so commute-window effects don't depend on the test runner's
   *  wall-clock. */
  now?: Date;
  /** Live-traffic delay vs the route's free-flow duration, in seconds.
   *  When set, the pleasant score is reduced proportional to the delay
   *  (real-drive #65) — predicted-grim and actually-grim are different
   *  things, and live traffic is the ground truth. Caller computes this
   *  as `trafficDurationS - route.duration` once Mapbox returns the
   *  traffic-adjusted ETA. Negative values are clamped to 0 (live
   *  traffic faster than free-flow is just noise). Used as a FALLBACK
   *  when `trafficLegs` isn't available. */
  trafficDelaySec?: number;
  /** Per-leg traffic signals from Mapbox's congestion annotation (#66).
   *  When provided, replaces the whole-route trafficDelaySec penalty
   *  with a severity-weighted version: severe congestion stretches hurt
   *  more than sustained moderate slowdowns, so a route with one really
   *  bad block on it can be correctly demoted below a generally slow
   *  but consistently-flowing alternative.
   *  When `startCoord`/`endCoord` are present (#67), the worst-severity
   *  leg's midpoint is matched back to the route's steps to surface the
   *  road name in the evidence chip ("Severe traffic on Broadway") —
   *  more actionable than the generic "on this route." */
  trafficLegs?: Array<{
    durationS: number;
    freeFlowS: number | null;
    congestion: "low" | "moderate" | "heavy" | "severe" | "unknown";
    startCoord?: LngLat;
    endCoord?: LngLat;
  }>;
}): ScoredRoute {
  const {
    route, features, fastestDuration, prefs, community, prioritizeCommunity,
    profile, osm, osmPoints, pois, googlePlaces, osmEnv, communityTags, buffer,
    toleranceMultiplier, mode, now, trafficDelaySec, trafficLegs,
  } = args;
  // (#124 phase 2) `toleranceMultiplier` is intentionally unused here.
  // Pleasantness is tolerance-agnostic: the same physical route produces
  // the same pleasantness score regardless of which category card (Direct,
  // Pleasant, Scenic) ends up surfacing it. The argument is still received
  // for backwards compatibility with existing call sites and will be
  // removed entirely when those call sites are audited.
  void toleranceMultiplier;
  const preferenceMatch = preferenceMatchScore(features, prefs);
  // Seasonal nudge: derive the season from the clock (now, or live) and the
  // trip's midpoint latitude (hemisphere-aware), then lean the nature score
  // toward waterfront/greenery in summer, foliage in fall, etc.
  const midCoord = route.coordinates[Math.floor(route.coordinates.length / 2)];
  const season = midCoord ? seasonFor(now ?? new Date(), midCoord[1]) : undefined;
  let scenic = scenicScore(features, season);
  const efficiency = efficiencyScore(route.duration, fastestDuration);
  const calm = calmScore(features);
  const avoidsHighways = prefs.includes("no-highways") || prefs.includes("quiet");

  // ---- New segment-based scoring (preferred) ----
  let segmented: SegmentedRoute | undefined;
  let dimensions: Record<Dim, number> = {
    green: 0, blue: 0, historic: 0, quiet: 0, lively: 0, negative: 0,
  };
  let evidence: string[] = [];
  let profMatch = 50;
  // Unsafe flag (#124): set to true when the route has dangerous mode-
  // specific exposure (e.g., walking on motorway shoulder). Surfaced
  // on the ScoredRoute so classifyRoutes can drop it from every
  // category's pool before any scoring matters.
  let unsafe = false;

  if (profile && osm && pois && buffer != null) {
    segmented = segmentRoute({
      route,
      buffer,
      pois,
      osm,
      osmPoints,
      community: communityTags ?? [],
      googlePlaces,
      osmEnv,
      profile,
    });
    dimensions = aggregateRouteDimensions(segmented);
    profMatch = profileMatchScore(dimensions, profile, prefs, segmented);
    evidence = buildEvidence(segmented, profile);
  }

  // ---- CURATED GREAT-ROAD SCENIC CREDIT ----
  // When the route genuinely runs along a hand-curated road / street (Round
  // Hill Road, etc.), credit scenic value even if OSM has no parks or water
  // tagged here — being curated IS the scenic signal, and it's the whole
  // reason the route was surfaced. Without this, a route chosen FOR a great
  // road can show "Scenic 0," which reads as broken.
  const curatedMode =
    mode === "walking" ? "walking" : mode === "biking" ? "biking" : "driving";
  // Only credit a curated road the route GENUINELY travels (confirmed by the
  // turn-by-turn steps), not one it merely passes near. Threshold scales with
  // mode — walking streets are short blocks. The boost scales with distance, so
  // a route that runs a curated road for a while is credited far more than a clip.
  const curatedMinM = curatedMode === "walking" ? 90 : curatedMode === "biking" ? 300 : 800;
  const curatedTravelled = curatedRoadsTravelled(
    { coordinates: route.coordinates, steps: route.steps, distance: route.distance },
    curatedMode,
    { minMeters: curatedMinM },
  );
  if (curatedTravelled.length > 0) {
    const top = curatedTravelled[0];
    const mi = top.meters / 1609.344;
    const bonus = Math.min(45, 18 + mi * 6);
    scenic = Math.min(100, scenic + bonus);
    // The curated road's name + blurb now leads the CARD HEADLINE in every mode
    // ("Along Round Hill Road — …"), so when we have a blurb don't repeat it as
    // an evidence line. Roads without a blurb keep this line for the extra
    // "one of our curated scenic roads" context the terse headline lacks.
    const headlineCarriesIt = !!top.blurb;
    if (!headlineCarriesIt) {
      evidence = [
        top.blurb
          ? `Runs along ${top.name} — ${top.blurb}`
          : `Runs along ${top.name}, one of our pleasant roads`,
        ...evidence,
      ];
    }
  }

  // ---- Pleasantness weights (#124 rewrite) ----
  // Pleasantness is now INDEPENDENT of efficiency. Time is no longer a
  // pleasantness component — it lives only in the per-tolerance selection
  // logic (filter for Pleasant, ranking signal for Direct's 2nd card).
  //
  // Community signal reduced from 22-30% to 5% per Morgan's spec — it
  // remains visible to users via a "loved by N locals" chip, but no
  // longer dominates the score. Will grow back as the tester base grows.
  // (Morgan, experiment) Preferences removed from the base weighting for now —
  // pref weight set to 0 and its share redistributed proportionally across
  // scenic/calm/community so the score stays in the same 0-100 range. Revert by
  // restoring pref: 0.22 / 0.20 and the original scenic/calm/com weights.
  const w = prioritizeCommunity
    ? { pref: 0, scenic: 0.50, calm: 0.25, com: 0.25 }
    : { pref: 0, scenic: 0.58, calm: 0.36, com: 0.06 };

  let pleasant =
    w.pref * preferenceMatch +
    w.scenic * scenic +
    w.calm * calm +
    w.com * community;
  void efficiency; // intentionally unused — efficiency is not pleasantness

  // When the new engine ran, blend the profile match in heavily —
  // it's the source of truth for "is this route actually pleasant?".
  if (profile && segmented) {
    pleasant = pleasant * 0.25 + profMatch * 0.75;
  }

  // ---- SCORE DECOMPOSITION (observation only — changes no arithmetic) ----
  // Morgan, Aug 22: "how many points did each family of rules contribute to
  // that win?" __pt() records how far `pleasant` moved since the previous
  // checkpoint, so a term's contribution is MEASURED, not re-derived from a
  // formula that could drift. __praw() records a component that was clamped
  // together with another and so never landed on its own.
  // scoreTerms.ts says what each label ultimately measures.
  const __terms: Array<{
    label: string; delta: number; applied: boolean;
    /** Present only for nonlinear rules, measured either side of the real
     *  production statement. Absent for continuous terms. */
    before?: number; after?: number; violated?: boolean;
  }> = [];
  let __prev = pleasant;
  const __pt = (label: string) => {
    const d = pleasant - __prev;
    __prev = pleasant;
    if (d !== 0) __terms.push({ label, delta: d, applied: true });
  };
  // Morgan, Aug 22: "I want each mutation checkpoint captured directly around
  // the actual statement... No reconstructed 'raw' score should be used to
  // determine whether a clamp bound." __mut takes the real before and after,
  // measured either side of the unchanged production line.
  const __mut = (label: string, before: number, after: number) => {
    __prev = pleasant;                       // keep the linear trail in step
    const kind = __NONLINEAR_KIND[label];
    // INVARIANT: a ceiling may only lower, a floor may only raise. A violation
    // means the rule is not what its label claims, and every number downstream
    // is untrustworthy — so it is recorded, not swallowed.
    const violated =
      (kind === "ceiling" && after > before + 1e-9) ||
      (kind === "floor" && after < before - 1e-9);
    __terms.push({ label, delta: after - before, applied: true, before, after, violated });
  };
  const __praw = (label: string, d: number) => {
    if (d !== 0) __terms.push({ label, delta: d, applied: false });
  };
  const __base = pleasant;
  const __baseParts = {
    scenic: w.scenic * scenic,
    calm: w.calm * calm,
    community: w.com * community,
    blendedWithProfMatch: !!(profile && segmented),
  };

  // HEAVY avoid penalty (Morgan): roads marked Avoid/Not nice on the in-drive
  // pleasant scale steer routes away — far stronger than the soft community
  // weight above. Approval-gated: the bbox RPC only returns approved tags, so a
  // vetoed report drops out automatically.
  if (communityTags && communityTags.length) {
    const ap = avoidPenalty(args.route, communityTags);
    if (ap > 0) {
      const __b_communityAvoidPenalty = pleasant;
      pleasant = Math.max(0, pleasant - ap);
      __mut("communityAvoidPenalty", __b_communityAvoidPenalty, pleasant);
      // AVOIDED MEANS AVOIDED (Morgan, Aug 2: "when someone puts avoid down,
      // those roads are off limits for travel on pleasant or scenic").
      // The penalty above is the strong-cost half (Pleasant); the flag is the
      // hard-ban half — classifyThreeCards excludes flagged routes from the
      // Scenic slot outright, no amount of scenery buys it back. Tagged on the
      // route object (same pattern as tagRouteSource) because ScoredRoutes are
      // rebuilt in several places but the underlying route object survives.
      //
      // Deliberately COMMUNITY TAGS ONLY, not the curated "avoid" category:
      // curated avoids include seasonal congestion overlays that blanket the
      // entire Lake Michigan shore — a hard ban there would make the coastal
      // rule and the avoid rule fight, and the human tag is the one that's law.
      (args.route as { userAvoided?: boolean }).userAvoided = true;
    }
  }

  // (#124) The previous "Soft tolerance cost" block has been removed:
  // pleasantness is now independent of efficiency. Per-tolerance time
  // filtering happens in classifyRoutes, not here.

  // Hard guardrail: when the user asked to avoid highways/quiet streets,
  // a motorway-heavy route must not score high enough to be labeled pleasant.
  if (avoidsHighways && !ABLATION.highwayCapOff) {
    const hwy = features.highwayPct;
    if (hwy >= 25) {
      const cap = hwy >= 50 ? 20 : 45;
      profMatch = Math.min(profMatch, cap);
      const __b_avoidHighwaysCeiling = pleasant;
      pleasant = Math.min(pleasant, cap);
      __mut("avoidHighwaysCeiling", __b_avoidHighwaysCeiling, pleasant);
    }
  }
  // Checkpoint: this is a TRUNCATION, not a subtraction. Without its own
  // checkpoint the amount it removes would be misattributed to the next term.
  __pt("highwayCap");

  // Numbered and named arterial corridors are exactly what Adventure/Pleasant
  // should avoid everywhere — not just interstates, and not just one region.
  // Mapbox can return these under `exclude=motorway`, so penalize by visible
  // route shield/name/ref too (US-9, CA-1, Route 22, State Route 100, etc.).
  //
  // EXCEPTION — epic scenic highways: PCH (CA-1), Route 66, Blue Ridge
  // Parkway, Great Ocean Road etc. ARE numbered arterials by tag, but they
  // are precisely the experience the user is asking for. Subtract their
  // distance before computing the share so a route that IS PCH end-to-end
  // doesn't get capped at 14/100. The epic-highway block below still applies
  // its own boost on top.
  // (Morgan) SEVERITY-graded arterial penalty — "rank how bad highways are."
  // The OLD name-based penalty docked ANY route that touched a numbered road
  // (even 3%), which over-punished calm parkways. Disabling it entirely went
  // too far the other way: a route that is MOSTLY a numbered highway (the
  // I-287 slog, ~73%) then out-scored a genuinely calm back-roads route just
  // because it clipped a curated road — so the slog briefly won the Pleasant
  // card before OSM corrected it. Fix: only penalize a route that is MOSTLY
  // arterial. A route that merely touches one (low %) is left completely alone.
  const epicMetersForArterial = route.epicHighwayMeters ?? 0;
  const arterialMetersAdjusted = Math.max(
    0,
    namedArterialMeters(route) - epicMetersForArterial,
  );
  const namedArterialPct = route.distance > 0 ? (arterialMetersAdjusted / route.distance) * 100 : 0;
  if (!ABLATION.namedArterialPenaltyOff && namedArterialPct >= 40) {
    // 40% -> -20, 73% -> ~-46. Plus a hard cap so a mostly-highway route can
    // never win the Pleasant slot over a real back-roads alternative.
    const penalty = Math.min(50, (namedArterialPct - 40) * 0.8 + 20);
    pleasant -= penalty;
    __pt("namedArterialPenalty");
    profMatch -= penalty * 0.9;
    const cap = namedArterialPct >= 60 ? 18 : 30;
    const __b_namedArterialCeiling = pleasant;
    pleasant = Math.min(pleasant, cap);
    profMatch = Math.min(profMatch, cap);
    __mut("namedArterialCeiling", __b_namedArterialCeiling, pleasant);
  }

  // TUNNEL PENALTY REMOVED (Morgan, Aug 11): "driving through scenic
  // mountains has a lot of tunnels." The old curve (-10 pts at 5% tunnel,
  // -45 at 30%+, "effectively disqualified") treated tunnels as urban-bypass
  // dead zones — but in the Apennines, and on every serious mountain
  // corridor, tunnels are simply how the scenic road gets through the rock,
  // and the penalty was quietly killing the exact drives the app exists
  // for. tunnelMeters stays measured on the route (display / future use);
  // it just no longer costs anything.

  // City landmark boost: when a route runs past clusters of named landmarks
  // (Eiffel Tower, Notre-Dame, etc.) we treat it as a sightseeing win.
  // Trigger only in landmark-dense corridors so we don't reward suburban
  // routes that happen to clip a single museum sign.
  const namedLandmarks = features.landmarkNames?.length ?? 0;
  const totalLandmarks = features.landmarks;
  if (totalLandmarks >= 3 || namedLandmarks >= 2) {
    // Up to +18 for very dense / named corridors.
    const boost = Math.min(
      18,
      namedLandmarks * 5 + Math.min(10, totalLandmarks * 1.2),
    );
    pleasant += boost;
    __pt("landmarkBoost");
    profMatch += boost * 0.8;
  }

  // ---- Weather + air quality (#110 / #111) ----
  // All-modes ambient signals. Hits walking + biking hardest since
  // pedestrians and cyclists are directly exposed; driving gets a
  // mild penalty for heavy rain only (visibility / comfort).
  if (typeof features.precipitationMmHr === "number") {
    const mmHr = features.precipitationMmHr;
    if (mmHr > 0.1) {
      // Heavy rain (≥ 4 mm/hr) is miserable on foot or bike, mild
      // annoyance in a car. Drizzle (~1 mm/hr) is much softer.
      let rainPenalty = 0;
      if (mode === "walking" || mode === "biking") {
        rainPenalty = Math.min(12, mmHr * 2.5);
      } else if (mode === "driving") {
        rainPenalty = mmHr >= 4 ? Math.min(4, (mmHr - 4) * 0.6 + 2) : 0;
      }
      pleasant -= rainPenalty;
      __pt("rainPenalty");
      profMatch -= rainPenalty * 0.7;
    }
  }
  if (typeof features.temperatureC === "number" && (mode === "walking" || mode === "biking")) {
    const tC = features.temperatureC;
    // Penalize ranges that genuinely suck on foot / bike:
    //   < 0°C  → -3  (freezing)
    //   < -5°C → -5  (bitter)
    //   > 32°C → -3  (hot)
    //   > 37°C → -6  (dangerous)
    let tempPenalty = 0;
    if (tC < -5) tempPenalty = 5;
    else if (tC < 0) tempPenalty = 3;
    else if (tC > 37) tempPenalty = 6;
    else if (tC > 32) tempPenalty = 3;
    pleasant -= tempPenalty;
    __pt("tempPenalty");
    profMatch -= tempPenalty * 0.6;
  }
  if (typeof features.windKmh === "number" && mode === "biking") {
    const wind = features.windKmh;
    // Biking-specific wind penalty. Headwind hurts; 30 km/h+
    // is hard, 50+ is dangerous.
    let windPenalty = 0;
    if (wind > 50) windPenalty = 6;
    else if (wind > 30) windPenalty = 3;
    else if (wind > 20) windPenalty = 1;
    pleasant -= windPenalty;
    __pt("windPenalty");
    profMatch -= windPenalty * 0.6;
  }
  if (typeof features.usAqi === "number" && (mode === "walking" || mode === "biking")) {
    const aqi = features.usAqi;
    // AQI penalties from EPA categories:
    //   100-150 (moderate)             → -2
    //   150-200 (unhealthy)            → -5
    //   200-300 (very unhealthy)       → -10
    //   300+    (hazardous)            → -15
    let aqiPenalty = 0;
    if (aqi > 300) aqiPenalty = 15;
    else if (aqi > 200) aqiPenalty = 10;
    else if (aqi > 150) aqiPenalty = 5;
    else if (aqi > 100) aqiPenalty = 2;
    pleasant -= aqiPenalty;
    __pt("aqiPenalty");
    profMatch -= aqiPenalty * 0.7;
  }
  // Sun / shade bonus (#113). Walking-mode-only. Boosts tree-lined
  // routes when the sun is high enough to actually cast meaningful
  // heat AND it's warm out. Tiny lever — never the deciding factor,
  // but a nice tiebreaker in summer.
  //
  //   sun altitude > 25° AND temp ≥ 22°C AND treeLinedKm ≥ 0.2
  //     → +1 per kilometer of tree-lined route, capped +4.
  //   sun altitude > 55° (overhead) doubles the rate, capped +6.
  if (
    mode === "walking" &&
    typeof features.sunAltitudeDeg === "number" &&
    features.sunAltitudeDeg > 25 &&
    typeof features.temperatureC === "number" &&
    features.temperatureC >= 22 &&
    (features.treeLinedKm ?? 0) >= 0.2
  ) {
    const tlKm = features.treeLinedKm ?? 0;
    const overhead = features.sunAltitudeDeg > 55;
    const ratePerKm = overhead ? 2 : 1;
    const cap = overhead ? 6 : 4;
    const shadeBoost = Math.min(cap, tlKm * ratePerKm);
    pleasant += shadeBoost;
    __pt("shadeBoost");
    profMatch += shadeBoost * 0.7;
  }
  // Street lighting at night (#114). When the sun is below civil
  // twilight (altitude < -6°), well-lit routes feel safer + more
  // comfortable, dark routes feel risky.
  //
  // Walking: strongest — +5 max for ≥80% lit coverage; −8 max
  //   when < 20% lit (a true dark-night walk is borderline unsafe).
  // Biking:  moderate — boosts/penalties scaled to 0.6× of walking.
  // Driving: small — +2 / −3 (cars have headlights but well-lit
  //   urban streets do feel calmer at night).
  if (
    typeof features.sunAltitudeDeg === "number" &&
    features.sunAltitudeDeg < -6
  ) {
    const km = Math.max(0.1, route.distance / 1000);
    const litPct = Math.min(100, ((features.litStreetKm ?? 0) / km) * 100);
    let litBoost = 0;
    let litPenalty = 0;
    if (litPct >= 80) litBoost = 5;
    else if (litPct >= 50) litBoost = 3;
    else if (litPct >= 30) litBoost = 1;
    if (litPct < 20) litPenalty = 8;
    else if (litPct < 30) litPenalty = 5;
    if (mode === "biking") {
      litBoost *= 0.6;
      litPenalty *= 0.6;
    } else if (mode === "driving") {
      litBoost = Math.min(2, litBoost * 0.4);
      litPenalty = Math.min(3, litPenalty * 0.4);
    }
    pleasant += litBoost - litPenalty;
    __pt("litAdj");
    profMatch += (litBoost - litPenalty) * 0.7;
  }

  // ---- Mode-specific infrastructure scoring ----
  // Walking and biking succeed or fail based on whether the route has the
  // right surface for that mode. A "scenic" walking route on a shoulderless
  // arterial is genuinely worse than a slightly less scenic one with full
  // sidewalks — score it that way.
  const km = Math.max(0.1, route.distance / 1000);

  // ---- Water-over-downtown adjustment (#85) ----
  // Real-world ask: "downtown should not be weighed more than lake." Any
  // route that actually runs alongside water should out-score a route
  // that threads a commercial downtown corridor — even if the downtown
  // route is shorter. We do two things:
  //   1) Bonus the route in proportion to its true water-adjacency meters
  //      (geometry-checked against OSM water features). 100m along a
  //      reservoir = +5, 500m = +18, 1km = +30.
  //   2) Tax routes with high cafe/shop density that the user didn't ask
  //      for. That's the literal "downtown" signal.
  //
  // (#124) Previously gated behind `tol > 1.05` so the Fast pick stayed
  // "purely about time." Under the unified scoring model, pleasantness
  // is tolerance-agnostic — water adjacency makes a route more pleasant
  // regardless of which category card surfaces it. Selection by category
  // happens in classifyRoutes, not by hiding pleasantness signals here.
  const wantsLively = prefs.includes("lively-streets");
  {
    const waterM = features.waterAdjacentMeters ?? 0;
    const waterBoost = Math.min(36, Math.sqrt(waterM / 30) * 5);
    pleasant += waterBoost;
    __pt("waterBoost");
    profMatch += waterBoost * 0.7;

    // OCEAN PREMIUM (#all-users). A road that actually hugs the sea — CA-1
    // past Devil's Slide, PCH, the Big Sur coast — is the most dramatic
    // scenic value there is, and it should out-pull an inland route for
    // EVERYONE, not just people who ticked the "coastal" preference (that
    // path still layers extra on top via coastalBoost). This stacks ON TOP of
    // the generic waterBoost above, so coastline counts roughly double:
    //   ~250 m of coast ≈ +12, ~1 km ≈ +24, ~4 km ≈ +45 (capped).
    // coastlineAdjacentMeters is geometry-true (OSM kind=coastline), so an
    // inland river or reservoir never triggers it.
    const coastM = features.coastlineAdjacentMeters ?? 0;
    if (coastM > 0) {
      const oceanBoost = Math.min(45, Math.sqrt(coastM / 18) * 5);
      pleasant += oceanBoost;
      __pt("oceanBoost");
      profMatch += oceanBoost * 0.8;
    }

    // Profile-dependent commercial density (#124).
    //   Lively profile / user opted into shops/coffee/lively → BOOST.
    //   Quiet / nature / scenic profile → strong penalty.
    //   Historic / architecture → mild penalty (interesting buildings
    //     somewhat offset the commercial density).
    //   Default / other → mild penalty.
    // (#177) Commercial-density penalty strengthened so urban-corridor
    // routes (e.g. through White Plains) get meaningfully pushed below
    // their suburban alternatives. Before: penalty maxed at -6 for the
    // default profile, only fired above 8 shops/km. Now: fires above
    // 5/km and maxes at -12. The intuition: a route running through
    // a busy retail strip isn't pleasant for driving regardless of
    // user profile, unless they explicitly want shops/lively. Real-
    // world assumption: commercial-POI density is a good proxy for
    // "this corridor feels urban / stop-and-go" without needing extra
    // OSM landuse data or crime feeds.
    const commercialCount = (features.cafes ?? 0) + (features.shops ?? 0);
    const commercialPerKm = commercialCount / km;
    // Fire on EITHER sustained density (short urban trips) OR a concentrated
    // commercial cluster the route threads even when the route-wide AVERAGE is
    // diluted by length. A 32-mi drive that dips through downtown White Plains
    // has ~25+ POIs over ~50 km → reads as ~0.5/km and used to slip under the
    // 5/km bar entirely, so the "ugly downtown" penalty never fired. The
    // absolute-count arm catches that "it threads a downtown" case. (Morgan)
    if (commercialPerKm >= 5 || commercialCount >= 15) {
      const intensity = Math.min(
        1.8,
        Math.max((commercialPerKm - 4) / 4, (commercialCount - 10) / 15),
      );
      const wantsCommercial =
        wantsLively ||
        prefs.includes("shops") ||
        prefs.includes("coffee") ||
        profile === "lively";
      const wantsQuiet =
        profile === "quiet" || profile === "nature" || profile === "water" ||
        prefs.includes("quiet") || prefs.includes("parks") ||
        prefs.includes("coastal") || prefs.includes("scenic");
      const wantsHistoric =
        profile === "historic" ||
        prefs.includes("historic") || prefs.includes("architecture");
      let delta: number;
      if (wantsCommercial) {
        delta = +Math.min(8, 5 * intensity);          // boost
      } else if (wantsQuiet) {
        delta = -Math.min(20, 14 * intensity);         // strong penalty
      } else if (wantsHistoric) {
        delta = -Math.min(8, 5 * intensity);           // mild penalty
      } else {
        delta = -Math.min(12, 7 * intensity);          // default — now meaningful
      }
      pleasant += delta;
      __pt("noiseAdj");
      profMatch += delta * 0.7;
    }

    // Historic district overlay (#104). Small lever: when the route
    // passes through a tagged historic district (Boston Back Bay,
    // Charleston, Savannah, Santa Fe core, Greenwich Village historic
    // district, etc.), boost the score modestly. Different from
    // individual historic POIs, which are already counted via the
    // landmarks/historic feature counts — this catches the "whole
    // neighborhood is pretty" character that a single point can't.
    //   1 district  → +3
    //   2 districts → +6
    //   3+ districts → capped at +8
    // Calibrated as a SMALL LEVER per Morgan's framing — meaningful
    // tiebreaker, never dominates the overall score.
    const districtCount = features.historicDistrictCount ?? 0;
    if (districtCount > 0) {
      const districtBoost = Math.min(8, districtCount * 3);
      pleasant += districtBoost;
      __pt("districtBoost");
      profMatch += districtBoost * 0.7;
    }

    // Tree-lined km (#106). Free OSM proxy for tree canopy. Walking
    // benefits most (shade matters when you're on foot), but the
    // boost applies to all modes since a leafy street is genuinely
    // nicer to drive/bike too. Small lever:
    //   0.1 km tree-lined → +1
    //   0.5 km            → +3
    //   1 km              → +4.5
    //   3+ km             → capped at +6
    // Square-root curve so brief leafy stretches still register, but
    // long forests don't dominate the score.
    const treeKm = features.treeLinedKm ?? 0;
    if (treeKm > 0.05) {
      const baseBoost = Math.min(6, Math.sqrt(treeKm) * 4);
      // Walking mode gets the full +6 cap; biking + driving get 75%
      // of it — shade is nicest when you're on foot.
      const modeMul = mode === "walking" ? 1.0 : 0.75;
      const treeBoost = baseBoost * modeMul;
      pleasant += treeBoost;
      __pt("treeBoost");
      profMatch += treeBoost * 0.7;
    }

    // (#124 removed median home value boost — was a wealth proxy, not a
    // pleasantness signal. Replaced by direct OSM signals already in
    // the score: tree-lined, historic district, parks, water, sidewalk
    // presence, low traffic speed, user ratings.)

    // ---- Driving-specific signals (#108) ----
    // The bike + walk modes have their own scoring branches further
    // down; driving doesn't, so put the driving-specific OSM signals
    // here inside the Pleasant + Scenic gate.
    if (mode === "driving") {
      // Scenic byway boost — an officially-designated scenic route
      // (Blue Ridge Parkway, Mohawk Trail, state byways) is the
      // strongest possible signal that this road is meant to be driven
      // for its own sake. Strong lever: up to +12 at full coverage.
      const bywayKm = features.scenicBywayKm ?? 0;
      if (bywayKm > 0.1) {
        const bywayBoost = Math.min(12, bywayKm * 3);
        pleasant += bywayBoost;
        __pt("bywayBoost");
        profMatch += bywayBoost * 0.7;
      }
      // Slow-street bonus + fast-arterial penalty. Sweet-spot lever:
      // a route that's almost entirely 25mph residential picks up
      // ~+5, a route that's heavily 45mph stroad loses ~−6.
      // `?? 0` here was how "we never measured this" became "this route has
      // none of it". Both levers now stand down on an unmeasured input rather
      // than firing on a fabricated zero. Thresholds unchanged.
      const slowPct = features.slowStreetKm == null
        ? null
        : Math.min(100, (features.slowStreetKm / km) * 100);
      const fastPct = features.fastArterialKm == null
        ? null
        : Math.min(100, (features.fastArterialKm / km) * 100);
      if (slowPct !== null && slowPct >= 30) {
        const slowBoost = Math.min(5, (slowPct - 30) * 0.10);
        pleasant += slowBoost;
        __pt("slowStreetBoost");
        profMatch += slowBoost * 0.7;
      }
      if (fastPct >= 20) {
        const fastPenalty = Math.min(6, (fastPct - 20) * 0.12);
        pleasant -= fastPenalty;
        __pt("fastArterialPenalty");
        profMatch -= fastPenalty * 0.7;
      }
      // Wide 4-lane road penalty (Morgan), SCALED UP on short drives.
      // A 4+-lane road (OSM lanes >= 4) is a wide, hostile arterial even at
      // a moderate speed — the opposite of a pleasant drive. On a SHORT trip
      // the whole point is to enjoy the drive and there's rarely a reason to
      // accept a stroad, so the shorter the trip the harder we dock it:
      //   >= 30 min → 1.0x (baseline; some 4-lane can be unavoidable far out)
      //   ~15 min   → ~1.5x
      //   ~5 min    → ~1.8x   (ramps toward 2.0x at 0 min, clamped)
      // Kept SEPARATE from fastPct above so a fast 4-lane road isn't double
      // counted — this is purely the width signal.
      const fourLanePct = features.fourLaneKm == null
        ? null
        : Math.min(100, (features.fourLaneKm / km) * 100);
      if (fourLanePct !== null && fourLanePct >= 8) {
        const tripMin = route.duration / 60;
        const shortTripMul = tripMin >= 30 ? 1 : 1 + ((30 - tripMin) / 30) * 1.0;
        const fourLanePenalty = Math.min(16, (fourLanePct - 8) * 0.25) * shortTripMul;
        pleasant -= fourLanePenalty;
        __pt("fourLanePenalty");
        profMatch -= fourLanePenalty * 0.7;
      }
      // Primary-road penalty (#121). White Plains Road in Tarrytown,
      // Albany Post Road, North Broadway, etc. are tagged `primary`
      // in OSM — busy commercial corridors that ARE the thing
      // Pleasant should avoid, but neither motorway exclusion nor
      // parkway-name detection catches them. features.highwayPct
      // covers motorway+trunk+primary, so use it directly as a
      // proxy: any non-trivial primary/trunk exposure (>= 15%)
      // earns a penalty proportional to coverage.
      if (!ABLATION.primaryPenaltyOff && features.highwayPct >= 15) {
        const primaryPenalty = Math.min(10, (features.highwayPct - 15) * 0.20);
        pleasant -= primaryPenalty;
        __pt("primaryPenalty");
        profMatch -= primaryPenalty * 0.7;
      }
      // Nice-town bonus (Morgan): a route that passes through a genuinely
      // nicer town — Greenwich, Bedford, Cold Spring — over a duller one
      // earns a small, capped nudge so the charming way wins when the time
      // cost is comparable. Additive + capped (≤8) so it never overpowers the
      // core signal; absence of a town is never a penalty. Registry in
      // utils/niceTowns.ts, extendable like the curated walk cities.
      const townBonus = niceTownBonus(route.coordinates);
      if (townBonus > 0) {
        pleasant += townBonus;
        __pt("townBonus");
        profMatch += townBonus * 0.7;
      }
      // Avoid-road penalty: a route that still travels a hand-marked stroad /
      // ugly commercial strip / high-stress arterial (the curated "avoid"
      // category) loses points so a calmer alternative wins. Capped (Morgan:
      // curated avoid roads should steer routes away from the bad corridors
      // that highwayPct can't catch — a stroad is tagged "primary", not
      // "motorway").
      const avoidPen = avoidDrivePenalty(route.coordinates);
      if (avoidPen > 0) {
        pleasant -= avoidPen;
        __pt("avoidPenalty");
        profMatch -= avoidPen * 0.7;
      }
    }
  }

  if (mode === "walking") {
    const sidewalkPct = Math.min(100, (features.sidewalkKm / km) * 100);
    const onRouteNoSidewalkKm = routeKmNearKinds(route, osmEnv, new Set(["no_sidewalk"]), 35);
    const classBusyKm = routeClassKm(route, BUSY_WALK_ROAD_KINDS);
    const busyRoadPct = Math.min(100, (classBusyKm / km) * 100);
    const effectiveHighwayPct = Math.max(features.highwayPct, busyRoadPct);
    const classMajorKm = routeClassKm(route, MAJOR_WALK_ROAD_KINDS);
    const onRouteMajorKm = Math.max(
      classMajorKm,
      routeKmNearKinds(route, osmEnv, MAJOR_WALK_ROAD_KINDS, 35),
    );
    const noSidewalkPct = Math.min(100, (Math.max(features.noSidewalkKm, onRouteNoSidewalkKm) / km) * 100);
    // OSM sidewalk tagging is wildly incomplete in cities — most NYC/SF/Boston
    // streets have sidewalks but aren't tagged. Treat sidewalkPct as a soft
    // bonus only and lean on the explicit no-sidewalk / highway penalties.
    const walkBoost = Math.max(0, sidewalkPct - 20) * 0.08;
    // Dedicated foot infrastructure — footways, trails, hiking paths,
    // tracks, bridleways, steps. These are *the* ideal walking surface
    // (Rockefeller carriage roads, park trails, greenways) and should
    // strongly attract the route over any street-side alternative.
    const onRouteFootKm = routeKmNearKinds(route, osmEnv, FOOT_WALK_KINDS, 45);
    const footwayKm = Math.max((osm as any)?.footwayKm ?? 0, onRouteFootKm);
    const trailPct = Math.min(100, (footwayKm / km) * 100);
    const trailBoost = trailPct * 0.40;
    // Expose the car-free distance to the card (so it can recognize a calm,
    // away-from-traffic walk even when there's no named park).
    features.carFreeKm = footwayKm;
    // CELEBRATE car-free travel (Morgan: "any time the walk/bike goes where cars
    // aren't allowed, call it out and celebrate it"). Footways, park trails,
    // greenways and protected paths keep you off the road entirely — the whole
    // reason to go on foot somewhere nice. Lead the evidence with it.
    if (footwayKm >= 0.4 || trailPct >= 25) {
      const mi = footwayKm / 1.60934;
      // Aug 19 units audit: was hardcoded "mi". Evidence is baked at scoring
      // time, so the chip reflects the units at generation — acceptable, since
      // regenerating the route rebakes it. The 0.3 mi wording threshold stays
      // in miles: it's a judgement about when a number is worth printing.
      evidence = [
        mi >= 0.3
          ? `${fmtDistance(footwayKm * 1000)} on car-free paths — away from traffic`
          : "On car-free paths — away from traffic",
        ...evidence,
      ];
    }
    // Walking on arterials (8th Ave, 6th Ave, Canal, N Broadway, etc.) is
    // genuinely unpleasant — loud, exhaust-heavy, no shade, scary crossings.
    // Suburban secondary/tertiary roads are the WORST case: 45 mph traffic,
    // missing/intermittent sidewalks, no buildings to buffer noise. Crank
    // the penalty hard so the scorer will gladly accept a longer walk
    // (even one that loops or backtracks slightly) to stay off them.
    // majorRoadPct (secondary/tertiary) catches busy non-highway arterials
    // like N Broadway, Geary, US-9 business routes — these aren't motorways
    // but are absolutely miserable to walk along.
    const majorRoadPct = Math.max(features.majorRoadPct, Math.min(100, (onRouteMajorKm / km) * 100));
    const walkPenalty = noSidewalkPct * 1.00
      + effectiveHighwayPct * 1.30
      + majorRoadPct * 2.40;
    // Walking elevation penalty (#109). Softer than biking — walking
    // tolerates moderate hills better, but steep climbs still hurt:
    //   100m ascent → −1.5
    //   300m ascent → −4
    //   500m+       → capped −6
    //   max grade ≥ 12% → extra −3 (a 12% sustained climb is brutal)
    let walkElevationPenalty = 0;
    const wAscentM = features.totalAscentM ?? 0;
    if (wAscentM > 30) {
      walkElevationPenalty = Math.min(6, Math.sqrt(wAscentM) * 0.27);
    }
    if ((features.maxGradePct ?? 0) >= 12) {
      walkElevationPenalty += 3;
    }
    // MAIN STREET PREMIUM (Morgan, Aug 8, from Mikolai Altenberg: "instead of
    // taking me down the scenic Main Street of my town it routed me down a
    // wider, less shady and less appealing parallel street").
    //
    // Between two parallel streets of equal length the scorer had no reason to
    // prefer the lively one: shops/cafés only counted for people who ticked
    // the "lively streets" preference. This is the same correction the OCEAN
    // PREMIUM makes above — a genuinely nice street is nicer for EVERYONE, not
    // only for the person who asked for it. A town's main street is a real
    // pedestrian amenity: shelter, shade, shopfronts, eyes on the street,
    // somewhere to stop.
    //
    // DENSITY, not count: a long walk naturally passes more shops, so we
    // measure per kilometre. ~4 places/km ≈ +6, ~10/km ≈ +11, capped at +14 —
    // deliberately well under trailBoost (0.40/pct, tens of points) so rule 5
    // holds: if one option goes through a park, the park is still the pleasant
    // one. This only decides between STREETS.
    const livelyCount = features.cafes + features.shops + features.landmarks * 0.5;
    const livelyPerKm = km > 0.2 ? livelyCount / km : 0;
    const mainStreetBoost = livelyPerKm >= 1.5
      ? Math.min(14, Math.sqrt(livelyPerKm) * 3.4)
      : 0;
    const walkAdj = walkBoost + trailBoost + mainStreetBoost - walkPenalty - walkElevationPenalty;
    pleasant += walkAdj;
    __pt("walkAdj");
    profMatch += walkAdj * 0.8;
    if (mainStreetBoost > 0) {
      features.mainStreetBoost = mainStreetBoost;
      evidence = [
        livelyPerKm >= 6
          ? "Along a lively main street — shops and cafés the whole way"
          : "Past shops and cafés rather than a bare parallel street",
        ...evidence,
      ];
    }
    // Walking safety (#124): distinguish UNSAFE from UNPLEASANT.
    // Unsafe = physically dangerous in this mode → drops from the
    // candidate pool entirely (set unsafe flag, classifyRoutes filters
    // it out before scoring matters). Unpleasant = annoying but
    // survivable → score penalty / soft cap.
    // STRICT walking safety (Morgan): never route a pedestrian onto a highway
    // or a highway-like dangerous road. A walk should take a much longer, safe
    // path rather than any meaningful exposure to fast traffic. So the unsafe
    // thresholds are deliberately tight — essentially ANY motorway/trunk/
    // primary, any 40mph+ stroad, a busy 4-lane arterial, or a no-sidewalk
    // stretch disqualifies the route entirely (it's dropped from the pool and
    // never surfaced unless literally nothing safer exists).
    // This gate DROPS a walk from the pool. Firing it on a fabricated zero would
    // be bad; firing it on corridor-wide data was worse — every urban walk sat
    // at the clamped 100% and was removed on the strength of a 40mph road that
    // might be a kilometre away. Unmeasured now means this clause abstains and
    // the other three conditions decide.
    const fastArterialPct = features.fastArterialKm == null
      ? null
      : Math.min(100, (features.fastArterialKm / km) * 100);
    const isUnsafeWalk =
      effectiveHighwayPct >= 2 ||      // any real stretch on motorway/trunk/primary
      (fastArterialPct !== null && fastArterialPct >= 4) ||  // 40mph+ stroad
      majorRoadPct >= 5 ||             // busy 4-lane arterial
      noSidewalkPct >= 5;              // no-sidewalk road (tightened from 8)
    if (isUnsafeWalk) {
      unsafe = true;
    } else if (noSidewalkPct >= 2 || majorRoadPct >= 3) {
      // Borderline-unpleasant walking: keep in pool but cap the score so
      // it won't win Pleasant. Still allowed to surface as Direct fallback
      // if literally nothing else exists.
      const __b_walkBorderlineCeiling = pleasant;
      pleasant = Math.min(pleasant, 25);
      profMatch = Math.min(profMatch, 25);
      __mut("walkBorderlineCeiling", __b_walkBorderlineCeiling, pleasant);
    }
    // Pretty-neighborhood boost (walking only): if a meaningful share of
    // the route runs through a curated pretty neighborhood (West Village,
    // Beacon Hill, Le Marais, etc.), reward it. This is what makes a walk
    // *feel* nice beyond the bare infrastructure metrics — brownstones,
    // tree canopy, café tables on the sidewalk. Tier-weighted so a stretch
    // through Greenwich Village beats one through a less iconic district.
    const pretty = prettyNeighborhoodCoverage(route.coordinates, cachedNominationsAsPretty());
    if (pretty.fraction > 0.05) {
      // Up to ~+22 for a route entirely inside tier-3 neighborhoods.
      const prettyBoost = Math.min(22, pretty.weighted * 70);
      pleasant += prettyBoost;
      __pt("prettyBoost");
      profMatch += prettyBoost * 0.9;
    }
    // Walk Score boost (#99). Walking-mode-only external-data signal:
    // Walk Score blends amenity density, intersection density, sidewalk
    // presence, and proximity to parks/transit — all of which correlate
    // with "pleasant to walk on." Calibrated as a SMALL LEVER (Morgan's
    // framing) so it nudges rather than dominates:
    //   • Walk Score 50 (somewhat walkable) → 0 boost
    //   • Walk Score 75 (very walkable)     → +5
    //   • Walk Score 90 (walker's paradise) → +8
    //   • Capped at +10 even for perfect 100.
    if (typeof features.walkScore === "number" && features.walkScore >= 50) {
      const walkBoost = Math.min(10, (features.walkScore - 50) * 0.2);
      pleasant += walkBoost;
      __pt("walkBoost");
      profMatch += walkBoost * 0.7;
    }
  } else if (mode === "biking") {
    const bikePct = Math.min(100, (features.bikeLaneKm / km) * 100);
    // Protected vs painted distinction (#101). Physically-separated
    // infrastructure (dedicated cycleways, off-street paths) is vastly
    // nicer than paint-only and should pull harder on routing. Painted
    // lanes still help over no infra but get a smaller boost.
    const protectedPct = features.protectedBikeKm != null
      ? Math.min(100, (features.protectedBikeKm / km) * 100)
      : 0;
    const paintedPct = features.paintedBikeKm != null
      ? Math.min(100, (features.paintedBikeKm / km) * 100)
      : 0;
    const smoothPct = features.smoothBikeKm != null
      ? Math.min(100, (features.smoothBikeKm / km) * 100)
      : 0;
    // Quiet residential streets are bike-friendly too, weighted at 40% of
    // generic cycleway and 25% of protected cycleway.
    const bikeFriendlyPct = Math.min(100, bikePct + features.residentialPct * 0.4);
    const bikeBoost = (bikeFriendlyPct - 30) * 0.20;
    // Protected adds an extra +0.10 per percent — at full coverage that's
    // up to +10 on top of the base bike boost. Painted only adds +0.04.
    const protectedBoost = protectedPct * 0.10;
    const paintedBoost = paintedPct * 0.04;
    // Smooth-surface micro-bonus — capped at +4 so it never dominates,
    // but a smooth dedicated path outranks a cobblestone one.
    const smoothBoost = Math.min(4, smoothPct * 0.04);
    const bikePenalty = features.highwayPct * 0.55;
    // People for Bikes city-level boost (#105). Captures network
    // connectivity / safety that per-segment OSM tags can't —
    // Davis CA scores 95 because the network as a whole is cyclist-
    // friendly, even on streets without painted lanes. Small lever:
    //   50 (avg)      → 0
    //   65 (good)     → +3
    //   80 (great)    → +6
    //   95 (Davis)    → +9 (cap)
    const pfb = features.peopleForBikesScore;
    const pfbBoost = typeof pfb === "number" && pfb >= 50
      ? Math.min(9, (pfb - 50) * 0.2)
      : 0;
    // Walk Score's Bike Score boost. Point-sampled along the actual route
    // (vs People for Bikes' single city rating), so it rewards a route that
    // threads the more bikeable parts of town. Small lever, additive only:
    //   50 → 0, 70 → +4, 85+ → capped +6.
    const wsBike = features.bikeScore;
    const wsBikeBoost = typeof wsBike === "number" && wsBike >= 50
      ? Math.min(6, (wsBike - 50) * 0.17)
      : 0;
    // Elevation penalty (#109). Steep / hilly routes are work; for
    // pleasantness scoring, flat wins. Calibrated as a meaningful but
    // not-dominant lever for biking:
    //   100m ascent → −3
    //   300m ascent → −8
    //   500m+       → capped −12
    //   max grade ≥ 8% → extra −4
    let elevationPenalty = 0;
    const ascentM = features.totalAscentM ?? 0;
    if (ascentM > 30) {
      elevationPenalty = Math.min(12, Math.sqrt(ascentM) * 0.55);
    }
    if ((features.maxGradePct ?? 0) >= 8) {
      elevationPenalty += 4;
    }
    // BIKE LANES ARE A HUGE LEVER — IN CITIES (Morgan). In a developed/urban
    // ride, the presence of bike infrastructure should dominate the bike
    // pleasant score: a lane-covered city route must decisively out-rank a
    // laneless one. We gate this to urban context so a rural ride — where a
    // lovely country road legitimately has no lanes — isn't judged against a
    // lane standard (and it's self-limiting anyway: no lanes → no bonus).
    // Urban context from the route's own STRUCTURE (residential/commercial
    // street fabric), NOT from bike ratings — a PfB/bike score shouldn't decide
    // "city" or it would conflate with those separate levers.
    const isCityBike =
      features.residentialPct >= 25 ||
      (features.shops ?? 0) + (features.cafes ?? 0) >= 3;
    // Up to roughly +30 at full protected coverage, on top of the base boosts —
    // enough to move bike lanes from "a factor" to "the deciding factor" in town.
    const cityLaneBonus = isCityBike
      ? protectedPct * 0.25 + paintedPct * 0.12 + bikePct * 0.08
      : 0;
    const bikeAdj = bikeBoost + protectedBoost + paintedBoost + smoothBoost + pfbBoost + wsBikeBoost + cityLaneBonus - bikePenalty - elevationPenalty;
    pleasant += bikeAdj;
    __pt("bikeAdj");
    profMatch += bikeAdj * 0.8;
    // Biking safety (#124): distinguish UNSAFE from UNPLEASANT.
    // Unsafe = meaningful motorway/trunk exposure → drop entirely.
    // Unpleasant = trunk/primary without bike lane → strong penalty
    // but still in pool (might be the only option for some trips).
    if (features.highwayPct >= 5) {
      unsafe = true;
    } else if (features.highwayPct >= 15) {
      const __b_bikeUnsafeCeiling = pleasant;
      pleasant = Math.min(pleasant, 40);
      profMatch = Math.min(profMatch, 40);
      __mut("bikeUnsafeCeiling", __b_bikeUnsafeCeiling, pleasant);
    }
  }

  // ---- GLOBAL BACKROADS-FIRST RULE ----
  // Applies to every mode and every profile: a route that runs mostly on
  // residential streets is *intrinsically* more pleasant than one that runs
  // on arterials/highways, even when the user picked no specific prefs and
  // even on "fast" or "lively" profiles. This is the user's standing rule:
  // backroads beat main roads as a baseline. The boost/penalty is bounded so
  // it nudges scoring without overriding strong scenic/landmark signals.
  //
  //   residentialPct 80 → +12  (mostly side streets — clearly nicer)
  //   residentialPct 50 → +3
  //   residentialPct 20 → -3
  //   highwayPct     30 → -9   (arterial-heavy — penalize)
  //   highwayPct     60 → -18
  // (#124) Uses the stronger (formerly scenic-only) coefficients uniformly.
  // Pleasantness is tolerance-agnostic: an arterial-heavy route has the same
  // backroad-rule score regardless of category. Direct selection happens at
  // classifyRoutes, not by lowering the penalty here.
  const arterialMul = 0.70;
  const arterialFloor = -45;
  const backroadBoost = (features.residentialPct - 40) * 0.15;        // ~ -6 .. +9
  const arterialPenalty = ABLATION.motorwayScoreOff
    ? 0
    : Math.max(0, features.highwayPct - 5) * arterialMul;
  const backroadAdj = backroadBoost - arterialPenalty;
  // Cap so this rule never single-handedly flips a clearly-better route.
  const cappedBackroadAdj = Math.max(arterialFloor, Math.min(12, backroadAdj));
  pleasant += cappedBackroadAdj;
  __praw("backroadBoost", backroadBoost);
  __praw("arterialPenalty", -arterialPenalty);
  __pt("backroadAdj.applied");
  profMatch += cappedBackroadAdj * 0.7;

  // WATER TIE-BREAKER (Morgan). Passing water is better than a boring main road
  // when two routes are otherwise comparable — but water is NOT worth a highway
  // detour. So it's a SMALL, percentage-weighted, capped bonus applied to EVERY
  // route (not only when the user opted into "coastal", which is where the water
  // credit used to live). A waterside back road beats a comparable dry one, while
  // a brief-water-touch highway route still loses on its (much larger) highway
  // penalty: "the reservoir should win the tie, but a tiny stretch of highway on
  // water should not beat back roads all the way."
  const waterAdjKm = (features.waterAdjacentMeters ?? 0) / 1000;
  const waterFrac = km > 0 ? Math.min(1, waterAdjKm / km) : 0;
  const waterTieBonus = Math.min(6, waterFrac * 12); // up to +6 when fully waterside
  if (waterTieBonus > 0) {
    pleasant += waterTieBonus;
    __pt("waterTieBonus");
    profMatch += waterTieBonus * 0.6;
  }

  // ---- CONTINUITY OF PLEASANTNESS ----
  // The router was reverting to arterials mid-route — pleasant at the
  // edges, ugly in the middle. We measure the actual *spread* of pleasant
  // segments across the route via segmented.pleasantSegmentRatio and
  // penalize routes whose pleasantness is concentrated rather than
  // continuous.
  // (#124) Uses the stronger (formerly scenic-only) magnitudes uniformly.
  // Continuity is a property of the route, not the tolerance choice — a
  // route with ugly stretches in the middle is just as broken-feeling
  // whether it ends up labeled Direct, Pleasant, or Scenic. Hard cap
  // applies in all categories: anything > 25% on ugly segments tops out
  // at 30/100 pleasant score.
  if (segmented) {
    const ratio = segmented.pleasantSegmentRatio ?? 0;
    const ugly = segmented.unpleasantDistanceRatio ?? 0;
    // Reward continuity heavily; punish routes that dip into ugly stretches.
    const contBoost = (ratio - 0.5) * 30;          // -15 .. +15
    const dipPenalty = Math.max(0, ugly - 0.10) * 80; // grows fast past 10%
    pleasant += contBoost - dipPenalty;
    __pt("continuityAdj");
    profMatch += (contBoost - dipPenalty) * 0.9;
    // Hard cap: route that runs >25% on negative segments can never
    // score above 30 — it broke the promise mid-route.
    if (ugly >= 0.25) {
      const __b_brokenPromiseCeiling = pleasant;
      pleasant = Math.min(pleasant, 30);
      profMatch = Math.min(profMatch, 30);
      __mut("brokenPromiseCeiling", __b_brokenPromiseCeiling, pleasant);
    }
  }

  // ---- GLOBAL SCENIC ATTRACTOR RULE ----
  // Any road that runs near a forest/park, historic site, or body of water
  // should automatically attract the route. This is a standing user rule
  // and applies on every mode/profile, regardless of selected preferences.
  // We boost based on the *named* features actually adjacent to the route
  // line (parkNames / natureNames / historicNames / waterNames) plus the
  // raw counts as a fallback signal.
  //
  //   each named park/forest area  → +4 (cap 12)
  //   each named historic site     → +5 (cap 15)
  //   each named body of water     → +4 (cap 12)
  //   raw park count fallback      → up to +6
  //   raw water % fallback         → up to +6
  // Total attractor boost capped at +28 so it nudges scoring strongly
  // without overwhelming hard guardrails (highways, tunnels, no-sidewalk).
  const namedParks = (features.parkNames?.length ?? 0) + (features.natureNames?.length ?? 0);
  const namedHistoric = features.historicNames?.length ?? 0;
  const namedWater = features.waterNames?.length ?? 0;
  const parkAttractor = Math.min(12, namedParks * 4) + Math.min(6, features.parks * 1.5);
  const historicAttractor = Math.min(15, namedHistoric * 5);
  const waterAttractor = Math.min(8, namedWater * 2.5) + Math.min(3, features.waterPct * 0.04);
  const scenicAttractor = Math.min(28, parkAttractor + historicAttractor + waterAttractor);
  pleasant += scenicAttractor;
  __pt("scenicAttractor");
  profMatch += scenicAttractor * 0.8;

  // ---- "Coastal views" preference boost (opt-in) ----
  // Tunable via COASTAL_WEIGHTS. Geometry-derived: based on actual OSM
  // coastline / waterside meters along this route, NOT on highway labels.
  // Capped to keep the existing scoring envelope predictable.
  const coastalAdj = coastalBoost(features, prefs);
  if (coastalAdj > 0) {
    pleasant += coastalAdj;
    __pt("coastalAdj");
    profMatch += coastalAdj * 0.85;
  }

  // ---- EPIC HIGHWAY EXCEPTION ----
  // When the route runs a meaningful stretch of a legendary scenic highway
  // (Pacific Coast Highway, Route 66, Blue Ridge Parkway, Great Ocean Road,
  // etc.), it should win against the usual backroads-first rule. We:
  //   1) Add a strong positive boost proportional to epic-highway km.
  //   2) Reverse the arterial/highway penalties applied above for the
  //      portion of the route that ran on the epic highway.
  // Net effect: a 30 mi PCH stretch easily becomes the top pleasant route.
  const epicMeters = route.epicHighwayMeters ?? 0;
  if (epicMeters > 1000) {
    const epicKm = epicMeters / 1000;
    const epicShare = Math.min(1, epicMeters / Math.max(1, route.distance));
    // Up to +35 for routes that ARE the epic highway end-to-end.
    const epicBoost = Math.min(35, epicKm * 1.5 + epicShare * 25);
    // Refund the arterial/highway penalties that were applied above on the
    // epic share of the route — that's the whole point of the exception.
    const refundedPenalty = arterialPenalty * epicShare;
    const epicAdj = epicBoost + refundedPenalty;
    pleasant += epicAdj;
    __pt("epicAdj");
    profMatch += epicAdj * 0.9;
    // Lift any prior "highway = bad" cap so the epic route isn't held down
    // by the avoidsHighways guardrail. The user explicitly said epic
    // highways should beat other options.
    if (avoidsHighways) {
      const __b_epicHighwayLift = pleasant;
      pleasant = Math.max(pleasant, 75);
      profMatch = Math.max(profMatch, 70);
      __mut("epicHighwayLift", __b_epicHighwayLift, pleasant);
    }
  }

  // Water should be a decisive route-shaper only when the user explicitly
  // asked for it. Otherwise it remains a nice signal, but a waterfront loop
  // should not beat a materially more direct route just because a named bay
  // or reservoir sits nearby.
  const wantsWater = profile === "water" || prefs.includes("scenic");
  if (!wantsWater && fastestDuration > 0 && route.duration > fastestDuration) {
    const extraRatio = (route.duration - fastestDuration) / fastestDuration;
    const waterDominance = Math.max(0, (features.waterNames?.length ?? 0) - namedParks - namedHistoric);
    const penalty = Math.min(18, extraRatio * 55 + waterDominance * 3);
    pleasant -= penalty;
    __pt("waterDominancePenalty");
    profMatch -= penalty * 0.8;
  }

  // Surface epic-highway evidence chip when applicable.
  if ((route.epicHighwayMeters ?? 0) > 1000 && route.epicHighwayLabels?.length) {
    const label = route.epicHighwayLabels[0];
    // Aug 19 units audit: was hardcoded "mi". Evidence is baked at scoring
    // time, so the chip reflects the units at generation — acceptable, since
    // regenerating the route rebakes it.
    evidence = [`${fmtDistance(route.epicHighwayMeters ?? 0)} along ${label}`, ...evidence];
  }

  // (Removed the "Unavoidable arterials on this corridor (N%)" chip — Morgan:
  // it's a negative callout that doesn't help the user, and we only surface
  // what's ON a route, not what it lacks.)

  // ---- Time-aware proxy weighting ----
  // Lightweight modifier (NOT live traffic — purely time-of-day heuristics)
  // that nudges scores based on predictable patterns: rush-hour arterial
  // misery, isolated parks at night, weekend scenic-route bonus, etc.
  // Capped at ±8 pts so it can't single-handedly flip a clearly-better route.
  const timeAdj = timeAwareAdjustment(features, now);
  if (timeAdj.delta !== 0) {
    pleasant += timeAdj.delta;
    __pt("timeOfDayAdj");
    profMatch += timeAdj.delta * 0.6;
    if (timeAdj.chip) evidence = [timeAdj.chip, ...evidence];
  }

  // ---- Live-traffic penalty (#65, #66) ----
  // Predicted-grim and actually-grim are different things. The time-aware
  // layer above is a heuristic — at 5pm, downtown probably sucks. Live
  // Mapbox traffic is ground truth — right now, this exact route is
  // moving at 8 mph because of a fender-bender on Broadway.
  //
  // Two penalty paths in priority order:
  //
  //   (A) Segment-level (#66, preferred): when Mapbox returned per-leg
  //       congestion annotations, weight each leg's delay by severity.
  //       Severe congestion on a small stretch is more user-relevant
  //       than uniform "moderate" across the whole route, because the
  //       user remembers crawling for 90 seconds — not averaged numbers.
  //
  //   (B) Whole-route (#65, fallback): when only an aggregate delay is
  //       known (no leg data), use the simpler 4 pts/min × delay rule.
  //
  // Either path caps at -35 (slightly steeper than the #65-only cap
  // because severe congestion legitimately deserves a bigger swing).
  // Surfaces an evidence chip when the worst stretch is meaningful.
  const SEVERITY_WEIGHT: Record<"low" | "moderate" | "heavy" | "severe" | "unknown", number> = {
    unknown: 0.5, // treat unknown as light; not no signal
    low: 0,
    moderate: 1.0,
    heavy: 2.5,
    severe: 4.0,
  };
  if (trafficLegs && trafficLegs.length > 0) {
    // (A) — segment-level
    let weightedDelaySec = 0;
    let worstSeverity: "low" | "moderate" | "heavy" | "severe" | "unknown" = "low";
    let worstSeverityWeight = 0;
    let secInWorst = 0;
    let worstLegIdx = -1;
    let worstLegDelay = 0; // delay on the single worst-severity leg, for road-name match
    for (let i = 0; i < trafficLegs.length; i++) {
      const leg = trafficLegs[i];
      const baseline = leg.freeFlowS ?? leg.durationS; // 0 delay if no baseline
      const delay = Math.max(0, leg.durationS - baseline);
      const w = SEVERITY_WEIGHT[leg.congestion];
      weightedDelaySec += delay * w;
      // Track worst-severity exposure for the chip.
      if (w > worstSeverityWeight) {
        worstSeverity = leg.congestion;
        worstSeverityWeight = w;
        secInWorst = delay;
        worstLegIdx = i;
        worstLegDelay = delay;
      } else if (leg.congestion === worstSeverity) {
        secInWorst += delay;
        // Among same-severity legs, keep the one with the largest single
        // delay as the named one — most likely the actual chokepoint.
        if (delay > worstLegDelay) {
          worstLegIdx = i;
          worstLegDelay = delay;
        }
      }
    }
    const weightedMin = weightedDelaySec / 60;
    const trafficPenalty = Math.min(35, weightedMin * 4);
    if (trafficPenalty > 0.5) {
      pleasant -= trafficPenalty;
      __pt("trafficPenalty");
      profMatch -= trafficPenalty * 0.5;
    }
    // Road-name lookup for the chip (#67). When the worst leg has coords
    // and the route has step data, snap the leg's midpoint to the route
    // polyline, walk steps by cumulative distance to find which step
    // covers that point, and use its roadName / roadRef. When any piece
    // is missing (no coords, no steps, no name), fall back to the
    // generic "on this route" phrasing.
    let worstRoadName: string | undefined;
    if (worstLegIdx >= 0) {
      const worstLeg = trafficLegs[worstLegIdx];
      const sc = worstLeg.startCoord;
      const ec = worstLeg.endCoord;
      if (sc && ec && route.coordinates.length >= 2 && route.steps.length > 0) {
        const mid: LngLat = [(sc[0] + ec[0]) / 2, (sc[1] + ec[1]) / 2];
        try {
          const snap = nearestPointOnPolyline(mid, route.coordinates);
          // Walk steps by cumulative distance until we cover snap.along.
          let cumDist = 0;
          for (const step of route.steps) {
            cumDist += step.distance;
            if (cumDist >= snap.along) {
              const nm =
                step.roadName ||
                step.roadRef ||
                step.highTrafficName;
              if (typeof nm === "string" && nm.trim().length > 0) {
                worstRoadName = nm.trim();
              }
              break;
            }
          }
        } catch {
          // Best-effort — never let chip generation break the score.
        }
      }
    }
    // Chip — only when the user-facing impact is meaningful. We surface the
    // worst-severity exposure rather than total weighted delay because that's
    // what the user will feel ("2 min stuck in heavy traffic on Broadway" >
    // "5 min of weighted delay across this route"). When we know the road
    // name, we name it; otherwise fall back to generic.
    if (secInWorst >= 90 && (worstSeverity === "heavy" || worstSeverity === "severe")) {
      const m = Math.max(1, Math.round(secInWorst / 60));
      const word = worstSeverity === "severe" ? "Severe" : "Heavy";
      const chip = worstRoadName
        ? `${word} traffic on ${worstRoadName} right now (${m} min)`
        : `${m} min in ${word.toLowerCase()} traffic on this route`;
      evidence = [chip, ...evidence];
    } else if (trafficPenalty > 5) {
      // Material penalty but no single severe stretch — surface the
      // aggregate so the score change isn't mysterious.
      const m = Math.max(1, Math.round(weightedMin));
      const chip = worstRoadName
        ? `${m} min of live traffic (slowest near ${worstRoadName})`
        : `${m} min of live traffic on this route`;
      evidence = [chip, ...evidence];
    }
  } else if (typeof trafficDelaySec === "number" && trafficDelaySec > 30) {
    // (B) — fallback whole-route path
    const delayMin = trafficDelaySec / 60;
    const trafficPenalty = Math.min(25, delayMin * 4);
    pleasant -= trafficPenalty;
    __pt("trafficPenalty2");
    profMatch -= trafficPenalty * 0.5;
    if (delayMin >= 3) {
      const m = Math.round(delayMin);
      evidence = [`${m} min of live traffic on this route`, ...evidence];
    }
  }

  // ---- HUDSON VALLEY PLEASANT ROAD INDEX (Morgan's curated layer) ----
  // A STRONG ranking lever in the Hudson Valley: reward routes that run the
  // hand-scored 3s/2s (estate lanes, ridge roads) and penalise the 0s (I-684,
  // Route 9/9A). Region-gated and bounded; "the 0s are as important as the 3s".
  {
    const cov = hudsonCoverage(route.steps, route.coordinates);
    const adj = hudsonScoreAdjustment(cov);
    if (adj !== 0) {
      pleasant += adj;
      __pt("hudsonAdj");
      profMatch += adj * 0.7;
      if (adj > 0) scenic = Math.min(100, scenic + Math.min(20, adj * 0.6));
      if (cov.preferNames.length > 0 && adj > 0) {
        evidence = [`Runs ${cov.preferNames.slice(0, 2).join(" and ")} — pleasant local roads`, ...evidence];
      } else if (cov.avoidNames.length > 0 && adj < 0) {
        evidence = [`Heavy on ${cov.avoidNames.slice(0, 2).join(" and ")}`, ...evidence];
      }
    }
  }

  // ---- REGIONAL PLEASANT ROAD INDEX (Paris, Riviera, Provence, Vermont, …) ----
  // The same curated lever as the Hudson Valley, generalized to every region
  // Morgan has hand-scored. Region-gated by disjoint bounding boxes, so it
  // never double-counts with the Hudson block above (a route falls in at most
  // one region).
  {
    const cov = regionRoadCoverage(route.steps, route.coordinates);
    const adj = regionRoadScoreAdjustment(cov);
    if (adj !== 0) {
      pleasant += adj;
      __pt("regionRoadAdj");
      profMatch += adj * 0.7;
      if (adj > 0) scenic = Math.min(100, scenic + Math.min(20, adj * 0.6));
      if (cov.preferNames.length > 0 && adj > 0) {
        const where = cov.regionLabel ? ` — pleasant ${cov.regionLabel} roads` : " — pleasant local roads";
        evidence = [`Runs ${cov.preferNames.slice(0, 2).join(" and ")}${where}`, ...evidence];
      } else if (cov.avoidNames.length > 0 && adj < 0) {
        evidence = [`Heavy on ${cov.avoidNames.slice(0, 2).join(" and ")}`, ...evidence];
      }
    }
  }

  // Sanity floor — if any single component is strong (community, scenic, or
  // preference match all ≥ 50), the overall pleasant score should not drop
  // into single digits. Single-digit scores on routes that clearly have a
  // strong dimension are an artifact of stacked penalties (named-arterial,
  // detour-overshoot, etc.) compounding past the point of usefulness. Real-
  // world feedback: "the pleasantness rating is off — if the drive is
  // pleasant, we should not be getting 3/100." Floor relative to the
  // strongest component so the badge stays informative.
  // Only a component genuinely ABOVE the neutral 50 baseline is evidence the
  // drive is pleasant — community defaults to 50 with no data, and
  // preferenceMatch is 50 with no matched preference, so flooring at exactly 50
  // would lift every data-less route to 28 and swallow real additive boosts
  // (bike-network quality, historic districts, etc.). Require > 50.
  // RANKING AND DISPLAY ARE NOW TWO NUMBERS (Morgan, Aug 22).
  //
  // componentFloor exists to keep a badge informative — "if the drive is
  // pleasant, we should not be getting 3/100". That is a PRESENTATION concern,
  // and it was also deciding which route won. Measured across four corridors
  // on 22 Aug, after the NaN, road-class and deadline fixes were in:
  //
  //   Pleasantville  7 candidates, raw spread 41.2 pts -> all 55.0
  //   Ossining       5 candidates, raw spread 99.0 pts -> all 55.0
  //   White Plains   3 candidates, raw spread 71.1 pts -> all 39.0
  //   Katonah        5 candidates, raw spread 79.1 pts -> all 41.0
  //
  // and on Pleasantville it INVERTED the order: the raw-38.4 candidate lost to
  // the raw-2.8 one. A floor may only ever raise a number, so applying it
  // before ordering compresses every candidate onto the same value and hands
  // the decision to whatever the sort does with ties.
  //
  // `pleasant` (used for ranking) no longer carries the floor. `pleasantDisplay`
  // does, and is what the UI shows. The continuous model, the weights, the
  // thresholds and the other safety-honesty mutations are untouched.
  //
  // NOTE, deliberately surfaced: threshold comparisons elsewhere (minScore,
  // "+8 better than fastest", "within 10 of the pleasant pick") now compare an
  // UNFLOORED value. Those constants are unchanged, but the scale they see is
  // the continuous one. That is inherent to the separation, not a side effect
  // of it - a floored input is exactly what made them meaningless.
  const strongest = ABLATION.componentFloorOff ? 0 : Math.max(community, scenic, preferenceMatch);
  const __b_componentFloor = pleasant;
  // THE UNFLOORED RANKING VALUE (Morgan, Aug 22).
  //
  // Captured here, BEFORE componentFloor, and carried out as `pleasantPreFloor`.
  // `pleasant` keeps its existing meaning and its existing floor, because
  // thirty-plus readers compare it against tuned thresholds (minScore, "+8
  // better than fastest", "within 10 of the pleasant pick"). Redefining it
  // would bundle a broad threshold-semantics change into what must be a single
  // behavioural change: componentFloor can no longer alter candidate ordering.
  //
  // NOT CLAMPED AT 0, deliberately. Real Pleasantville candidates scored -42.6
  // and -2.8; clamping both to zero would re-flatten exactly the tail this
  // field exists to separate. It is the continuous model's own output.
  const pleasantPreFloor = pleasant;
  if (strongest > 50) {
    pleasant = Math.max(pleasant, Math.round(strongest * 0.55));
  } else if (strongest >= 35) {
    pleasant = Math.max(pleasant, Math.round(strongest * 0.45));
  }
  __mut("componentFloor", __b_componentFloor, pleasant);
  // Hard 0-100 envelope (Morgan's pl146 log, La Crèche): the additive boosts
  // above (scenic attractor, epic highway, nice towns, curated-road coverage,
  // byways...) are individually capped but can STACK past 100 — especially
  // when a starved OSM fetch zeroes the penalty side (hwy0 → no primary/
  // arterial penalties) while geometry-based boosts still fire. A 12-min
  // main-road loop scored 146 and beat the short park route. Nothing may
  // leave this function above 100.
  const __b_envelope0to100 = pleasant;
  pleasant = Math.min(100, Math.max(0, pleasant));
  profMatch = Math.min(100, Math.max(0, profMatch));
  __mut("envelope0to100", __b_envelope0to100, pleasant);

  // FINAL FINITE GUARD — defence in depth, NOT the fix (Morgan, Aug 22).
  //
  // The root cause ("lake" off OsmAreaKind) is fixed at source in api/osm.ts
  // and guarded at both lookup boundaries in segments.ts. This exists because
  // Math.min/Math.max/clamp all pass NaN straight through, so a non-finite
  // score reached the ranking silently, sorted unpredictably (every NaN
  // comparison is false), and on 22 Aug WON the Pleasant slot on a real
  // search. A route nobody could score must never outrank one we could.
  //
  // Loud, not silent: a future non-finite score should show up as a named
  // fault, not as three mysterious no-op mutations. 0 is deliberate — it is
  // the bottom of the envelope, so a broken candidate loses rather than wins.
  if (!Number.isFinite(pleasant) || !Number.isFinite(profMatch)) {
    // eslint-disable-next-line no-console
    console.error(
      `[scoring] NON-FINITE SCORE reaching ranking — pleasant=${String(pleasant)} ` +
      `profMatch=${String(profMatch)}. Forced to 0 so it cannot outrank a scored ` +
      `route. This is a BUG upstream of here, not a data condition.`,
    );
    if (!Number.isFinite(pleasant)) pleasant = 0;
    if (!Number.isFinite(profMatch)) profMatch = 0;
  }

  return {
    route,
    features,
    scores: {
      preferenceMatch: Math.round(preferenceMatch),
      scenic: Math.round(scenic),
      efficiency: Math.round(efficiency),
      calm: Math.round(calm),
      community: Math.round(community),
      pleasant: Math.round(pleasant),
      /**
       * ORDERING ONLY. The score as it stands IMMEDIATELY BEFORE componentFloor
       * — not the untouched continuous-model output. Every earlier mutation
       * (brokenPromiseCeiling and the rest of the safety-honesty set) has
       * already been applied; only the display floor and the final envelope
       * have not. Named for that, so nobody reads it as "the raw model score".
       *
       * Kept to one decimal, and NOT clamped at 0 — real candidates scored
       * -42.6 and -2.8, and clamping both to zero would re-flatten exactly the
       * tail this field exists to separate.
       *
       * Sorting, winner selection and ranking comparisons read this. Every
       * eligibility and safety threshold still reads `pleasant`.
       */
      pleasantPreFloor: Number.isFinite(pleasantPreFloor) ? Math.round(pleasantPreFloor * 10) / 10 : 0,
      profileMatch: Math.round(profMatch),
    },
    // Observation only. Nothing reads this to make a decision.
    breakdown: {
      base: __base,
      baseParts: __baseParts,
      terms: __terms,
      final: pleasant,
      // If this is not ~0, a term moved `pleasant` without being labelled and
      // the decomposition is incomplete. Better to print the hole than to
      // present a tidy total that quietly omits something.
      unexplained:
        pleasant - (__base + __terms.filter((t) => t.applied).reduce((a, t) => a + t.delta, 0)),
    },
    dimensions,
    evidence,
    explanation: buildExplanation(features, route, fastestDuration),
    segmented,
    googlePlaces,
    unsafe: unsafe || undefined,
  };
}

/**
 * Build the one-sentence "what this route runs through" summary used as
 * `ClassifiedRoute.explanation`.
 *
 * INTENTIONAL — what this string DOES NOT do:
 *   It never restates time deltas ("X extra min", "same time as the
 *   fastest"). The route cards own the time-comparison UI (the "+X min"
 *   pill + the ETA-confidence chip), and they compute it against the
 *   *displayed* baseline (Google when available, Mapbox otherwise).
 *   Repeating a time claim here would let the explanation contradict the
 *   badges sitting next to it. We describe categorical highlights only —
 *   parks, waterfront, coffee, named landmarks — and let the badges own
 *   the math.
 *
 * Highlight categories surfaced (in priority order):
 *   1. Parks — named when known (e.g. "Prospect Park"), otherwise count.
 *   2. Waterfront — named body (e.g. "along the Hudson River") preferred,
 *      generic "waterfront blocks" only when waterPct is meaningful.
 *   3. Landmarks — named when known, otherwise count.
 *   4. Coffee — explicit category mention so users see it as a stop type,
 *      not a vague extra.
 *   5. Quiet streets — only when nothing more concrete is available.
 *
 * Up to 3 categories are joined with the locale's serial connector so a
 * route running by parks AND water AND coffee can advertise all three
 * (previously capped at 2 — coffee got dropped on rich routes).
 *
 * Exported for direct unit testing; not part of the runtime surface.
 */
export function buildExplanation(f: FeatureSummary, r: RawRoute, _fastest: number): string {
  // All copy comes from the locale dictionary so EN/ES/FR phrasing stays
  // in lock-step with the route classification labels rendered on the card.
  const t = getRouteCopy();

  // Epic highway short-circuit — name it explicitly when present.
  if ((r.epicHighwayMeters ?? 0) > 1000 && r.epicHighwayLabels?.length) {
    const label = r.epicHighwayLabels[0];
    // Aug 19 units audit: the template used to append " mi" itself, so ES/FR
    // and metric drivers all got miles. The caller now passes a fully
    // formatted distance in the driver's chosen system.
    return t.explanation.epicHighway(fmtDistance(r.epicHighwayMeters ?? 0), label);
  }

  // Collect highlight phrases by category. Each branch contributes at
  // most one phrase so the final list captures *category breadth*, not
  // duplicates of a single feature.
  const highlights: string[] = [];

  // Parks — named when known.
  if (f.parkNames?.length) {
    highlights.push(f.parkNames[0]);
  } else if (f.parks > 0) {
    highlights.push(t.highlights.parks(f.parks));
  }

  // Waterfront — promoted ahead of landmarks/coffee so routes near water
  // always advertise it. Named body preferred.
  if (f.waterNames?.length) {
    const names = f.waterNames.slice(0, 2).join(t.explanation.and);
    highlights.push(t.highlights.waterAlongNamed(names));
  } else if ((f.waterPct ?? 0) > 25) {
    highlights.push(t.highlights.waterfrontBlocks);
  }

  // Landmarks — concrete anchors users recognize.
  if (f.landmarkNames?.length) {
    highlights.push(f.landmarkNames[0]);
  } else if (f.landmarks > 0) {
    highlights.push(t.highlights.landmarks(f.landmarks));
  }

  // Coffee — explicit category mention. Always surfaces when present so
  // "coffee stops" reads as a deliberate route property, not noise.
  if (f.cafes > 0) {
    highlights.push(
      f.cafes === 1
        ? t.highlights.coffeeOne
        : t.highlights.coffeeMany(t.numberWord(f.cafes)),
    );
  }

  // Quiet streets — only when we have nothing more concrete to say.
  if (highlights.length === 0 && (f.residentialPct ?? 0) > 55) {
    highlights.push(t.highlights.quietStreets);
  }

  const passes = highlights.length
    ? joinHighlights(highlights.slice(0, 3), t)
    : t.explanation.calmerPace;
  return t.explanation.routesThrough(passes);
}

/** Locale-aware serial join: ["a", "b"] → "a and b"; ["a", "b", "c"] →
 *  "a, b and c". Comma-joins everything except the last item then uses
 *  the locale's `and` connector before the last — keeps EN/ES/FR
 *  phrasing identical to the existing 2-item form, just with an extra
 *  leading ", " segment for richer routes. */
function joinHighlights(items: string[], t: ReturnType<typeof getRouteCopy>): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return items.join(t.explanation.and);
  const head = items.slice(0, -1).join(", ");
  return head + t.explanation.and + items[items.length - 1];
}

function numberWord(n: number): string {
  return getRouteCopy().numberWord(n);
}

/**
 * Classify routes into fastest / scenic / pleasant.
 * `pleasantTolerance` controls how much slower than the fastest a route may
 * be and still qualify as the "pleasant" pick. Default 1.35 (35% slower);
 * pass a higher value when the user has explicitly opted into slower options
 * like `no-highways` for a long-distance trip.
 */
/** Minimum pleasant score before we're willing to label a route "pleasant".
 *  Below this, we'd rather show only fastest+scenic than mislead the user
 *  into thinking we found something genuinely nice. */
const MIN_PLEASANT_SCORE = 55;

/** Geometry signature used to dedupe Mapbox alternatives that share most of
 *  their road network (very common — Mapbox often returns 3 routes that
 *  diverge for one block). Buckets every ~50m of polyline into a 0.0005°
 *  grid (~55m) and signs the route by its sampled bucket sequence. Two
 *  routes overlapping ≥80% by sampled buckets are treated as duplicates. */
export function geometryBuckets(coords: LngLat[]): Set<string> {
  const out = new Set<string>();
  // Sample every Nth coord, bounded so very long routes don't blow up memory.
  const stride = Math.max(1, Math.floor(coords.length / 200));
  for (let i = 0; i < coords.length; i += stride) {
    const [lng, lat] = coords[i];
    out.add(`${Math.round(lng * 2000)}|${Math.round(lat * 2000)}`);
  }
  return out;
}

export function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let inter = 0;
  for (const x of a) if (b.has(x)) inter++;
  const union = a.size + b.size - inter;
  return union === 0 ? 0 : inter / union;
}

/** Fraction of the route's polyline that revisits ground it already covered.
 *  An out-and-back detour (walk down a road to a park, then back up the same
 *  road) shows up as ~0.2-0.4 here. A clean point-A-to-point-B route is ~0. */
export function selfOverlapFraction(coords: LngLat[]): number {
  if (coords.length < 4) return 0;
  // Sample ~300 points evenly across the polyline.
  const stride = Math.max(1, Math.floor(coords.length / 300));
  const samples: { lng: number; lat: number; idx: number }[] = [];
  for (let i = 0; i < coords.length; i += stride) {
    samples.push({ lng: coords[i][0], lat: coords[i][1], idx: i });
  }
  // Index samples into a 12m grid for fast neighbor lookup. Cell size is
  // intentionally smaller than the proximity threshold (~25m) so a true
  // overlap pair always lands within a 3×3 neighborhood — fixes the
  // grid-boundary blind spot the old Math.round / 25m grid had where a
  // pair within 20m could be bucketed separately.
  const GRID_DEG = 0.0001; // ~11m
  const PROXIMITY_M = 25;
  const MIN_INDEX_GAP = 4; // skip near-neighbors in polyline order
  const cells = new Map<string, number[]>();
  for (let s = 0; s < samples.length; s++) {
    const cx = Math.floor(samples[s].lng / GRID_DEG);
    const cy = Math.floor(samples[s].lat / GRID_DEG);
    const key = `${cx}|${cy}`;
    const arr = cells.get(key) ?? [];
    arr.push(s);
    cells.set(key, arr);
  }
  // For each sample, look at its own cell + 8 neighbors. Count it as a
  // "repeat" if it has any non-adjacent neighbor within PROXIMITY_M.
  const flagged = new Set<number>();
  for (let s = 0; s < samples.length; s++) {
    const a = samples[s];
    if (flagged.has(s)) continue;
    const cx = Math.floor(a.lng / GRID_DEG);
    const cy = Math.floor(a.lat / GRID_DEG);
    outer: for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const arr = cells.get(`${cx + dx}|${cy + dy}`);
        if (!arr) continue;
        for (const j of arr) {
          if (j === s) continue;
          if (Math.abs(j - s) < MIN_INDEX_GAP) continue;
          const b = samples[j];
          const meanLat = (a.lat + b.lat) / 2;
          const dxM = (b.lng - a.lng) * 111320 * Math.cos(meanLat * Math.PI / 180);
          const dyM = (b.lat - a.lat) * 110540;
          if (Math.hypot(dxM, dyM) <= PROXIMITY_M) {
            flagged.add(s);
            flagged.add(j);
            break outer;
          }
        }
      }
    }
  }
  return samples.length === 0 ? 0 : flagged.size / samples.length;
}

/** (Morgan, La Crèche July 15) The honest "doubles back on the same road"
 *  measure. `selfOverlapFraction` flags ANY two route points within 25m of
 *  each other regardless of direction — so a winding park road whose curves
 *  swing near each other (Sleepy Hollow Rd through Rockefeller) read as a
 *  retrace and the BEST candidate in the pool got dropped, while genuinely
 *  doubling-back engine routes were never checked at all.
 *
 *  This returns the longest CONTIGUOUS stretch, in meters, where the route
 *  runs alongside another part of itself (within 25m) AND the two local
 *  headings are parallel or anti-parallel (same road alignment). Curvy
 *  roads produce only scattered, short flags; perpendicular self-crossings
 *  (an overpass) don't count; a real out-and-back produces one long run. */
export function contiguousRetraceMeters(coords: LngLat[]): number {
  if (coords.length < 6) return 0;
  const stride = Math.max(1, Math.floor(coords.length / 300));
  const samples: { lng: number; lat: number }[] = [];
  for (let i = 0; i < coords.length; i += stride) {
    samples.push({ lng: coords[i][0], lat: coords[i][1] });
  }
  if (samples.length < 6) return 0;
  const mDist = (a: { lng: number; lat: number }, b: { lng: number; lat: number }) => {
    const meanLat = ((a.lat + b.lat) / 2) * Math.PI / 180;
    const dxM = (b.lng - a.lng) * 111320 * Math.cos(meanLat);
    const dyM = (b.lat - a.lat) * 110540;
    return Math.hypot(dxM, dyM);
  };
  // Approximate along-route meters per sample.
  let total = 0;
  for (let s = 1; s < samples.length; s++) total += mDist(samples[s - 1], samples[s]);
  const perSample = total / Math.max(1, samples.length - 1);
  if (perSample <= 0) return 0;
  const bearing = (s: number): number => {
    const a = samples[Math.max(0, s - 1)], b = samples[Math.min(samples.length - 1, s + 1)];
    return Math.atan2(b.lat - a.lat, (b.lng - a.lng) * Math.cos((a.lat * Math.PI) / 180));
  };
  const GRID_DEG = 0.0001; // ~11m
  const PROXIMITY_M = 25;
  const minIndexGap = Math.max(3, Math.ceil(120 / perSample)); // ≥120m apart along the route
  const cells = new Map<string, number[]>();
  for (let s = 0; s < samples.length; s++) {
    const key = `${Math.floor(samples[s].lng / GRID_DEG)}|${Math.floor(samples[s].lat / GRID_DEG)}`;
    const arr = cells.get(key) ?? [];
    arr.push(s);
    cells.set(key, arr);
  }
  const flagged: boolean[] = new Array(samples.length).fill(false);
  for (let s = 0; s < samples.length; s++) {
    const a = samples[s];
    const cx = Math.floor(a.lng / GRID_DEG);
    const cy = Math.floor(a.lat / GRID_DEG);
    outer: for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const arr = cells.get(`${cx + dx}|${cy + dy}`);
        if (!arr) continue;
        for (const j of arr) {
          if (Math.abs(j - s) < minIndexGap) continue;
          if (mDist(a, samples[j]) > PROXIMITY_M) continue;
          // Same-road alignment: parallel or anti-parallel within ~35°.
          let dAng = Math.abs(bearing(s) - bearing(j));
          if (dAng > Math.PI) dAng = 2 * Math.PI - dAng;
          const alignment = Math.min(dAng, Math.PI - dAng); // 0 = aligned
          if (alignment > (35 * Math.PI) / 180) continue;
          flagged[s] = true;
          break outer;
        }
      }
    }
  }
  // Longest contiguous flagged run (allow a 1-sample gap so GPS jitter
  // doesn't split one real retrace into two short runs).
  let best = 0, run = 0, gap = 0;
  for (let s = 0; s < samples.length; s++) {
    if (flagged[s]) {
      run += 1 + gap;
      gap = 0;
      best = Math.max(best, run);
    } else if (run > 0 && gap === 0) {
      gap = 1;
    } else {
      run = 0;
      gap = 0;
    }
  }
  return best * perSample;
}

/** (#126) Returns true when the polyline returns to within ~50m of a
 *  point it previously visited, with at least ~200m of intervening
 *  polyline distance between the two visits.
 *
 *  This is a tight, absolute-distance detector for loops and back-tracks
 *  that the other two filters miss:
 *    - `selfOverlapFraction` is a percentage of total route, so a 150m
 *      loop on a 7km trip looks like 2% and slides past a 1.5% threshold.
 *    - `hasUTurn` only fires when two segments are physically PARALLEL
 *      within 40m, so it misses P-shape loops where the user makes three
 *      right turns and ends up back on a previous street ~100m away.
 *
 *  `hasNearReturn` ignores fractions and ignores headings — if the
 *  polyline ever closes back near itself with non-trivial intervening
 *  distance, it's a loop. Period. */
// ---------------------------------------------------------------------------
// MEMOISED (Aug 27 2026 — the physical-iPhone freeze).
//
// THE BUG. `hasNearReturn` is the most expensive predicate in the file and the
// only one that does not sample-reduce: it walks every coordinate and scans a
// 9x9 grid neighbourhood around each. Measured on a 3,000-point route (the
// MAX_DISPLAY_POINTS cap) it costs ~60 ms — versus ~1.3 ms for
// selfOverlapFraction, ~1.3 ms for hasNeighborhoodDetour and ~0.3 ms for
// hasUTurn. It is 98% of the coherence gate.
//
// It is also evaluated over and over on the SAME array. `makeCoherenceGate`'s
// `passes` calls it twice; `classifyThreeCards` builds a gate for its
// quarantine pass and `classifyRoutes` builds three more (direct / pleasant /
// scenic); and `generateRoutes` runs `classifyThreeCards` four to five times
// per search. Measured end to end: one `classifyThreeCards` over a 15-route
// pool of 3,000-point routes took 8.0 s in the container — ~8.7 recomputations
// of the identical verdict per route, per pass.
//
// WHY THAT BECAME A FREEZE, AND ONLY ON THE DEGRADED PATH. Normally those
// passes are separated by real Overpass awaits, so the WebView gets the main
// thread back between them. When every endpoint times out, every promise the
// tail awaits is ALREADY settled, so the awaits resolve in the microtask queue
// and the whole classification tail drains as one uninterrupted macrotask.
// That is what WebKit reports as gesture recognizers blocked for 40-60 s with
// no JS exception: not a hang, one enormous synchronous job.
//
// THE FIX IS A CACHE, NOT A NEW RULE. `computeNearReturn` is a pure function
// of the coordinate array and nothing in the app mutates a route's
// `coordinates` after it is built (the only `.push` sites in `src/` are
// `api/valhalla.ts` and `utils/polyline.ts`, both filling a fresh local array
// during decode). So the verdict is keyed on the array identity in a WeakMap:
// same array, same answer, computed once. No threshold, no exemption and no
// candidate changes — every call returns exactly what it returned before.
// ---------------------------------------------------------------------------
const NEAR_RETURN_MEMO = new WeakMap<LngLat[], boolean>();

export function hasNearReturn(coords: LngLat[]): boolean {
  const cached = NEAR_RETURN_MEMO.get(coords);
  if (cached !== undefined) return cached;
  const verdict = computeNearReturn(coords);
  NEAR_RETURN_MEMO.set(coords, verdict);
  return verdict;
}

function computeNearReturn(coords: LngLat[]): boolean {
  if (coords.length < 6) return false;
  // Compute cumulative polyline distance per coord index.
  const cum: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    const [pLng, pLat] = coords[i - 1];
    const [cLng, cLat] = coords[i];
    const meanLat = (pLat + cLat) / 2;
    const dx = (cLng - pLng) * 111320 * Math.cos(meanLat * Math.PI / 180);
    const dy = (cLat - pLat) * 110540;
    cum.push(cum[i - 1] + Math.hypot(dx, dy));
  }
  // (#154 → #158) Widened twice. Started at 50m / 200m, bumped to
  // 100m / 400m, still missed the Tarrytown loop on Archville →
  // Hackley School (parallel-street zigzag through downtown, separated
  // by ~120-200m in this neighborhood — wider than typical residential
  // grids). Now at 150m / 500m. The 150m proximity covers parallel
  // streets in downtowns where blocks are wider; the 500m polyline gap
  // keeps the filter from misfiring on natural curves on country
  // roads. Real-world assumption: any route that comes within 150m
  // of a point it visited 500m+ ago is looping — drop it. Long
  // scenic detours (reservoir, parkway) make continuous one-way
  // progress and don't come back near earlier points.
  const PROXIMITY_M = 150;     // how close is "back near here"
  // SCALE-AWARE GAP (Morgan, Aug 8 — San Jose → Flagstaff, the log that found
  // it: "[pool] coherence rejects by gate — neighborhoodDetour:5 nearReturn:7",
  // 13 of 14 candidates dead INCLUDING mapbox-direct, a straight run down I-40).
  //
  // A fixed 500 m gap is a sound loop test on a 5 km trip and a coin flip on a
  // 720 mi one: come within 150 m of a point you passed 600 m ago and you have
  // described every cloverleaf, switchback and sweeping S-curve in America. A
  // cross-country route passes hundreds. The test was guaranteed to fail at
  // road-trip length, which is why long trips have always collapsed to two
  // cards while Hudson Valley trips were fine.
  //
  // What actually matters is WASTED distance relative to the drive: travelling
  // 600 m to end up 150 m away is nothing across 1,159 km and everything across
  // 5 km. So the gap floor scales — 500 m or 2% of the trip, whichever is
  // larger. Trips under ~25 km are completely unchanged (2% < 500 m).
  const tripLengthM = cum[cum.length - 1];
  const MIN_POLYLINE_GAP_M = Math.max(500, tripLengthM * 0.02);

  // Bucket every coord into a 25m-resolution grid so we can find
  // nearby candidates in O(1) instead of O(n²).
  const GRID_DEG = 0.00022; // ~24m
  const cells = new Map<string, number[]>();
  for (let i = 0; i < coords.length; i++) {
    const cx = Math.floor(coords[i][0] / GRID_DEG);
    const cy = Math.floor(coords[i][1] / GRID_DEG);
    const key = `${cx}|${cy}`;
    const arr = cells.get(key) ?? [];
    arr.push(i);
    cells.set(key, arr);
  }
  // For each coord, look at its own + 8 neighboring cells. If any
  // candidate is within PROXIMITY_M geographically AND MIN_POLYLINE_GAP_M
  // along the polyline, we have a near-return — loop detected.
  // (#154 → #158) Search radius widened ±1 → ±3 → ±4 cells. Each cell
  // is ~24m wide, so ±4 catches points up to ~120m apart in the grid,
  // enough to find candidates within the new 150m proximity threshold
  // (the exact geo distance check below filters to ≤150m precisely).
  const CELL_SEARCH_RADIUS = 4;
  for (let i = 0; i < coords.length; i++) {
    const [aLng, aLat] = coords[i];
    const cx = Math.floor(aLng / GRID_DEG);
    const cy = Math.floor(aLat / GRID_DEG);
    for (let dx = -CELL_SEARCH_RADIUS; dx <= CELL_SEARCH_RADIUS; dx++) {
      for (let dy = -CELL_SEARCH_RADIUS; dy <= CELL_SEARCH_RADIUS; dy++) {
        const arr = cells.get(`${cx + dx}|${cy + dy}`);
        if (!arr) continue;
        for (const j of arr) {
          if (j <= i) continue; // only look forward, no double-counting
          const polylineGap = cum[j] - cum[i];
          if (polylineGap < MIN_POLYLINE_GAP_M) continue;
          const [bLng, bLat] = coords[j];
          const meanLat = (aLat + bLat) / 2;
          const ddx = (bLng - aLng) * 111320 * Math.cos(meanLat * Math.PI / 180);
          const ddy = (bLat - aLat) * 110540;
          const geo = Math.hypot(ddx, ddy);
          if (geo <= PROXIMITY_M) return true;
        }
      }
    }
  }
  return false;
}

/** (#136) Returns true when the polyline contains a "neighborhood detour" —
 *  a stretch where the route covers significant polyline distance with
 *  small straight-line displacement. The classic bug shape: route enters
 *  a downtown grid, zigzags through a few blocks on parallel streets, and
 *  exits not far from where it entered.
 *
 *  Existing filters miss this:
 *    - `selfOverlapFraction` is a percentage of total route, so a 1km
 *      Tarrytown zigzag inside a 7km trip looks like ~14% — and is below
 *      the 1.5% threshold anyway.
 *    - `hasUTurn` only fires when two segments are within 40m AND in
 *      opposite directions — but parallel downtown streets are 100-200m
 *      apart, too far for this filter.
 *    - `hasNearReturn` requires the polyline to come within 50m of
 *      itself; the parallel-streets shape never satisfies that either.
 *
 *  `hasNeighborhoodDetour` uses a sliding-window tortuosity check:
 *  for any 800m stretch of polyline, if the polyline distance divided
 *  by the straight-line displacement between the stretch's endpoints
 *  exceeds 2.0, the user has driven ≥ 2× the necessary distance in that
 *  window — that's a detour, drop it.
 *
 *  Tunings:
 *    WINDOW_M = 800m — long enough to span a downtown zigzag but small
 *      enough that natural curves on rural roads don't accumulate.
 *    TORTUOSITY_THRESHOLD = 2.0 — strict; reservoir-style winding roads
 *      typically run ~1.2-1.4 (curves but make net progress). 2.0 leaves
 *      headroom for natural geometry. */
export function hasNeighborhoodDetour(coords: LngLat[]): boolean {
  if (coords.length < 4) return false;
  // Cumulative polyline distance per coord index.
  const cum: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    cum.push(cum[i - 1] + haversine(coords[i - 1], coords[i]));
  }
  const total = cum[cum.length - 1];
  // (#136 tuning, post-real-drive): tightened from WINDOW_M=800,
  // THRESHOLD=2.0 because some loops have overall progress baked in
  // (the route makes net displacement DESPITE the zigzag). Smaller
  // window + lower threshold catches in-town loops without flagging
  // reservoir-style winding roads (typical tortuosity ~1.2-1.4).
  const WINDOW_M = 600;
  const TORTUOSITY_THRESHOLD = 1.7;
  if (total < WINDOW_M * 0.75) return false; // trip too short — skip

  // Single-pass sliding window. For each starting index `i`, advance
  // `j` until the polyline window from i→j spans at least WINDOW_M.
  // Then check polyline-distance / straight-line-displacement.
  // PROPORTION, NOT PRESENCE (Morgan, Aug 8 — same Flagstaff log that caught
  // hasNearReturn). Rejecting on the FIRST offending window is a sound test for
  // a town loop and an impossible one for a road trip: a 1,159 km drive holds
  // ~1,900 windows, and one interchange, hairpin or town main street fails it.
  // Five of the thirteen dead Flagstaff candidates died right here.
  //
  // The question worth asking is whether the route WANDERS, not whether it
  // contains a single bend. So count how much of the drive sits inside
  // offending windows: a block-loop on a short walk lights up a large share of
  // its windows, while ten interchanges across a cross-country drive light up a
  // fraction of a percent. Short trips behave exactly as before — a real loop
  // still blows past 2%.
  let offending = 0;
  let examined = 0;
  let j = 0;
  for (let i = 0; i < coords.length; i++) {
    while (j < coords.length && cum[j] - cum[i] < WINDOW_M) j++;
    if (j >= coords.length) break;
    const polylineDist = cum[j] - cum[i];
    const displacement = haversine(coords[i], coords[j]);
    if (displacement <= 0) continue;
    examined++;
    if (polylineDist / displacement > TORTUOSITY_THRESHOLD) offending++;
  }
  if (examined === 0) return false;
  // The cap is set from MEASURED shapes, not intuition (Aug 8):
  //     downtown zigzag, genuinely wandering ... 75.6%
  //     cross-country, 40 interchanges ........  5.8%
  //     cross-country, 12 interchanges ........  1.8%
  //     300 km of scenic S-curves .............  0.0%
  // Wandering and merely-bendy are an order of magnitude apart, so 25% sits in
  // the empty space between them: a route has to be a quarter zigzag before we
  // call it a detour. Route 66 is hundreds of miles of curling old highway and
  // must never trip this — that is the whole point of the fix.
  const OFFENDING_SHARE_CAP = 0.25;
  return offending / examined > OFFENDING_SHARE_CAP;
}

/** Returns true when the polyline contains a U-turn — two non-adjacent
 *  points that come within ~25m of each other AND are traveling in
 *  roughly opposite directions (heading within 35° of 180° apart).
 *
 *  Distinguishes a true U-turn from highway cloverleafs and roundabouts,
 *  which also produce close-by polyline points BUT in same/rotating
 *  directions (you enter east, exit east). This is the right shape for
 *  Morgan's rule: "there should be no U-turns." Loops and circles fall
 *  out of this check too, since by definition they involve reversed
 *  travel along the same area. */
export function hasUTurn(coords: LngLat[]): boolean {
  if (coords.length < 6) return false;
  const stride = Math.max(1, Math.floor(coords.length / 250));
  // Sample [position, heading-in-degrees] pairs along the polyline.
  const samples: Array<{ lng: number; lat: number; bearing: number; idx: number }> = [];
  for (let i = stride; i < coords.length - stride; i += stride) {
    const [aLng, aLat] = coords[i - stride];
    const [bLng, bLat] = coords[i + stride];
    const dLng = (bLng - aLng) * Math.cos(((aLat + bLat) / 2) * Math.PI / 180);
    const dLat = bLat - aLat;
    if (dLng === 0 && dLat === 0) continue;
    // 0° = east, 90° = north; values in [0, 360).
    let bearing = Math.atan2(dLat, dLng) * 180 / Math.PI;
    if (bearing < 0) bearing += 360;
    samples.push({ lng: coords[i][0], lat: coords[i][1], bearing, idx: i });
  }
  // Sort into a coarse spatial grid (50m cells) so we only compare points
  // that are physically close, instead of doing O(n²) over the whole route.
  const cells = new Map<string, number[]>();
  for (let s = 0; s < samples.length; s++) {
    const { lng, lat } = samples[s];
    const key = `${Math.round(lng * 2000)}|${Math.round(lat * 2000)}`;
    const arr = cells.get(key) ?? [];
    arr.push(s);
    cells.set(key, arr);
  }
  // Widened from 25m to 40m so wider hairpins / gentler U-turns
  // also get flagged. Cloverleaf segments still don't trigger because
  // their headings rotate continuously (you exit east when you entered
  // east) — the OPPOSITE_TOLERANCE check rejects them (#115).
  const MAX_DIST_M = 40;
  const MIN_GAP_SAMPLES = 4; // skip neighbors to avoid catching natural curvature
  const OPPOSITE_TOLERANCE_DEG = 45; // within 45° of being 180° apart counts
  for (const arr of cells.values()) {
    if (arr.length < 2) continue;
    for (let x = 0; x < arr.length; x++) {
      for (let y = x + 1; y < arr.length; y++) {
        const a = samples[arr[x]];
        const b = samples[arr[y]];
        if (Math.abs(arr[y] - arr[x]) < MIN_GAP_SAMPLES) continue;
        // Haversine-ish distance in meters at this latitude.
        const dx = (b.lng - a.lng) * 111320 * Math.cos(a.lat * Math.PI / 180);
        const dy = (b.lat - a.lat) * 110540;
        const dist = Math.hypot(dx, dy);
        if (dist > MAX_DIST_M) continue;
        // Angular distance between headings, normalized to [0, 180].
        let diff = Math.abs(a.bearing - b.bearing);
        if (diff > 180) diff = 360 - diff;
        // U-turn = heading diff is close to 180°.
        if (Math.abs(diff - 180) <= OPPOSITE_TOLERANCE_DEG) {
          return true;
        }
      }
    }
  }
  return false;
}

/** (#127) Returns true when the route's internal segments detour into
 *  an OSM-tagged dead-end way (noexit=yes or service=driveway).
 *
 *  Exception: a dead-end way that contains the trip's start coordinate
 *  OR end coordinate is allowed — the user can legitimately route TO
 *  or FROM an address on a dead-end street. The detector ignores any
 *  dead-end way within ~60m of either endpoint.
 *
 *  The "internal" check skips the first 50m and last 50m of the
 *  route's polyline so the start/end approaches don't false-trigger.
 *
 *  Inputs:
 *    coords          — the route's polyline
 *    deadEndLines    — all OSM ways tagged dead_end within the route
 *                       corridor (osmEnv.lines filtered by kind)
 *
 *  Behavior: any internal polyline point within ~30m of a non-exempted
 *  dead-end way → drop. */
export function hasDeadEndDetour(
  coords: LngLat[],
  deadEndLines: { line: LngLat[] }[],
): boolean {
  if (coords.length < 4 || deadEndLines.length === 0) return false;
  const ENDPOINT_EXEMPT_M = 60;      // dead-end ways near start/end are allowed
  const ENDPOINT_SKIP_M = 50;        // ignore the first/last 50m of the route

  const start: LngLat = coords[0];
  const end: LngLat = coords[coords.length - 1];

  // Fast geographic distance in meters between two coords.
  const distM = (a: LngLat, b: LngLat): number => {
    const meanLat = (a[1] + b[1]) / 2;
    const dx = (b[0] - a[0]) * 111320 * Math.cos(meanLat * Math.PI / 180);
    const dy = (b[1] - a[1]) * 110540;
    return Math.hypot(dx, dy);
  };

  // Find the cumulative-distance offsets so we can skip the first/last 50m.
  const cum: number[] = [0];
  for (let i = 1; i < coords.length; i++) {
    cum.push(cum[i - 1] + distM(coords[i - 1], coords[i]));
  }
  const totalLen = cum[cum.length - 1];
  if (totalLen <= 2 * ENDPOINT_SKIP_M) return false; // trip too short — skip

  // For each dead-end way, decide whether it's "exempt" (touches the
  // start or end address) or "internal" (must be avoided).
  const internalDeadEnds: LngLat[][] = [];
  for (const dl of deadEndLines) {
    let touchesStart = false;
    let touchesEnd = false;
    for (const p of dl.line) {
      if (!touchesStart && distM(p, start) <= ENDPOINT_EXEMPT_M) touchesStart = true;
      if (!touchesEnd && distM(p, end) <= ENDPOINT_EXEMPT_M) touchesEnd = true;
      if (touchesStart && touchesEnd) break;
    }
    if (touchesStart || touchesEnd) continue; // exempted
    internalDeadEnds.push(dl.line);
  }
  if (internalDeadEnds.length === 0) return false;

  // TRAVERSAL, NOT PROXIMITY (Morgan, Aug 23 — Pleasantville).
  //
  // This loop used to return true the moment ANY internal route point came
  // within 30 m of ANY point of a dead-end way. That is not a detour, it is a
  // radius. A cul-de-sac mouth or a driveway 10-25 m off the through-road you
  // are driving straight down would drop the candidate, and in a suburban
  // village those line every street. Production Pleasantville, 23 Aug:
  //
  //   [pool] scored=6 → dedupe=5 → coherence=0
  //   [pool] coherence rejects by gate — deadEnd:3 selfOverlap:2
  //
  // The whole pool, three of the five taken by this rule.
  //
  // The Paris protection is not weakened; it is given evidence. Ask how much
  // of the dead-end way the route actually COVERED: for each of the way's own
  // vertices, is an internal route point within a tight radius, and how far
  // along the way do the covered vertices span? Passing a mouth covers the
  // junction vertex and nothing beyond it — a span of ~0. Driving up the way
  // and back covers its length.
  //
  // Radius is 20 m, not 30: this now asks "was the route ON this way", which
  // is a tighter question than "was it near it". The 40 m span is what
  // separates touching an entrance from turning into it — a perpendicular
  // driveway only ever puts its first two or three vertices inside the radius.
  const TRAVERSAL_RADIUS_M = 20;
  const MIN_TRAVERSED_M = 40;
  const internalPoints: LngLat[] = [];
  for (let i = 0; i < coords.length; i++) {
    if (cum[i] < ENDPOINT_SKIP_M) continue;
    if (totalLen - cum[i] < ENDPOINT_SKIP_M) continue;
    internalPoints.push(coords[i]);
  }
  for (const line of internalDeadEnds) {
    // Cumulative distance along the dead-end way itself.
    const along: number[] = [0];
    for (let j = 1; j < line.length; j++) along.push(along[j - 1] + distM(line[j - 1], line[j]));
    let first = Infinity;
    let last = -Infinity;
    for (let j = 0; j < line.length; j++) {
      for (const c of internalPoints) {
        if (distM(c, line[j]) <= TRAVERSAL_RADIUS_M) {
          if (along[j] < first) first = along[j];
          if (along[j] > last) last = along[j];
          break;
        }
      }
    }
    if (last - first >= MIN_TRAVERSED_M) return true;
  }
  return false;
}

/** (#129) Three-cards-always classifier.
 *
 *  Returns EXACTLY 3 ClassifiedRoute objects, one per category, in the
 *  order [direct, pleasant, scenic]. Every slot is always populated;
 *  when a category has no meaningfully-different option, it surfaces
 *  the engine fastest with `sameAsFastest: true` so the UI can render
 *  honestly ("No Pleasant option for this trip", etc.).
 *
 *  Selection per category:
 *    DIRECT  — engine fastest (lowest duration in `scored`).
 *    PLEASANT — highest pleasantness within max(25%, 10-min floor)
 *               time cap. If no candidate is meaningfully different
 *               from Direct (jaccard ≥ 0.85 against direct polyline),
 *               surface Direct with sameAsFastest=true.
 *    SCENIC  — byway-first then pleasantness. No time cap. Same
 *               sameAsFastest fallback rule.
 *
 *  Inputs:
 *    scored — full pool of scored routes (post-summarizeFeatures + scoreRoute)
 *    opts   — same shape as classifyRoutes; opts.osmEnv used for dead-end
 *             detection; opts.mode controls mode-specific filters.
 *
 *  Does NOT change the existing classifyRoutes behavior. Callers opt in
 *  by calling this instead.
 *
 *  Sub-ticket of #125. Stage 1 of the rebuild.
 */

/** (#160) Backtrack-toward-destination loop detector. Returns true when
 *  the polyline's distance-to-destination first reaches a minimum, then
 *  later exceeds that minimum by more than `backtrackThresholdM` meters.
 *
 *  This is a different shape from `hasNearReturn` (which checks whether
 *  the polyline revisits a nearby point). Some loops are too wide for
 *  proximity-based detection — the parallel streets are 200m+ apart so
 *  hasNearReturn at any reasonable threshold misses them. But ALL loops
 *  share the same behavioral signature: at some point the driver is
 *  closer to the destination than they will be a moment later. That's
 *  a backtrack.
 *
 *  Real-world assumption: a route making honest progress toward the
 *  destination has monotonically (mostly) decreasing distance-to-dest.
 *  Tiny ups and downs from road curves are normal — anything over 300m
 *  of net moving-away is wandering. This filter is for Pleasant + Direct
 *  pools. Scenic detours intentionally wander (reservoir route goes
 *  AWAY from destination toward the reservoir, then comes back) — the
 *  caller should NOT apply this to the Scenic pool. */
export function hasBacktrackTowardDestination(
  coords: LngLat[],
  destination: LngLat,
  backtrackThresholdM?: number,
  /** Filled in for the fate log (rule 17). Diagnosis only — never read by
   *  logic. "lost mapbox-pleasant:1" names the victim but not the reason, and
   *  on Umbria → Nice the reason is the whole question: is 20 km of honest
   *  Apennine geometry being read as a U-turn? */
  report?: { excursionM: number; thresholdM: number; straightLineM: number },
): boolean {
  if (coords.length < 6) return false;
  // SCALE-AWARE (Morgan, Aug 8 — the third gate of the same family, found
  // because [pleasant] never printed on San Jose → Flagstaff: this filter runs
  // BEFORE classification and had emptied the pool, so classifyRoutes returned
  // instantly). The old default was a flat 300m and the comment below says
  // exactly what it was built for — "city scale". No 1,159 km road approaches
  // its destination monotonically; a pass, a river crossing or a single
  // sweeping curve moves you 300m further from Flagstaff than your closest
  // approach so far, and the candidate dies.
  //
  // A backtrack worth rejecting is one you would NOTICE: going past the
  // destination and coming back. That is a share of the trip, not a fixed
  // number of metres. Floored at the original 300m — so city trips keep the
  // exact behaviour the Archville → Hackley fix needed.
  //
  // 2% → 5% (Morgan, Aug 9, current location → Nice). The share was measured,
  // not argued. With the per-victim confession turned on, all SIX dropped
  // candidates reported:
  //     mapbox-direct/444m        strayed 8.9km   limit 8.7km
  //     mapbox-pleasant/952m      strayed 8.7km   limit 8.7km
  //     valhalla-adventure/907m   strayed 8.7km   limit 8.7km
  //     valhalla-adventure/1004m  strayed 8.9km   limit 8.7km
  //     valhalla-balanced/434m    strayed 8.9km   limit 8.7km
  //     valhalla-balanced/496m    strayed 8.9km   limit 8.7km
  // Six routes from three independent engines, every one within 200m of the
  // limit. Routes that genuinely double back do not cluster like that; a
  // threshold drawn through the middle of a corridor's ordinary geometry does.
  // Umbria → Nice crosses the Apennines and meets the Ligurian coast, which
  // approaches Nice and then bends away again — 8.8 km of honest driving.
  //
  // The cost of getting this wrong is not one lost candidate. It killed BOTH
  // calm routes (hwy7% and hwy9%) and all three adventure routes, leaving the
  // Scenic slot to pick from motorway only — which is why a 93%-highway line
  // was showing as "Scenic route / Mostly on main roads".
  //
  // 5% of 435 km = 21.8 km here: still far short of passing Nice and coming
  // back, which is what this filter exists to catch. The 300m floor governs
  // anything under ~6 km, so city behaviour is untouched by this change.
  const straightLineM = (() => {
    const a = coords[0];
    const meanLat = (a[1] + destination[1]) / 2;
    return Math.hypot(
      (a[0] - destination[0]) * 111320 * Math.cos(meanLat * Math.PI / 180),
      (a[1] - destination[1]) * 110540,
    );
  })();
  const threshold = backtrackThresholdM ?? Math.max(300, straightLineM * 0.05);
  if (report) {
    report.thresholdM = threshold;
    report.straightLineM = straightLineM;
    report.excursionM = 0;
  }
  let minDistSoFar = Number.POSITIVE_INFINITY;
  for (const p of coords) {
    // Approximate equirectangular distance — fast, accurate enough for
    // 300m threshold checks at city scale.
    const meanLat = (p[1] + destination[1]) / 2;
    const dx = (p[0] - destination[0]) * 111320 * Math.cos(meanLat * Math.PI / 180);
    const dy = (p[1] - destination[1]) * 110540;
    const d = Math.hypot(dx, dy);
    if (d < minDistSoFar) {
      minDistSoFar = d;
    } else if (d - minDistSoFar > threshold) {
      if (report) report.excursionM = d - minDistSoFar;
      return true;
    } else if (report && d - minDistSoFar > report.excursionM) {
      // Track the worst NON-fatal excursion too, so a route that survives by a
      // hair is visible before it starts dying on the next corridor.
      report.excursionM = d - minDistSoFar;
    }
  }
  return false;
}

/** THE PLEASANT DOOR (extracted from classifyThreeCards, behavior-identical).
 *
 *  Five fallbacks, two of them positional — and every one of them, positional
 *  included, is bounded by the same 1.6×-Fastest budget the eligibility pass
 *  respected. The Genova run is why: eligibility correctly found nothing
 *  (323m against a 176m cap), collapsed the slot onto Direct — and the old
 *  unbounded cards[0] door put the 323-minute route on the card anyway,
 *  logging of itself "⚠ POSITIONAL — no test". A door BENEATH a cap must not
 *  reverse the cap's verdict (rule 13's shape: a later pass overriding a
 *  correct earlier decision).
 *
 *  Deliberately looser (1.6×) than pleasantBudgetSeconds — a door TIGHTER
 *  than eligibility would refuse cards the engine correctly allowed, which
 *  would be a new policy smuggled in as a bug fix. Full reasoning at the
 *  call site (2862223f).
 *
 *  Exported for the regression test; keep it pure. */
export function pleasantDoorPick(
  distinct: ClassifiedRoute[],
  cards: ClassifiedRoute[],
  directDurationSec: number,
  fallback: () => ClassifiedRoute,
): { pick: ClassifiedRoute; door: string; capSec: number } {
  const capSec = directDurationSec * 1.6;
  const withinBudget = (c: { route: RawRoute }) => c.route.duration <= capSec;
  const distinctInBudget = distinct.filter(withinBudget);
  const cardsInBudget = cards.filter(withinBudget);
  const door = (() => {
    if (distinctInBudget.find((c) => c.classification === "pleasant")) return "distinct/classified-pleasant";
    if (distinctInBudget[0]) return "distinct[0] positional (within budget)";
    if (cardsInBudget.find((c) => c.classification === "pleasant")) return "cards/classified-pleasant";
    if (cardsInBudget[0]) return "cards[0] positional (within budget)";
    return "fallbackCard (engine fastest) — nothing fit the budget";
  })();
  const pick =
    distinctInBudget.find((c) => c.classification === "pleasant") ??
    distinctInBudget[0] ??
    cardsInBudget.find((c) => c.classification === "pleasant") ??
    cardsInBudget[0] ??
    fallback();
  return { pick, door, capSec };
}

/**
 * THE COHERENCE GATE, DEFINED ONCE (Morgan, Aug 27 2026).
 *
 * This block used to live inside `classifyRoutes`, which meant its verdict
 * died with that stack frame. `classifyThreeCards` then filled the Pleasant
 * slot from the RAW `scored` pool it was handed — so a candidate the gate had
 * just rejected stayed reachable by every swap, promotion and fallback path.
 * Briarcliff Manor -> Tarrytown, from the production console:
 *
 *   [pool] coherence rejects by gate — tortuosity:1 selfOverlap:2
 *   [pleasant-swap] better: mapbox-direct (rank 7.5) -> scenic-road (rank 22.1)
 *   [nav-trace] pleasant id=6d938fac src=scenic-road dur=992s dist=11515m
 *
 * selfOverlap rejected that route; four lines later it was on the card.
 *
 * Hoisting the gate changes nothing about WHAT it decides — the predicate and
 * every threshold below are the same text, moved. What changes is that both
 * callers can now ask the SAME function, so there is exactly one definition of
 * "coherent" in the codebase and no way for the two to drift.
 *
 * Returns the predicate plus the values `classifyRoutes` needs afterwards for
 * its attrition measurements and its per-gate blame attribution, so those keep
 * reading the same constants the verdict was made with.
 */
export function makeCoherenceGate(opts?: {
  mode?: "driving" | "walking" | "biking";
  osmEnv?: OsmEnvironment;
  isLoop?: boolean;
}) {
  const tortuosityOfEarly = (coords: LngLat[]): number => {
    if (coords.length < 2) return 1;
    const polyLen = totalDistance(coords);
    const directLen = haversine(coords[0], coords[coords.length - 1]);
    if (directLen <= 0) return 1;
    return polyLen / directLen;
  };
  // Real-drive rule (#93 → #121): "A route should never overlap itself."
  // Tightened to 1.5% after a real Tarrytown trip showed a clear loop
  // around 3%. Tight enough to catch borderline loops; still loose
  // enough to allow legitimate cloverleafs / roundabouts (~2%).
  // Tortuosity stays at 2.4 — dead-end / spiral guard. Reservoir
  // backroads run ~1.5-1.7, downtown spirals 2.0+.
  const TORTUOSITY_GLOBAL_CAP = 2.4;
  /** How much road a route may drive twice, in opposite directions, before we
   *  refuse it. An eighth of a mile — Morgan's call, and the stricter one: a
   *  driver notices being sent the wrong way well before a quarter mile is up.
   *  Still comfortably above a U-turn at a junction or a crossover on a divided
   *  road, which is what the floor has to clear.
   *
   *  Written in feet because that is the unit this app's drivers think in; the
   *  geometry underneath is metric, so it is converted once, here.
   *
   *  Returned by the gate, like the other two caps — NOT read as a free
   *  variable. Both of these were written at column 0 inside this function, so
   *  they read as file-level constants and were not: classifyThreeCards and
   *  classifyRoutes referenced them and got `ReferenceError: RETRACE_CAP_M is
   *  not defined` at runtime, in 70 tests. The indentation is load-bearing. */
  const RETRACE_CAP_FT = 660; // an eighth of a mile
  const RETRACE_CAP_M = RETRACE_CAP_FT * 0.3048;

  const SELFOVERLAP_GLOBAL_CAP = 0.015;
  // (#127) Dead-end ways for the corridor, pulled once. The filter
  // below passes them to hasDeadEndDetour for every candidate. Empty
  // array (i.e. no opts.osmEnv) means the dead-end filter is a no-op
  // for callers that didn't enrich — backwards compatible.
  const deadEndLines = (opts?.osmEnv?.lines ?? []).filter(
    (l) => l.kind === "dead_end",
  );
  // Routes built through ordered waypoints by construction — the discovered
  // corridors (scenic-corridor, calm-corridor) and Mapbox's own calmer line
  // (mapbox-pleasant) — are coherent by design and run SPECIFIC local roads on
  // purpose. The coherence gate below (especially the OSM dead-end check, which
  // is the ONLY check that's a no-op in the cheap/refresh passes and then fires
  // in the enriched pass once osmEnv loads) false-positives on exactly those
  // local roads, which is what made the calm Albany Post Road Pleasant route
  // "appear then revert a second later". Exempt them — like scenic-corridor.
  const isConstructedIntentRoute = (s: ScoredRoute) => {
    const src = getRouteSource(s.route);
    // coastal-pch added Aug 2 (Morgan). The definition above — "routes built
    // through ordered waypoints by construction" — described the coastal
    // thread exactly, and it wasn't on the list. The fate log proved the
    // consequence: POOL present ×1 at 351m, SLOT inPool=0 — alive in the raw
    // pool, then removed by this chain's unconditional hasNearReturn (line
    // ~3970, a DIFFERENT gate from the one patched earlier) because a dozen
    // commanded shoreline touches read as an accidental loop. Sixth and, per
    // the diag, final mechanism between Holland→Traverse City and the lake.
    return src === "scenic-corridor" || src === "calm-corridor" || src === "coastal-pch";
  };
  // mapbox-pleasant is Mapbox's own calmer line — coherent enough to pass every
  // GEOMETRY check (that's why it shows in the cheap pass), but it runs local
  // roads that the OSM dead-end check false-positives on once osmEnv loads in
  // the enriched pass. Exempt it from THAT check only, not the geometry gate.
  const exemptFromDeadEnd = (s: ScoredRoute) =>
    isConstructedIntentRoute(s) || getRouteSource(s.route) === "mapbox-pleasant";
  // (Morgan: the Paris walk that goes "up and down a dead-end") On FOOT or BIKE
  // an out-and-back spur — head up a short dead-end street, U-turn, come back —
  // is never acceptable, even on a discovered scenic-corridor route. The
  // constructed-intent exemption below lets driving's calm/scenic corridors skip
  // the U-turn / dead-end checks (their local roads false-positive), but a walk
  // has no reason to ever reverse on itself. So for pedestrian modes we apply
  // those two spur detectors to EVERY route, before the exemption. (hasNearReturn
  // already applies to all; it misses SHORT spurs that don't travel the 200m it
  // needs — hasUTurn catches those by parallel opposite-heading segments.)
  const isPedestrian = opts?.mode === "walking" || opts?.mode === "biking";
  // A requested Pleasant Loop stands down the three anti-loop geometry gates
  // below. See `opts.isLoop` — this flag comes from explicit user intent only.
  const loopRequested = opts?.isLoop === true;
  // NOTE (July 31): these global gates were ALREADY in the 10 July build that
  // shipped to the App Store and that Morgan remembers as working well. I
  // briefly exempted constructed corridors from them on the assumption they
  // were a later addition — they weren't, and the change was reverted. Leave
  // them alone; the real slotting regression was in the Scenic highway gate,
  // which is where the fix belongs.
  const passes = (s: ScoredRoute): boolean => (
    // (Morgan: "the are loops") A NEAR-RETURN loop — the route boxes around a
    // block and comes back near itself — is never acceptable, not even on a
    // discovered "scenic" detour. So this check applies to EVERY route, BEFORE
    // the constructed-intent exemption. A genuine long-way (coastline, parkway)
    // makes one-way progress and won't trip it; a walk that loops around a block
    // will. Constructed-intent routes still skip the OTHER geometry checks (which
    // false-positive on the specific local roads they run on purpose).
    // COASTAL THREADS ARE COMMANDED GEOMETRY (Morgan, Aug 2 — the diag line
    // showed coastal-pch alive in the pool at 351m yet never topping a ranking
    // it's built to win outright; these gates were the only layer in between).
    // A route threaded through a dozen shoreline vias re-passes within 50m of
    // itself at every town it was ORDERED to touch — hasNearReturn reads the
    // hug as an accidental loop, exactly the false positive a requested Loop
    // hits, so it gets the same standdown. The 2.5km retrace ceiling in
    // generateRoutes still catches a genuinely broken coastal shape.
    // CURATED ROADS TOO, NOT JUST COASTAL (Morgan, Aug 10). The standdown above
    // was granted to coastal-pch and never extended to scenic-road, so a
    // hand-picked road got a gate a hand-picked shoreline was excused from.
    //
    // Measured on San Jose → Flagstaff, seven times in a single run:
    //     [scenic-road] injection "Historic Route 66: Topock / Oatman / Kingman…"
    //     [pool] coherence rejects by gate — nearReturn:1
    // Route 66 doglegs south through Oatman and back — that is where the road
    // GOES, and rule 16 names this exact drive: "a human chose it, and the
    // filter kept deleting all three of its threads". Provenance first, shape
    // second. Same exemption `isCuratedSrc` already grants in the backtrack
    // filter; these two lists should have matched all along.
    //
    // The 2.5km retrace ceiling in generateRoutes still catches a genuinely
    // broken curated shape, so this isn't a free pass.
    (loopRequested ||
      getRouteSource(s.route) === "coastal-pch" ||
      getRouteSource(s.route) === "scenic-road" ||
      !hasNearReturn(s.route.coordinates)) &&
    // GROSS-LOOP guard applied to EVERY route, INCLUDING constructed scenic
    // detours (Morgan: a Scenic drive "looped me back to the same place" on a
    // ~3-mile local trip). `hasNearReturn` only catches a loop that comes within
    // 50m of itself; a WIDE out-and-back loop doesn't, and the constructed-intent
    // exemption below skipped the backtrack gate entirely — so the loop survived.
    // The cap is ADAPTIVE: on a short local trip (< 8 km) even a modest backtrack
    // is an obvious loop, so we hold it to 0.28; a long scenic drive earns 0.40
    // so a genuine coastline/parkway detour still passes.
    (loopRequested ||
      makesCoherentProgress(
        s.route.coordinates,
        s.route.coordinates[0],
        s.route.coordinates[s.route.coordinates.length - 1],
        {
          maxBacktrackFraction:
            haversine(
              s.route.coordinates[0],
              s.route.coordinates[s.route.coordinates.length - 1],
            ) < 8000
              ? 0.28
              : 0.40,
        },
      )) &&
    // TIGHT-KNOT guard — applied to EVERY route at EVERY trip length (Morgan:
    // "still has weird things like this — there should be no loops or spurs").
    // A lasso at ONE END of an otherwise-long trip was escaping the old <8km
    // gate, so we now run it always. It only trips on a genuine loop — two points
    // <80m apart but ≥500m apart ALONG the path — so a tight mountain hairpin
    // (short path between the close points) is NOT caught; only a real out-and-
    // loop-and-back is. That's exactly the shape we never want to show.
    (loopRequested || getRouteSource(s.route) === "coastal-pch" || !hasTightKnot(s.route.coordinates)) &&
    // RETRACE guard (Morgan, Aug 30, from a real search): "this route had me go
    // the opposite direction from the place i intended down a road and back,
    // retracing the road the whole way until finally going the right direction
    // on the same road. it's just extra needless driving."
    //
    // The knot gate above cannot catch this. It was deliberately blunted so it
    // would stop rejecting Route 66's Oatman hairpins, and a retrace of a few
    // hundred metres now passes under it.
    //
    // `retracedMeters` asks a different and unambiguous question: how much road
    // does this route drive TWICE, in opposite directions, over the same
    // pavement. A hairpin scores tens of metres — its legs are separated by the
    // hillside. Driving up a road and back scores the length of the road. So
    // this can be sharp where the knot gate had to be blunt.
    //
    // RETRACE_CAP_M is the point where a driver notices. A U-turn at a junction
    // or a divided-road crossover is well under it; the quarter mile Morgan was
    // sent the wrong way is well over.
    (loopRequested || retracedMeters(s.route.coordinates) < RETRACE_CAP_M) &&
    // Pedestrian out-and-back / dead-end spur guard — applied to ALL walk/bike
    // routes, scenic-corridor included.
    (!isPedestrian ||
      (!hasUTurn(s.route.coordinates) &&
        !hasDeadEndDetour(s.route.coordinates, deadEndLines))) &&
    // A requested loop stands down this whole second block too. It repeats
    // hasNearReturn / makesCoherentProgress and adds selfOverlapFraction and
    // hasNeighborhoodDetour — every one of which a circle trips by design.
    (loopRequested ||
    isConstructedIntentRoute(s) ||
    (tortuosityOfEarly(s.route.coordinates) < TORTUOSITY_GLOBAL_CAP &&
    selfOverlapFraction(s.route.coordinates) < SELFOVERLAP_GLOBAL_CAP &&
    // Monotonic-progress gate (route-philosophy pass). Catches the LARGE-scale
    // spur the tight-loop filters above miss: a candidate that heads far away
    // from the destination and comes back (out-and-back to a distant point).
    // Length is allowed; backtracking is not. 0.30 = went 30% of the trip's
    // straight-line distance backward at some point → reject.
    makesCoherentProgress(
      s.route.coordinates,
      s.route.coordinates[0],
      s.route.coordinates[s.route.coordinates.length - 1],
      { maxBacktrackFraction: 0.30 },
    ) &&
    // No U-turns (#94). Direction-aware: a cloverleaf has close points
    // in the SAME direction (you enter east, exit east); a U-turn has
    // close points in OPPOSITE directions. This drops U-turns without
    // flagging legitimate interchanges.
    !hasUTurn(s.route.coordinates) &&
    // No near-returns (#126). Absolute-distance loop detector — catches
    // tight in-town loops (P-shape walks-around-a-block) that the
    // percentage and parallel-segment filters above miss. If the
    // polyline returns to within 50m of itself after traveling ≥ 200m,
    // it's a loop.
    !hasNearReturn(s.route.coordinates) &&
    // No neighborhood detours (#136). Sliding-window tortuosity check
    // — catches "drive around a few blocks for no reason" shapes that
    // the percentage and parallel-segment filters above miss (the
    // Tarrytown downtown rectangle that kept slipping through).
    !hasNeighborhoodDetour(s.route.coordinates) &&
    // No dead-end detours (#127). OSM-label based: drop any candidate
    // whose internal segments touch a noexit=yes / service=driveway
    // way, unless that way contains the trip's start or end (the user
    // can legitimately route TO or FROM an address on a dead-end).
    (exemptFromDeadEnd(s) || !hasDeadEndDetour(s.route.coordinates, deadEndLines))))
  );
  return {
    passes,
    deadEndLines,
    tortuosityOfEarly,
    TORTUOSITY_GLOBAL_CAP,
    SELFOVERLAP_GLOBAL_CAP,
    RETRACE_CAP_FT,
    RETRACE_CAP_M,
  };
}

export function classifyThreeCards(
  scored: ScoredRoute[],
  /** When true, Pleasant + Scenic apply the existing avoidHighways filter
   *  (drop candidates with highwayPct ≥ 25%). Direct never applies it —
   *  the user sees the engine's literal fastest even if it uses highways,
   *  because Direct's promise is "get me there fastest, no other rules." */
  avoidHighways: boolean = false,
  opts?: {
    excludeCoords?: LngLat[][];
    skipRank?: number;
    mode?: "driving" | "walking" | "biking";
    pleasantBudgetSec?: number;
    /** Absolute Pleasant time ceiling in seconds, computed ONCE from the
     *  initial fastest and held fixed across every pass. Prevents a later,
     *  slightly-faster Direct (e.g. Valhalla folding in) from shrinking the
     *  ceiling and evicting a Pleasant route that already qualified — the
     *  "appears then reverts" bug. When set, it overrides safeFastestDur +
     *  budget so the cap never moves with the pool. */
    pleasantTimeCapSec?: number;
    googleCoords?: LngLat[];
    osmEnv?: OsmEnvironment;
    /** Pass true to suppress the "pleasant" pick in the Pleasant slot
     *  when scoring confidence is low (the same `suppressPleasant`
     *  semantics classifyRoutes already uses). */
    suppressPleasant?: boolean;
    /** PLEASANT LOOP — explicit user intent, never inferred. Stands down the
     *  four anti-loop gates a requested circle would otherwise trip:
     *  hasBacktrackTowardDestination here, and hasNearReturn /
     *  makesCoherentProgress / hasTightKnot inside classifyRoutes. */
    isLoop?: boolean;
    /** (#155) True once background enrichment has finished and the
     *  scenic anchor list (parks/water/byways) is real. When false
     *  AND the Scenic slot would collapse to Pleasant via overlap,
     *  the card is marked `scenicSearching: true` so the UI can
     *  render "Searching scenic..." copy instead of generic
     *  "Same as the Pleasant route" copy. */
    scenicAnchorsLoaded?: boolean;
  },
): ClassifiedRoute[] {
  if (scored.length === 0) return [];

  // PEDESTRIAN out-and-back / dead-end spur guard (Morgan: the Paris walk that
  // goes up and down a dead-end). On foot or bike a route that reverses on
  // itself is never acceptable — even a discovered scenic-corridor one. Drop
  // these from the pool UP FRONT so no downstream path can resurrect them: not
  // classifyRoutes, not the full-pool distinctness fallback (pickDistinctFromPool
  // reads raw `scored`), and not the corridor-pick scenic override. Keep at least
  // the fastest route so the card list is never blank.
  const isPedestrianMode = opts?.mode === "walking" || opts?.mode === "biking";
  {
    const deadEndLines = (opts?.osmEnv?.lines ?? []).filter((l) => l.kind === "dead_end");
    // A route we BUILT by threading waypoints (scenic-corridor / calm-corridor)
    // can contain an out-and-back jog (an injection artifact). We'd rather not
    // show it — but only when a CLEANER alternative exists to take its place.
    const isSpurry = (s: ScoredRoute): boolean => {
      const src = getRouteSource(s.route);
      const constructed = src === "scenic-corridor" || src === "calm-corridor";
      if (!isPedestrianMode && !constructed) return false;
      return (
        hasUTurn(s.route.coordinates) ||
        hasDeadEndDetour(s.route.coordinates, deadEndLines)
      );
    };
    const fastest = scored.reduce((a, b) => (a.route.duration <= b.route.duration ? a : b));
    const clean = scored.filter((s) => !isSpurry(s));
    // Drop spurs ONLY when a clean route OTHER THAN Direct survives. Otherwise a
    // longer walk whose only alternatives are slightly joggy would collapse to
    // just the Fast card ("nothing comes up on longer walks"). A joggy option
    // beats no option — and an unavoidable shared jog (Eiffel / Champ de Mars)
    // is kept for the same reason.
    if (clean.some((s) => s.route !== fastest.route)) scored = clean;
  }

  // NO RESURRECTION AFTER THE VERDICT (Morgan, Aug 27 2026 — Briarcliff Manor
  // -> Tarrytown). The comment on the block above already named this hazard in
  // June — "drop these from the pool UP FRONT so no downstream path can
  // resurrect them: not classifyRoutes, not the full-pool distinctness fallback
  // (pickDistinctFromPool reads raw `scored`)" — and closed it for walks only.
  // Driving kept the hole, and the swap paths that exploit it were written
  // three weeks later. Production, verbatim:
  //
  //   [pool] coherence rejects by gate — tortuosity:1 selfOverlap:2
  //   [pleasant-swap] better: mapbox-direct (rank 7.5) -> scenic-road (rank 22.1)
  //
  // Six paths in this function read raw `scored` and can seat what they find:
  // `better`, `waterBetter`, pickDistinctFromPool (door + both rescues), the
  // corridor `sibling`, the supreme rule's `q`, and the twin-collapse
  // `replacement`. Filtering the pool once here closes all six, because they
  // all read this same array.
  //
  // The gate asked is THE gate — makeCoherenceGate is the same function
  // classifyRoutes filters with, so a route is judged here exactly as it is
  // judged there. Every pre-verdict exemption (constructed-intent, a requested
  // Loop, the curated coastal-pch / scenic-road standdowns, the pedestrian
  // rules) is inside that predicate and is preserved untouched.
  //
  // THE SAFETY VALVE IS THE SAME ONE THE BLOCK ABOVE USES: quarantine only when
  // a clean route OTHER THAN the fastest survives. When every non-Direct
  // candidate fails, nothing is removed, classifyRoutes sees the pool it sees
  // today, and its documented keep-one fallback (#115) behaves exactly as
  // before. A joggy option still beats no option.
  //
  // TWO POOLS, AND THE REASON THERE HAVE TO BE TWO (Morgan, Aug 27, after the
  // 48-corridor A/B). The first version of this block reassigned `scored`, and
  // that was wrong for one specific consumer: the FAST slot.
  //
  // `engineFastest` below is `scored.reduce(min duration)` — the engine's
  // literal fastest route — and `fallbackCard` is built from it. That is
  // deliberate and long-standing: "Direct never applies the avoidHighways
  // filter — the user sees the engine's literal fastest even if it uses
  // highways, because Direct's promise is 'get me there fastest, no other
  // rules.'" Filtering the array it reads breaks that promise.
  //
  // The A/B measured the damage on the captured 48-corridor pool: the Fast
  // card VANISHED on 9 corridors and was SWAPPED for a slower, more-highway
  // route on 3 more (73% → 85% highway on one), and total cards across the
  // affected corridors fell 30 → 19. Four coherent Scenic routes lost their
  // cards purely as knock-on, because the card set collapsed under them.
  //
  // So the quarantine no longer touches `scored`. `scored` stays RAW and Fast
  // keeps reading it. `coherentScored` is the quarantined pool, and ONLY the
  // Pleasant/Scenic paths that were resurrecting hard-gate failures read it:
  // `better`, `waterBetter`, pickDistinctFromPool (the Pleasant door/rescue AND
  // the Scenic full-pool fallback), the corridor `sibling`, the supreme rule's
  // `q`, and the twin-collapse `replacement`.
  //
  // classifyRoutes still receives the RAW pool at every call site, so its own
  // gate and its documented keep-one fallback (#115) behave exactly as they do
  // today. Nothing about ranking, thresholds or exemptions changes here.
  //
  /** The Fast slot's pool. The engine's literal candidate list, NEVER filtered. */
  const rawScored = scored;
  /** The Pleasant/Scenic pool. Equal to rawScored unless the quarantine fires. */
  let coherentScored = rawScored;
  {
    const { passes: passesCoherence, RETRACE_CAP_FT, RETRACE_CAP_M } = makeCoherenceGate(opts);
    const fastestOverall = rawScored.reduce((a, b) =>
      a.route.duration <= b.route.duration ? a : b,
    );
    // RETRACE IS NOT NEGOTIABLE (Morgan, Aug 30, after seeing it survive).
    //
    // The stand-down below exists so the card list is never blank. That is the
    // right instinct for most gates and the wrong one for this: a route that
    // drives an eighth of a mile down a road and straight back is not a
    // "pleasant" route with a flaw, it is wrong, and showing it labelled
    // Pleasant is worse than not showing a Pleasant card at all.
    //
    // Safe to make absolute, because the FAST slot reads `rawScored`, which is
    // never filtered. The floor is "you always see the fast route", not "you
    // always see three cards". With enrichment down the pool is tiny, so the
    // stand-down was firing on nearly every search and quietly re-admitting
    // exactly the shape Morgan keeps reporting.
    const retraceFails = (c: ScoredRoute) =>
      retracedMeters(c.route.coordinates) >= RETRACE_CAP_M;
    const retraced = rawScored.filter(retraceFails).length;
    if (retraced > 0) {
      // eslint-disable-next-line no-console
      console.log(
        `[pool] ${retraced} candidate(s) drive >${RETRACE_CAP_FT}ft of road twice ` +
          `in opposite directions — refused outright, not eligible for the ` +
          `coherence stand-down`,
      );
    }

    const coherent = rawScored.filter(passesCoherence);
    const lost = rawScored.length - coherent.length;
    if (lost > 0) {
      if (coherent.some((s) => s.route !== fastestOverall.route)) {
        // eslint-disable-next-line no-console
        console.log(
          `[pool] coherence quarantine: ${rawScored.length} → ${coherent.length} — ` +
            `${lost} candidate(s) failed a hard gate and are now unreachable by ` +
            `every later Pleasant/Scenic swap, promotion and fallback path ` +
            `(Fast still reads the unfiltered pool)`,
        );
        coherentScored = coherent;
      } else {
        // eslint-disable-next-line no-console
        console.log(
          `[pool] coherence quarantine STOOD DOWN — ${lost} candidate(s) fail a hard ` +
            `gate but no clean alternative to Direct survives; keeping them rather ` +
            `than showing a blank card list`,
        );
        // ...but never re-admit a retrace. See above.
        coherentScored = rawScored.filter((c) => !retraceFails(c));
      }
    }
    // Applies even when nothing else failed: a retrace must never reach a
    // Pleasant or Scenic card by any path.
    else if (retraced > 0) {
      coherentScored = rawScored.filter((c) => !retraceFails(c));
    }
  }

  // Run the three categories through the existing classifyRoutes
  // engine, each at its own tolerance preset. Take the FIRST card
  // each returns (the primary), drop any 2nd/alternate cards.
  // This reuses every filter / scoring rule that's already battle-
  // tested in classifyRoutes without rebuilding selection logic.
  const sharedOpts = {
    excludeCoords: opts?.excludeCoords,
    skipRank: opts?.skipRank,
    mode: opts?.mode,
    pleasantBudgetSec: opts?.pleasantBudgetSec,
    pleasantTimeCapSec: opts?.pleasantTimeCapSec,
    googleCoords: opts?.googleCoords,
    osmEnv: opts?.osmEnv,
    isLoop: opts?.isLoop,
  };

  // (#160) Backtrack filter: drop candidates that wander away from the
  // destination by 300m+ from their closest approach. Only applied to
  // the Direct + Pleasant pools — Scenic intentionally wanders (reservoir
  // detour, parkway loop) so the filter would kill legitimate scenic
  // options. Catches the loopy shape that hasNearReturn keeps missing
  // because the parallel streets are too far apart for proximity-based
  // detection. Real-drive trigger: Archville → Hackley showed a loopy
  // 6mi alternative whose Tarrytown downtown zig-zag had parallel streets
  // 200m+ apart; hasNearReturn at 150m never fired but the polyline
  // clearly got close to Hackley, then moved 300m+ farther away mid-trip.
  // A requested LOOP is exempt: its "destination" is its own start, so every
  // circle wanders away from it by definition and this filter would empty the
  // Direct and Pleasant pools outright.
  const destCoord = scored[0]?.route.coordinates.at(-1);
  // CURATED ROADS ARE EXEMPT (rule 16, Morgan Aug 8). The backtrack filter
  // exists for MACHINE-picked shapes — the Tarrytown zigzag whose loop is our
  // fault. Historic Route 66 doglegs south through Topock, Oatman and Kingman
  // because that is where the road GOES; a human chose it, and the filter kept
  // deleting all three of its threads on San Jose → Flagstaff:
  //     [pool] backtrack filter: 14 → 8 (lost ... scenic-road:3 ...)
  // Same exemption scenic-corridor / coastal-pch already hold elsewhere.
  const isCuratedSrc = (s: ScoredRoute) => {
    const src = getRouteSource(s.route);
    return src === "scenic-road" || src === "coastal-pch";
  };
  // Per-route measurements for the confession below. Filled as we filter so
  // the log can say HOW FAR each victim strayed and against WHAT limit — the
  // difference between "this route U-turns" and "we called 20km of Apennine
  // switchbacks a U-turn".
  const backtrackReports = new Map<
    ScoredRoute,
    { excursionM: number; thresholdM: number; straightLineM: number }
  >();
  setAttritionPass("backtrack");
  const noBacktrackPool = destCoord && !opts?.isLoop
    ? rawScored.filter((s) => {
        if (isCuratedSrc(s)) return true;
        const report = { excursionM: 0, thresholdM: 0, straightLineM: 0 };
        const rejected = hasBacktrackTowardDestination(
          s.route.coordinates,
          destCoord,
          undefined,
          report,
        );
        backtrackReports.set(s, report);
        return !rejected;
      })
    : rawScored;
  // CONFESSION (rule 17). This filter runs BEFORE classification, so when it
  // empties the pool classifyRoutes returns instantly and every downstream log
  // — including [pleasant] — simply never happens. That silence is what hid it
  // on San Jose → Flagstaff.
  if (noBacktrackPool.length < scored.length) {
    const lost: Record<string, number> = {};
    const kept = new Set(noBacktrackPool.map((s) => s.route));
    for (const s of scored) {
      if (kept.has(s.route)) continue;
      const k = getRouteSource(s.route) ?? "engine";
      lost[k] = (lost[k] ?? 0) + 1;
    }
        recordStage("backtrack", scored.length, noBacktrackPool.length);
    {
      const kept = new Set(noBacktrackPool.map((x) => x.route));
      for (const x of scored) {
        if (kept.has(x.route)) continue;
        recordRejection({
          stage: "backtrack", rule: "strayed-from-destination",
          candidate: x,
          roads: roadsOfSteps(x.route.steps),
          source: getRouteSource(x.route) ?? "engine",
          durationMin: x.route.duration / 60,
        });
      }
    }
console.log(
      `[pool] backtrack filter: ${scored.length} → ${noBacktrackPool.length}` +
      ` (lost ${Object.entries(lost).map(([k, n]) => `${k}:${n}`).join(" ")})` +
      `${noBacktrackPool.length === 0 ? " ⚠ EMPTIED — Direct+Pleasant slots get nothing" : ""}`,
    );
    // ...and WHY, one line per victim. Naming the victim was never enough:
    // `lost mapbox-pleasant:1` has appeared on every long corridor for days
    // without telling us whether the route really doubles back or whether a
    // 2%-of-straight-line threshold is simply too tight for mountain roads.
    for (const s of scored) {
      if (kept.has(s.route)) continue;
      const r = backtrackReports.get(s);
      if (!r) continue;
      console.log(
        `[backtrack] dropped ${getRouteSource(s.route) ?? "engine"}/` +
          `${Math.round(s.route.duration / 60)}m — strayed ` +
          `${(r.excursionM / 1000).toFixed(1)}km further from the destination ` +
          `than its closest approach (limit ${(r.thresholdM / 1000).toFixed(1)}km ` +
          `= 2% of ${(r.straightLineM / 1000).toFixed(0)}km straight line)`,
      );
    }
  }
  // REVERTED #185 — one-pass rewrite caused app crashes. Restoring
  // the previous classifyRoutes-based slot picking. We'll re-attempt
  // the rewrite more carefully later.
  setAttritionPass("direct");
  const directCards = classifyRoutes(noBacktrackPool, 1.0, false, opts?.suppressPleasant ?? false, sharedOpts);
  // Pleasant pool stays at the tight 1.20× here — a wide pool would let a
  // much-longer route (135m on a 78m trip) claim Pleasant, which is Scenic
  // territory. When the tight pool only yields a main-road pick, the
  // QUALITY RESCUE below widens to the trip's real budget instead.
  setAttritionPass("pleasant");
  const pleasantCardsRaw = classifyRoutes(noBacktrackPool, 1.20, true, opts?.suppressPleasant ?? false, sharedOpts);
  // AN UNMEASURED DIRECT ROUTE CANNOT COMPETE FOR PLEASANT (Morgan, Aug 9).
  //
  // The final diagnostic is the whole causal chain in one line:
  //
  //   [diag] pleasant:google-direct/75m/hwy0/com0/pl55
  //          -:mapbox-pleasant/116m/hwy28/com0/pl40
  //
  // Google scores 55 against the real Pleasant route's 40, because it reports
  // hwy0 — not because it avoids highways, but because nobody measured it. The
  // engine even says so out loud two lines earlier: "Direct reference is
  // unmeasured (google-direct)". Fixing summarizeFeatures stopped the FABRICATED
  // zero, but with Overpass down the OSM fallback is also zero, so the number is
  // still a lie by a different route. The type can't say "unknown" yet.
  //
  // So the fix is upstream of the number: a route with no road-class evidence
  // that is one of the engines' DIRECT lines never enters the Pleasant contest.
  // It can still be Direct, which is what it honestly is. This is narrow on
  // purpose — the July 17 approval ("Google's quiet fastest route can win
  // Pleasant like any other candidate") survives for a Google route we actually
  // measured, and no other source is touched.
  //
  // This edits the selection rather than guarding it afterwards, which is what
  // Morgan asked for: the good candidate is never displaced in the first place,
  // so there is nothing to fall back to.
  // QUARANTINE (Morgan, Aug 9): "keep Google for Direct/Fastest; quarantine
  // google-direct from Pleasant and Scenic."
  //
  // This REPLACES a measured-ness test that sat here for a few hours and did
  // not work. That test asked "does this route carry road-class evidence?" —
  // and on the Briarcliff run it answered YES for google-direct while
  // `isMeasured`, three hundred lines away, answered NO for the same route in
  // the same pass (`[pleasant] Direct reference is unmeasured (google-direct)`
  // is in that log, next to `pleasant/google-direct`). Two predicates for one
  // question, disagreeing — precisely the failure rule 20 was written about,
  // reintroduced by me while fixing rule 20.
  //
  // A source check cannot drift, and the reasoning is cleaner than the
  // heuristic it replaces: FASTEST is a claim about TIME, which Google's
  // response fully supports. PLEASANT and SCENIC are claims about CHARACTER,
  // and Google's carrier ships no road-class data at all — not "sometimes",
  // structurally. A card that asserts a road is calm or beautiful may not be
  // filled by a candidate nobody can look at.
  //
  // This reverses the July 17 approval ("Google's quiet fastest route can win
  // Pleasant like any other candidate"). That approval was made about a
  // candidate we could see was quiet; the real one never carried the numbers,
  // and the test that protected it only passed because its fixture handed it a
  // highwayPct the live route never has. Recorded in CLAUDE.md so it is not
  // re-litigated a fourth time.
  const TASTE_QUARANTINED_SOURCES = new Set(["google-direct"]);
  const canWearTasteLabel = (c: ClassifiedRoute): boolean =>
    !TASTE_QUARANTINED_SOURCES.has(getRouteSource(c.route) ?? "");
  const pleasantCards = pleasantCardsRaw.filter(canWearTasteLabel);
  if (pleasantCards.length !== pleasantCardsRaw.length) {
    console.log(
      `[pleasant] quarantined from the Pleasant contest (no road-class data, cannot be judged) — ${
        pleasantCardsRaw
          .filter((c) => !canWearTasteLabel(c))
          .map((c) => getRouteSource(c.route) ?? "engine")
          .join(" ")
      } (${pleasantCards.length} candidates left)`,
    );
  }
  // (#192) Adventure has NO time limit. Morgan: "adventure doesn't have a
  // time limit." Long backroad routes on trips like Sleepy Hollow → Greenwich
  // can easily double the highway time — those are exactly the routes Adventure
  // is supposed to surface. Cap removed (was 1.50×).
  setAttritionPass("scenic");
  const scenicCardsRaw = classifyRoutes(scored, Number.POSITIVE_INFINITY, true, opts?.suppressPleasant ?? false, sharedOpts);
  // Same quarantine for Scenic. Tonight's pool line shows google-direct sitting
  // in `eligible=[...]` for the Scenic slot too — a route we cannot measure was
  // one pick away from wearing the app's strongest aesthetic claim.
  const scenicCards = scenicCardsRaw.filter(canWearTasteLabel);
  if (scenicCards.length !== scenicCardsRaw.length) {
    console.log(
      `[scenic] quarantined from the Scenic contest (no road-class data, cannot be judged) — ${
        scenicCardsRaw
          .filter((c) => !canWearTasteLabel(c))
          .map((c) => getRouteSource(c.route) ?? "engine")
          .join(" ")
      } (${scenicCards.length} candidates left)`,
    );
  }

  const engineFastest = rawScored.reduce((a, b) =>
    a.route.duration <= b.route.duration ? a : b,
  );
  const fallbackCard = (classification: "fastest" | "pleasant" | "scenic"): ClassifiedRoute => ({
    ...engineFastest,
    classification,
    sameAsFastest: true,
    ...(classification !== "fastest" ? { sameAsClass: "fastest" as const } : {}),
  });

  let direct: ClassifiedRoute =
    directCards.find((c) => c.classification === "fastest") ??
    directCards[0] ??
    fallbackCard("fastest");

  const SLOT_DISTINCT_OVERLAP_THRESHOLD = 0.92;
  /**
   * ONE NOTION OF "SAME ROAD" FOR SLOT SELECTION (Morgan, Aug 23).
   *
   * Slot distinctness judged routes by bucket-jaccard alone. That measures
   * SAMPLING agreement, not geometry, so it falls apart across providers:
   * Pleasantville's google-direct/10m and mapbox-direct/11m sit an average of
   * 5 m apart across 60 samples — plainly one street — and scored jaccard
   * 0.561, comfortably "distinct". The engine then offered them as two cards
   * and the display, using routesAreSameRoad, collapsed them again. Two
   * predicates, one question, opposite answers.
   *
   * The 0.92 jaccard threshold is unchanged and every verdict it already
   * returns is unchanged. This only ADDS the geometric test, so the rule can
   * collapse more, never split more.
   */
  const sameRoadForSlot = (
    a: { coordinates: LngLat[]; distance: number },
    b: { coordinates: LngLat[]; distance: number },
    cachedJaccard?: number,
  ): boolean => {
    const j = cachedJaccard ?? jaccard(geometryBuckets(a.coordinates), geometryBuckets(b.coordinates));
    if (j >= SLOT_DISTINCT_OVERLAP_THRESHOLD) return true;
    return routesAreSameRoad(a, b);
  };

  // Direct tiebreaker (was #183, reworked per Morgan): when routes tie at
  // fastest, the NICER one belongs on the PLEASANT card — "it's the same time
  // or faster to go by parks and water." The old rule promoted the most-
  // pleasant tied route INTO Direct, which stole the green route from the
  // Pleasant slot and left Pleasant holding a main road (the same-time repro).
  // Now: Direct keeps the plainest tied route so the green one can win
  // Pleasant — but ONLY when the two are genuinely different lines on the
  // map. If they're the same geometry, keep the nicer one as Direct (old
  // behavior) since a separate Pleasant card can't exist anyway.
  // FAST MEANS FASTEST (Morgan, Aug 23): "the actual minimum-duration eligible
  // route must always be represented by the Fast card."
  //
  // This block's INTENT was already right — keep the plainest tied route as
  // Direct so the green one can win Pleasant. Two things stopped it delivering
  // that, and both showed up in the fixtures behind the Ossining work:
  //
  //  1. The tie window was +/-30s around whichever route happened to be Direct
  //     already, so a 9.5-minute route could be seated as Fast while a
  //     9-minute route sat in the pool — "Fast 9.5m beside Pleasant 9m", which
  //     is simply untrue. The tie set is now the routes AT the pool minimum.
  //     TIE_TOLERANCE_SEC is kept only to recognise clock-noise ties in that
  //     minimum, never to let a slower route hold the slot.
  //
  //  2. "Plainest" was measured with rankScore — the pleasantness model. On
  //     Neperan the quiet back road scores 48 and the 80%-highway route scores
  //     55, so the rule handed Direct to the quiet road and Pleasant to the
  //     highway: the exact inversion the supreme rule exists to correct. Main-
  //     road share is the same ground-truth signal the supreme rule and
  //     pickDistinctFromPool already tiebreak on ("highway % comes straight
  //     from Mapbox and is always reliable"), so it decides here too. rankScore
  //     still breaks ties when the road mix is identical.
  //
  // The old +/-30s window is GONE from the selection, not narrowed. Morgan's
  // invariant is strict — "if an eligible candidate has a shorter duration
  // than the route labelled fastest, the final card set is invalid" — and a
  // 30-second slack is precisely what let a 570s route hold Fast while a 540s
  // one existed. Durations arrive as whole seconds from the engines, so exact
  // equality is the right test for a genuine tie.
  const minPoolDuration = engineFastest.route.duration;
  const tiedAtFastest = rawScored.filter((s) => s.route.duration === minPoolDuration);
  // `> 0`, not `> 1`. With a single route at the minimum the old guard skipped
  // entirely and left Direct wherever directCards had put it — which is how the
  // 9.5-minute route kept the Fast card while a 9-minute route sat in the pool.
  // Seating the minimum is the point of the block, tie or no tie.
  if (tiedAtFastest.length > 0) {
    const mainShareOf = (s: { features: FeatureSummary }) =>
      (s.features.highwayPct ?? 0) + (s.features.majorRoadPct ?? 0) * 0.5;
    const mostPleasantOfTied = tiedAtFastest.reduce((a, b) =>
      mainShareOf(b) < mainShareOf(a) ||
      (mainShareOf(b) === mainShareOf(a) && rankScore(b) > rankScore(a)) ? b : a,
    );
    const leastPleasantOfTied = tiedAtFastest.reduce((a, b) =>
      mainShareOf(b) > mainShareOf(a) ||
      (mainShareOf(b) === mainShareOf(a) && rankScore(b) < rankScore(a)) ? b : a,
    );
    const tiedAreDistinct =
      jaccard(
        geometryBuckets(mostPleasantOfTied.route.coordinates),
        geometryBuckets(leastPleasantOfTied.route.coordinates),
      ) < SLOT_DISTINCT_OVERLAP_THRESHOLD;
    direct = {
      ...(tiedAreDistinct ? leastPleasantOfTied : mostPleasantOfTied),
      classification: "fastest",
    };
  }

  // Distinctness filter (was #181): Pleasant must be different from Direct.
  const directBucketsForDedup = geometryBuckets(direct.route.coordinates);
  const pleasantCandidatesDistinct = pleasantCards.filter((c) => {
    const cb = geometryBuckets(c.route.coordinates);
    return jaccard(cb, directBucketsForDedup) < SLOT_DISTINCT_OVERLAP_THRESHOLD;
  });

  // (#189) Full-pool fallback for distinctness. Real-drive trigger:
  // Hackley trip. Pool had 6 routes (3 valhalla-adventure, 2 mapbox-direct,
  // 1 valhalla-balanced) but classifyRoutes(1.20) returned only the top-2
  // by pleasantness — both valhalla-adventure variants similar to Direct.
  // The distinctness filter excluded both, then fell back to pleasantCards[0]
  // which was just Direct again. The mapbox-direct and valhalla-balanced
  // sat unused. Now: if the pre-classified pool can't yield a distinct
  // pick, search the full `scored` pool for the best-pleasant route that's
  // within the Pleasant tolerance AND geometrically distinct from Direct.
  const directDurForPool = direct.route.duration;
  const pickDistinctFromPool = (
    toleranceMult: number,
    excludeBucketsList: ReturnType<typeof geometryBuckets>[],
    /** The routes those exclusion buckets came from, positionally matched.
     *  Optional so existing callers keep working; supplying them lets the
     *  geometric same-road test run alongside bucket-jaccard. */
    excludeRoutes: ({ coordinates: LngLat[]; distance: number } | undefined)[] = [],
    /**
     * How to choose among the survivors. Defaults to the pleasantness pick
     * below, which is what every existing caller wants. The Fast re-seat
     * (Morgan, Aug 23) passes a shortest-duration picker instead: it needs the
     * SAME eligibility — the time budget, rule 20's measurement bar, and the
     * geometric distinctness exclusions — but Fast means fastest, so ranking
     * its candidates by pleasantness would be the wrong question. This
     * parameterises the final choice only; no new ranking system, and no
     * caller that omits it changes behaviour.
     */
    prefer?: (a: ScoredRoute, b: ScoredRoute) => ScoredRoute,
  ): ScoredRoute | null => {
    const budget = directDurForPool * toleranceMult;
    // THIS FUNCTION FILLS A *NAMED* SLOT, SO THE MEASUREMENT RULE APPLIES HERE
    // TOO (Morgan, Aug 9 — the fourth site reading the same missing field).
    //
    // The pool filter and the Direct yardstick were both fixed and the card was
    // STILL google-direct. The [diag] line said where it actually comes from:
    //
    //   [pleasant] pool=13 → top: mapbox-direct | mapbox-pleasant | valhalla-balanced
    //   [diag] pleasant:google-direct/77m/hwy0/com0/pl55
    //
    // Google is absent from the ranked pool — that fix worked — and still wins
    // the card, because the quality rescue above refills the slot straight from
    // `scored` through here. Google passes every test it applies: inside the
    // time cap, calmEnough because hwy0, and "geometrically distinct" from
    // Direct only because its coordinate sampling differs from Mapbox's on the
    // same road. A route we never measured cannot be named Pleasant or Scenic
    // (Morgan, Aug 1) — including when it arrives through the rescue door.
    const isMeasured = (s: ScoredRoute): boolean => {
      const r = s.route as {
        roadClassMeters?: Record<string, number>;
        steps?: Array<{ classes?: string[] }>;
      };
      return Object.keys(r.roadClassMeters ?? {}).length > 0 ||
        (r.steps ?? []).some((st) => (st.classes ?? []).length > 0);
    };
    const candidates = coherentScored.filter((s) => {
      if (s.route.duration > budget) return false;
      if (!isMeasured(s)) return false;
      const cb = geometryBuckets(s.route.coordinates);
      return excludeBucketsList.every((excl, i) => {
        const j = jaccard(cb, excl);
        const other = excludeRoutes[i];
        // Same widening as the slot checks: bucket-jaccard alone cannot see a
        // cross-provider twin. When the caller supplied the route behind the
        // exclusion buckets, the geometric test gets a say too.
        return other ? !sameRoadForSlot(s.route, other, j) : j < SLOT_DISTINCT_OVERLAP_THRESHOLD;
      });
    });
    if (candidates.length === 0) return null;
    if (prefer) return candidates.reduce(prefer);
    // Rank by pleasant score, but break ties with the ground-truth calm signal:
    // lower highway %. The enriched pleasant score is flat (~23) whenever the OSM
    // corridor fetch times out on long trips, so without this the fallback picks
    // an arbitrary tied route — often a near-Direct highway one (the live
    // Briarcliff→Rhinebeck bug: it picked the 78m/97%-highway route over the
    // genuinely-calmer 84m/70% route). Highway % comes straight from Mapbox and
    // is always reliable, so it's the right tiebreaker.
    return candidates.reduce((a, b) => {
      // PRE-FLOOR, NOT DISPLAY (Morgan, Aug 23). This is an ORDERING
      // comparator, so it belongs on the same value the selector ranks on.
      // It read `scores.pleasant`, which carries componentFloor — and with
      // candidates flattened onto the same floored number, `pb !== pa` was
      // false and the choice fell straight through to the highway tiebreak.
      //
      // Proven on Ossining: the door correctly picked mapbox-direct/14m, the
      // "Pleasant is same as Direct" rescue fired, and this reduce then handed
      // the slot to a route whose pre-floor score was -55.9 because every
      // candidate displayed 55. It was the last place componentFloor could
      // still decide which route won.
      //
      // Missed in the first audit sweep: that pass matched the pattern
      // `(b.scores.pleasant ?? 0) - (a.scores.pleasant ?? 0)`, and this site
      // destructures into pa/pb first.
      const pa = rankScore(a), pb = rankScore(b);
      if (pb !== pa) return pb > pa ? b : a;
      return (b.features.highwayPct ?? 100) < (a.features.highwayPct ?? 100) ? b : a;
    });
  };

  // --- Pleasant quality gate (calmEnough) ---
  // Defined BEFORE the Pleasant slot selection so the quality rescue below can
  // use it. Also used by the final picks assembly further down.
  const PLEASANT_HIGHWAY_MAX_PCT = 25;
  const PLEASANT_ROAD_HIGHWAY_MAX_PCT = 50;
  const sMode = opts?.mode ?? "driving";
  const isEpicHwy = (r: { route: RawRoute }) => (r.route.epicHighwayMeters ?? 0) > 1000;
  // (Morgan, Phelps-Way July 15: "it should not be included if it's not
  // actually scenic for MOST of the trip.") The old exemption passed any
  // route driving >=1200m of a curated road — so a 6.6mi busy multilane
  // route that merely CLIPPED Old Sleepy Hollow Road "midway through" wore
  // Scenic. Now the curated-road exemption is PROPORTIONAL: the curated
  // stretch must be a real share of the trip (>=25%) AND the route must not
  // be mostly highway (<=50% — the Round Hill headroom, which keeps the
  // ~30%-highway Round Hill route and still blocks the I-287 slog).
  const drivesPleasantRoad = (r: { route: RawRoute; features: FeatureSummary }) => {
    if ((r.features.highwayPct ?? 0) > PLEASANT_ROAD_HIGHWAY_MAX_PCT) return false;
    const roads = curatedRoadsTravelled(
      { coordinates: r.route.coordinates, steps: r.route.steps, distance: r.route.distance },
      sMode,
      { minMeters: 800 },
    );
    const curatedM = roads.reduce((s, rd) => s + (rd.meters ?? 0), 0);
    return r.route.distance > 0 && curatedM / r.route.distance >= 0.25;
  };
  // (Morgan) OFFER any route that's genuinely calmer than Direct (or low-highway
  // outright, or runs a curated road / epic scenic highway). But `hwy < directHwy`
  // alone let a 65%-highway route wear Pleasant just because Direct was 80% —
  // "those are not the nicer routes" (Boston→Hackley). A calmer-than-Direct route
  // only qualifies when it's MEANINGFULLY calmer: ≥20pts less highway. That keeps
  // the Briarcliff→Rhinebeck pick (70% vs a 97% Direct — a long corridor where
  // some highway is unavoidable) and Boston→Concord's "notably calmer" line,
  // while a main-road-vs-slightly-worse-main-road pair (65 vs 80) no longer
  // sneaks onto the Pleasant card.
  const directHwy = direct.features.highwayPct ?? 0;
  const calmEnough = (r: { route: RawRoute; features: FeatureSummary }) => {
    if (isEpicHwy(r) || drivesPleasantRoad(r)) return true;
    const hwy = r.features.highwayPct ?? 0;
    // (Morgan, Hackley July 15 night — thresholds HERS, set 11:30pm: 35/45.)
    // Water REDEEMS a main road: US-9 along the Hudson is tagged primary, so
    // the highway gates blocked it from Pleasant forever no matter how much
    // river it hugs. Substantial waterfront (>=35% of the route) qualifies,
    // up to a sanity cap (<=45% highway — an interstate with a river view is
    // still an interstate).
    if ((r.features.waterPct ?? 0) >= 35 && hwy <= 45) return true;
    // SHIPPED_JULY_14: the App Store build asks only that a route be LESS
    // highway than Direct. On 15 July that became "at least 20 points less",
    // which silently deleted the whole middle of the range — every route
    // genuinely nicer but not dramatically so. Toronto's Stockbridge route and
    // Hackley's Neperan route both live in that middle.
    if (SHIPPED_JULY_14_ROUTING) return hwy <= PLEASANT_HIGHWAY_MAX_PCT || hwy < directHwy;
    return hwy <= PLEASANT_HIGHWAY_MAX_PCT || directHwy - hwy >= 20;
  };

  // WHICH DOOR (rule 19). Five fallbacks, two of them positional — `[0]` takes
  // whatever happens to be first in the array with no test of any kind. On
  // current location → Nice the engine's own budget was 558m, its top three
  // candidates were 378/461/497m, and the card that reached the display was
  // mapbox-pleasant at 913m: something handed over a route its own cap had
  // excluded, and the display then refused it, leaving ONE card. Five
  // candidate branches and no way to tell which fired is how that stayed
  // invisible. Name the branch before changing any of them.
  // THE DOOR MUST RESPECT THE BUDGET (Morgan, Aug 11).
  //
  // Genova run: Fastest 121m, and the Pleasant cap correctly found NOTHING
  // eligible —
  //     [pleasant] pool=2 → cap 176m → eligible=0 ⚠ NO CALMER ALTERNATIVE
  // — and then the very next line put a 323-minute route on the card anyway:
  //     [pleasant-door] cards[0] ⚠ POSITIONAL — no test → mapbox-pleasant/323m
  //
  // The cap was never the problem. This door sits underneath it and hands over
  // whatever happens to be first in an array, with no test of any kind, which
  // silently reverses a verdict the engine got right. Same shape as Suzie's
  // card (rule 13): a later pass overriding a correct earlier decision.
  //
  // Morgan's framing is the clearest statement of why 323m is not a Pleasant
  // route: it is only ~16% longer in DISTANCE but ~167% longer in TIME. A
  // detour is a few extra miles; this is a different category of driving, and
  // the time budget is exactly the thing that already encodes that.
  //
  // So every door — positional ones included — is now bounded by the same
  // budget the eligibility pass used. If nothing fits, Pleasant collapses onto
  // Direct, which is the honest answer and the branch the engine already had.
  // A BACKSTOP, NOT A SECOND POLICY.
  //
  // Deliberately looser than pleasantBudgetSeconds (+24%, so 150m on this
  // trip) because the eligibility pass upstream used a tolerance-scaled cap of
  // 176m — and a door TIGHTER than eligibility would start refusing Pleasant
  // cards the engine correctly allowed. That would be a new policy smuggled in
  // as a bug fix.
  //
  // 1.6× only ever catches what eligibility already threw out, with margin. On
  // the Genova run: allows anything up to 194m, rejects the 323m route that
  // eligibility had already scored eligible=0. Morgan also reports the run
  // eventually settles on Fast & Pleasant + Scenic, which is the right answer —
  // this makes that the answer immediately instead of after a 323m card has
  // been on screen.
  // Door mechanism extracted VERBATIM into pleasantDoorPick (exported, pure)
  // so the regression test can drive every branch directly — the 1.6× bound
  // must not be removable without a test going red (Morgan, Aug 11 audit).
  const { pick: pleasantDoorPicked, door: pleasantDoor } = pleasantDoorPick(
    pleasantCandidatesDistinct,
    pleasantCards,
    direct.route.duration,
    () => fallbackCard("pleasant"),
  );
  let pleasant: ClassifiedRoute = pleasantDoorPicked;
  // WHO SUPPLIED THE FINAL PLEASANT CARD (Morgan, Aug 23).
  //
  // Three winner-selection defects were fixed one at a time and each revealed
  // another writer, because nothing recorded where the card came from. These
  // name the producer at every assignment; finalLabelRules reports it once on
  // whichever of its four return paths is taken, so the next divergence is
  // read rather than hunted.
  let pleasantFrom = `door:${pleasantDoor}`;
  let pleasantPrev: ClassifiedRoute | null = null;
  // THE MINUTES RULE (Morgan, Aug 20 - see minutesRule.ts for the whole
  // campaign). Driving trips under 25 miles only; both routes must be
  // measured; the pool is the door's own in-budget pool, so the 1.6x cap
  // and every earlier verdict still hold. One confession line per decision.
  {
    const capSec = direct.route.duration * 1.6;
    const mrPool = (pleasantCandidatesDistinct.length ? pleasantCandidatesDistinct : pleasantCards)
      .filter((c) => c.route.duration <= capSec);
    const mr = applyMinutesRule(pleasant, mrPool, { mode: opts?.mode ?? "driving" });
    console.log(`[minutes-rule] ${mr.reason}`);
    if (mr.flipped) pleasant = mr.pick;
  }
  console.log(
    `[pleasant-door] ${pleasantDoor} → ${getRouteSource(pleasant.route) ?? "engine"}/` +
      `${Math.round(pleasant.route.duration / 60)}m ` +
      `(distinct=${pleasantCandidatesDistinct.length} cards=${pleasantCards.length}) ` +
      `| distinct: ${pleasantCandidatesDistinct
        .slice(0, 4)
        .map((c) => `${getRouteSource(c.route) ?? "engine"}/${Math.round(c.route.duration / 60)}m/${c.classification}`)
        .join(" ")}`,
  );
  // Rescue via the full pool when the tight-tolerance pick failed — either
  // because it collapsed into Direct (filter exhausted) OR because it's a
  // main-road route that fails the quality gate while a genuinely green
  // sibling may sit just outside the 1.20× pool (Morgan's Stone Barns /
  // Boston→Hackley repros: Pleasant held a 65%-highway road while a
  // parks-and-water route at +30% time was in budget).
  const pleasantBucketsSource = pleasant.route.coordinates;
  const pleasantBucketsCheck = geometryBuckets(pleasantBucketsSource);
  const pleasantIsSameAsDirect =
    jaccard(pleasantBucketsCheck, directBucketsForDedup) >=
    SLOT_DISTINCT_OVERLAP_THRESHOLD;
  const pleasantFailsQuality = !calmEnough(pleasant);
  // The rescue (15 Jul, 8098ace) is NOT in the shipped build, but switching it
  // off costs two taste rules Morgan approved that same night — the La Crèche
  // curated-park-road pick, and "when the fastest route is itself the greenest,
  // IT wears Pleasant". Those are real and worth keeping.
  //
  // Its failure mode is the problem, not its existence: when nothing qualifies
  // it fills the slot from the pool, and on a long trip the pool is Fast again.
  // That is handled at the picks assembly instead, where a Pleasant pick still
  // riding Direct's road is now dropped by GEOMETRY rather than by object
  // identity. Boston gets two honest cards; La Crèche keeps its third.
  if (pleasantIsSameAsDirect || pleasantFailsQuality) {
    // (Morgan: "it comes back with scenic and should ADD a pleasant route.")
    // This rescue used a hardcoded 1.20× budget while the real Pleasant budget
    // (pleasantBudgetSeconds) allows up to 1.75× on these trips — so genuine
    // backroads candidates (Valhalla no-highway routes at +25–40%) were
    // invisible to the rescue, Pleasant stayed collapsed into Direct, and the
    // UI showed two cards. Use the trip's actual Pleasant time cap.
    const pleasantCapMult =
      opts?.pleasantTimeCapSec && directDurForPool > 0
        ? Math.max(1.2, opts.pleasantTimeCapSec / directDurForPool)
        : 1.2;
    const poolPick = pickDistinctFromPool(pleasantCapMult, [directBucketsForDedup], [direct.route]);
    // On a quality rescue, only swap if the wider-pool pick actually passes
    // the gate — otherwise keep the original (the picks assembly below will
    // drop it rather than mislabel it).
    if (poolPick && (pleasantIsSameAsDirect || calmEnough(poolPick))) {
      // The third replacement path, and until now the only silent one — which
      // is precisely why it hid behind the two that did announce themselves.
      // eslint-disable-next-line no-console
      console.log(
        `[pleasant-swap] rescue (${pleasantIsSameAsDirect ? "same-as-direct" : "failed-quality"}): ` +
        `${getRouteSource(pleasant.route) ?? "engine"} ` +
        `(rank ${rankScore(pleasant).toFixed(1)}, display ${pleasant.scores.pleasant}) → ` +
        `${getRouteSource(poolPick.route) ?? "engine"} ` +
        `(rank ${rankScore(poolPick).toFixed(1)}, display ${poolPick.scores.pleasant})`,
      );
      pleasantPrev = pleasant; pleasantFrom = `rescue:${pleasantIsSameAsDirect ? "same-as-direct" : "failed-quality"}`;
      pleasant = { ...poolPick, classification: "pleasant" };
    }
  }

  // BLIND-TIE REFINEMENT (Morgan's 45s console log, Tarrytown→Hackley):
  // when the corridor OSM fetch starves, enriched features flatten
  // (hwy0/com0) and pleasant scores TIE — and the tie fell to arbitrary
  // pool order, handing Pleasant to a near-copy of Direct
  // (pleasant:mapbox-direct/13m) while the curated scenic road
  // (scenic-road/15m through Sleepy Hollow) sat unused. On a tie (±3 pts),
  // a route BUILT to be pleasant — curated scenic-road injection,
  // discovered corridor, or the no-highway engine — outranks an anonymous
  // direct alternative for the Pleasant slot.
  {
    const PURPOSE_BUILT = new Set([
      "scenic-road", "scenic-corridor", "calm-corridor", "charming-towns",
      "scenic-anchor", "valhalla-adventure", "mapbox-pleasant",
    ]);
    if (!PURPOSE_BUILT.has(getRouteSource(pleasant.route))) {
      const cap = opts?.pleasantTimeCapSec ?? directDurForPool * 1.2;
      const better = coherentScored
        .filter(
          (s) =>
            s.route !== pleasant.route &&
            s.route !== direct.route &&
            PURPOSE_BUILT.has(getRouteSource(s.route)) &&
            s.route.duration <= cap &&
            // PRE-FLOOR, NOT DISPLAY (Morgan, Aug 23). This guard is what stops
            // a purpose-built route replacing a materially better Pleasant
            // pick. It compared `scores.pleasant`, which carries componentFloor
            // — and with nearly everything flattened onto 55, `55 >= 55 - 3`
            // was true for almost any candidate, so the guard was inert and the
            // swap ran on source alone. Same tolerance, same rule; it now reads
            // the value the selector itself ranked on.
            rankScore(s) >= rankScore(pleasant) - 3 &&
            jaccard(geometryBuckets(s.route.coordinates), directBucketsForDedup) <
              SLOT_DISTINCT_OVERLAP_THRESHOLD,
        )
        .sort(
          (a, b) =>
            rankScore(b) - rankScore(a) ||
            a.route.duration - b.route.duration,
        )[0];
      if (better && calmEnough(better)) {
        // eslint-disable-next-line no-console
        console.log(
          `[pleasant-swap] better: ${getRouteSource(pleasant.route) ?? "engine"} ` +
          `(rank ${rankScore(pleasant).toFixed(1)}, display ${pleasant.scores.pleasant}) → ` +
          `${getRouteSource(better.route) ?? "engine"} ` +
          `(rank ${rankScore(better).toFixed(1)}, display ${better.scores.pleasant})`,
        );
        pleasantPrev = pleasant; pleasantFrom = "better";
        pleasant = { ...better, classification: "pleasant" };
      }
    }
  }

  // WATER PREFERENCE (Morgan, same night): water is her strongest taste
  // signal. If a distinct, in-budget candidate runs SUBSTANTIALLY more
  // waterfront than the current Pleasant pick (+20 water pts) and isn't
  // wildly less pleasant otherwise (within 10 pts), it takes the slot —
  // the 19-min Hudson run beats the 16-min inland stone walls.
  {
    const cap = opts?.pleasantTimeCapSec ?? directDurForPool * 1.2;
    const waterBetter = coherentScored
      .filter(
        (s) =>
          s.route !== pleasant.route &&
          s.route !== direct.route &&
          s.route.duration <= cap &&
          (s.features.waterPct ?? 0) >= (pleasant.features.waterPct ?? 0) + 20 &&
          // PRE-FLOOR, NOT DISPLAY (Morgan, Aug 23). Proven on Ossining: the
          // selector's winner scored 5.1 and this block replaced it with a
          // route at -58.0, because both displayed 55 and `55 >= 55 - 10`
          // passed — leaving waterPct as the only real criterion. Tolerance
          // unchanged at 10; it just measures the right number now.
          rankScore(s) >= rankScore(pleasant) - 10 &&
          calmEnough(s) &&
          jaccard(geometryBuckets(s.route.coordinates), directBucketsForDedup) <
            SLOT_DISTINCT_OVERLAP_THRESHOLD,
      )
      .sort(
        (a, b) =>
          (b.features.waterPct ?? 0) - (a.features.waterPct ?? 0) ||
          rankScore(b) - rankScore(a),
      )[0];
    if (waterBetter) {
      // eslint-disable-next-line no-console
      console.log(
        `[pleasant-swap] waterBetter: ${getRouteSource(pleasant.route) ?? "engine"} ` +
        `(rank ${rankScore(pleasant).toFixed(1)}, display ${pleasant.scores.pleasant}, ` +
        `water ${Math.round(pleasant.features.waterPct ?? 0)}) → ` +
        `${getRouteSource(waterBetter.route) ?? "engine"} ` +
        `(rank ${rankScore(waterBetter).toFixed(1)}, display ${waterBetter.scores.pleasant}, ` +
        `water ${Math.round(waterBetter.features.waterPct ?? 0)})`,
      );
      pleasantPrev = pleasant; pleasantFrom = "waterBetter";
      pleasant = { ...waterBetter, classification: "pleasant" };
    }
  }

  const pleasantBucketsForDedup = geometryBuckets(pleasant.route.coordinates);
  const scenicCandidatesDistinct = scenicCards.filter((c) => {
    const cb = geometryBuckets(c.route.coordinates);
    return (
      !sameRoadForSlot(c.route, direct.route, jaccard(cb, directBucketsForDedup)) &&
      !sameRoadForSlot(c.route, pleasant.route, jaccard(cb, pleasantBucketsForDedup))
    );
  });
  let scenic: ClassifiedRoute =
    scenicCandidatesDistinct.find((c) => c.classification === "scenic") ??
    scenicCandidatesDistinct[0] ??
    scenicCards.find((c) => c.classification === "scenic") ??
    scenicCards[0] ??
    fallbackCard("scenic");
  // (#192) Same full-pool fallback for Adventure: must differ from BOTH
  // Direct and Pleasant. No time cap — Adventure surfaces the most
  // pleasant route in the pool regardless of duration.
  const scenicBucketsCheck = geometryBuckets(scenic.route.coordinates);
  const scenicIsSameAsDirect = sameRoadForSlot(
    scenic.route, direct.route, jaccard(scenicBucketsCheck, directBucketsForDedup));
  const scenicIsSameAsPleasant = sameRoadForSlot(
    scenic.route, pleasant.route, jaccard(scenicBucketsCheck, pleasantBucketsForDedup));
  if (scenicIsSameAsDirect || scenicIsSameAsPleasant) {
    const poolPick = pickDistinctFromPool(
      Number.POSITIVE_INFINITY,
      [directBucketsForDedup, pleasantBucketsForDedup],
      [direct.route, pleasant.route],
    );
    if (poolPick) {
      scenic = { ...poolPick, classification: "scenic" };
    }
  }

  // (#190) NEW RULES per Morgan, replacing the old overlap-percentage
  // collapse logic:
  //
  //   1. The three cards CANNOT pick the same route (distinct route
  //      objects only — drop duplicates).
  //   2. After picking, sort by duration: fastest = Direct, slowest =
  //      Adventure, middle = Pleasant.
  //   3. No more polyline-overlap collapse. If we have 3 distinct routes,
  //      we show 3 cards even when their geometries look similar — Morgan
  //      observed the overlap rule "doesn't work in certain routes"
  //      (cards collapsed when they should have stayed distinct).
  //
  // Earlier #183/#178/#168/#155/#152/#151 collapse blocks deleted here.
  // If you're looking at git blame for any of those, they lived in this
  // function above this comment.

  // Step 1: collect picks, dropping ones that share a route reference
  // with a higher-priority pick OR were marked sameAsFastest by
  // classifyRoutes (which means the per-tolerance pool yielded no
  // distinct alternative). Priority order: Direct > Pleasant > Adventure.
  // (Morgan, real-drive feedback) Pleasant and Scenic must NEVER be a highway
  // slog — that's the whole point of the app, and it's what Direct is for. Cap
  // them at 25% highway. The one deliberate exception is a route that runs a
  // legendary scenic highway (PCH, etc.), which IS the experience. If a corridor
  // has no calm option, the slot simply drops — the user sees Direct (and any
  // genuinely pleasant route) rather than a highway route mislabeled "Pleasant".
  // (calmEnough + helpers were moved ABOVE the Pleasant slot selection so the
  //  quality rescue can use them — see there.)

  const picks: ClassifiedRoute[] = [direct];
  const directRouteRef = direct.route;
  // NO PHANTOM PLEASANT (Morgan, July 31: "why are there no pleasant routes to
  // Boston" — and her 10 July App Store build, which showed only Fast + Scenic
  // for that trip and was right to).
  //
  // Identity here was reference-only: `pleasant.route !== directRouteRef`. A
  // rescue pick that follows the SAME ROAD but differs by a few metres is a
  // different object, so it passed — and New York → Boston showed a "Pleasant
  // route · FOR YOU" card carrying Fast's exact 3h 8m and 191.2 mi.
  //
  // On a 191-mile trip no road can be both inside the Pleasant time budget and
  // under the motorway bar; back roads lose far more than the budget allows.
  // The honest answer is two cards. The quality rescue (added 15 July) refuses
  // that and fills the slot with whatever the pool has, which is Fast again.
  //
  // So identity is judged by GEOMETRY, the same measure the rescue itself used
  // to detect the problem. Same road as Direct → no Pleasant card. The rescue
  // still runs and still helps where a genuinely different road exists.
  // Reuse the buckets computed before the rescue when the pick didn't change.
  // geometryBuckets walks every coordinate, and a transcontinental polyline is
  // ~35k points — not something to recompute for nothing (Morgan, Aug 1: the
  // app went "really slow" on a California trip).
  const pleasantIsStillDirectsRoad = sameRoadForSlot(
    pleasant.route,
    direct.route,
    jaccard(
      pleasant.route.coordinates === pleasantBucketsSource
        ? pleasantBucketsCheck
        : geometryBuckets(pleasant.route.coordinates),
      directBucketsForDedup,
    ),
  );
  if (
    pleasant.route !== directRouteRef &&
    !pleasant.sameAsFastest &&
    !pleasantIsStillDirectsRoad
  ) {
    if (calmEnough(pleasant)) {
      picks.push(pleasant);
    } else if ((pleasant.features.highwayPct ?? 100) <= 50) {
      // (Morgan: never drop — "call it alternative".) A borderline route
      // (fails the calm gate but isn't a true slog) stays visible as an
      // honest Alternative. True slogs (>50% highway) still get no card —
      // her morning rule ("should not be included") stands for those.
      picks.push({
        ...pleasant,
        variantLabel: "Alternative",
        variantSub: "Another good way",
      });
    }
  }
  if (
    scenic.route !== directRouteRef &&
    scenic.route !== pleasant.route &&
    !scenic.sameAsFastest &&
    calmEnough(scenic)
  ) {
    picks.push(scenic);
  }

  // Strip collapse-related flags so the UI doesn't render "Same as ..." copy.
  const strip = (p: ClassifiedRoute): ClassifiedRoute => {
    const { sameAsFastest, sameAsFastestReason, sameAsClass, scenicSearching, ...rest } = p;
    void sameAsFastest;
    void sameAsFastestReason;
    void sameAsClass;
    void scenicSearching;
    return rest as ClassifiedRoute;
  };

  // Step 2 + 3 (Morgan: "just have a scenic card, not have it live in pleasant").
  // A deliberately-discovered "long way" (tagged scenic-corridor — the coastline
  // / along-corridor / scenic-tag passes) ALWAYS takes the Scenic label, no
  // matter how its duration ranks. Otherwise, when the only alternative to Direct
  // IS the long coastal route, the positional "fastest, pleasant, scenic" labeling
  // would hand it the 2nd (Pleasant) slot — exactly the mislabel we're fixing.
  // The remaining picks are labeled by duration order (fastest, then pleasant).
  // When MORE THAN ONE discovered route is present (e.g. a walk with both the
  // waterfront route and the 'nice city streets' variety route), the LONGEST is
  // the Scenic "long way" and the other(s) fall into the Pleasant slot — so two
  // nice walks read as Pleasant + Scenic, never one as a bare "Alternate".
  const corridorPicks = picks.filter(
    (p) => getRouteSource(p.route) === "scenic-corridor",
  );
  // DIRECT IDENTITY WINS THE COLLISION (Morgan, Aug 23).
  //
  // "If the selected corridorPick is also the minimum-duration/Direct route, it
  // must not consume the Scenic slot as corridorPick. Its primary
  // classification is Fast."
  //
  // This is not a claim that a fast route cannot be scenic. It is that the same
  // road must not be pulled out of the Fast slot merely because it ARRIVED as a
  // scenic-corridor candidate — and must never appear twice, once as Fast and
  // once as Scenic.
  //
  // Reproduced on production Pleasantville: a scenic-corridor route came back
  // quicker than every direct route, became `direct`, was then also chosen as
  // corridorPick, and so was lifted out of `picks`. What remained held no Fast
  // identity at all, and the old positional labelling handed "fastest" to a
  // 13-minute route while 10- and 11-minute routes sat in the same pool:
  //
  //   [fast-invariant] VIOLATED — the Fast card is mapbox-direct/13m but
  //                    scenic-corridor/11m is quicker and was eligible.
  //
  // A long way that is the fastest way is not a long way. Excluding it here
  // leaves it in `picks` carrying its Direct identity, so the identity-based
  // labelling below gives it Fast, and Scenic is chosen from what is left.
  const corridorPicksNotDirect = corridorPicks.filter((p) => p.route !== directRouteRef);
  if (corridorPicks.length > 0 && corridorPicksNotDirect.length !== corridorPicks.length) {
    try {
      // eslint-disable-next-line no-console
      console.log(
        `[corridor] the corridor candidate IS the Direct route ` +
        `(${getRouteSource(direct.route) ?? "engine"}/${Math.round(direct.route.duration / 60)}m) — ` +
        `it keeps Fast and does not take the Scenic slot; Scenic comes from the rest.`,
      );
    } catch { /* a diagnostic must never break a classification */ }
  }
  const corridorPick =
    corridorPicksNotDirect.length > 0
      ? corridorPicksNotDirect.reduce((a, b) => (b.route.duration > a.route.duration ? b : a))
      : undefined;
  if (corridorPick) {
    const rest = picks
      .filter((p) => p !== corridorPick)
      .sort((a, b) => a.route.duration - b.route.duration);
    // (Morgan) When the ONLY non-long-way pick is the fastest route AND that
    // route runs a scenic parkway (the Taconic etc.), it IS the pleasant route —
    // there's no separate calmer road because the scenic parkway is both the
    // fastest and the nicest. Label it Pleasant rather than a bare "fastest", so
    // Briarcliff→Rhinebeck shows Pleasant (Taconic) + Scenic (the long way)
    // instead of collapsing the Pleasant slot to nothing.
    const lone = rest.length === 1 ? rest[0] : null;
    if (lone && isEpicHwy(lone)) {
      // (Morgan) The lone fast route runs a scenic parkway (the Taconic) — it's
      // both the quickest AND a lovely drive. KEEP IT AS THE FAST CARD, and look
      // for a genuinely-calmer sibling within the Pleasant budget to fill the
      // Pleasant slot: a distinct route (different line on the map) that's lower-
      // highway than Direct. When one exists we show Fast (the parkway) + Pleasant
      // (the calmer line) + Scenic (the long way) — the three-card layout Morgan
      // asked for ("keep a fast route card in those situations as well"). Only when
      // there's NO distinct calmer middle route does the parkway itself take the
      // Pleasant slot (it truly is the one fast-and-pleasant road for the trip).
      const loneBuckets = geometryBuckets(lone.route.coordinates);
      const cap = opts?.pleasantTimeCapSec ?? lone.route.duration * 1.24;
      const sibling =
        coherentScored
          .filter(
            (s) =>
              s.route !== lone.route &&
              s.route !== corridorPick.route &&
              s.route.duration <= cap,
          )
          .map(
            (s) => ({ ...s, classification: "pleasant" as const }) as ClassifiedRoute,
          )
          .filter(
            (s) =>
              jaccard(geometryBuckets(s.route.coordinates), loneBuckets) <
                SLOT_DISTINCT_OVERLAP_THRESHOLD && calmEnough(s),
          )
          .sort(
            (a, b) =>
              (a.features.highwayPct ?? 100) - (b.features.highwayPct ?? 100) ||
              a.route.duration - b.route.duration,
          )[0] ?? null;
      if (sibling) {
        return finalLabelRules([
          { ...strip(lone), classification: "fastest" as const },
          { ...strip(sibling), classification: "pleasant" as const },
          { ...strip(corridorPick), classification: "scenic" as const },
        ], "corridor + parkway sibling");
      }
      return finalLabelRules([
        { ...strip(lone), classification: "pleasant" as const },
        { ...strip(corridorPick), classification: "scenic" as const },
      ], "corridor + lone parkway");
    }
    // IDENTITY, NOT POSITION — the same fix as the picks assembly below
    // (Morgan, Aug 23). This block used to read
    //
    //   const restLabels = ["fastest", "pleasant"] as const;
    //   rest.map((p, i) => ({ ...strip(p), classification: restLabels[i] }))
    //
    // and I left it alone when 4474 was fixed, on the grounds that no case had
    // been reproduced on it. One was, on the very next Pleasantville run:
    //
    //   [fast-invariant] VIOLATED — the Fast card is mapbox-direct/13m but
    //                    scenic-corridor/11m is quicker and was eligible.
    //   [diag] -:mapbox-direct/11m | fastest:mapbox-direct/13m
    //          | pleasant:scenic-corridor/11m | -:scenic-corridor/10m
    //
    // The mechanism is `rest` EXCLUDING corridorPick and then being sorted by
    // duration: whatever lands at index 0 is handed "fastest", so when the
    // corridor route is quicker than everything left, a slower route acquires
    // the Fast label purely by array position. An 11-minute mapbox-direct sat
    // in the same pool with no card at all.
    //
    // Labels now come from route identity, exactly as at the picks assembly:
    // direct -> fastest, pleasant -> pleasant, the corridor pick -> scenic.
    // The duration sort above is untouched and still sets DISPLAY order; it no
    // longer decides what anything IS. There is no positional fallback — an
    // unmatched pick keeps whatever classification it arrived with and says so
    // loudly, because inventing a label from an index is the defect itself.
    const out: ClassifiedRoute[] = rest.map((p) => {
      const byIdentity =
        p.route === directRouteRef
          ? ("fastest" as const)
          : p.route === pleasant.route
            ? ("pleasant" as const)
            : scenic && p.route === scenic.route
              ? ("scenic" as const)
              : null;
      if (byIdentity) return { ...strip(p), classification: byIdentity };
      try {
        // eslint-disable-next-line no-console
        console.error(
          `[picks-invariant] a pick in the corridor branch matches none of ` +
          `direct/pleasant/scenic — source=${getRouteSource(p.route) ?? "engine"} ` +
          `dur=${Math.round(p.route.duration / 60)}m keeping classification=${p.classification}.`,
        );
      } catch { /* a diagnostic must never break a classification */ }
      return strip(p);
    });
    out.push({ ...strip(corridorPick), classification: "scenic" });
    return finalLabelRules(out, "corridor + rest");
  }

  // No discovered long-way in the mix: original behavior. Sort by duration and
  // assign labels positionally — fastest = Direct, middle = Pleasant, slowest =
  // Scenic.
  picks.sort((a, b) => a.route.duration - b.route.duration);
  // POSITIONAL LABELS NEED THREE PICKS (Morgan, Aug 9, current location → Nice).
  //
  // With three, sorting by duration and handing out fastest/pleasant/scenic in
  // order is exactly right. With TWO, index 1 collects "pleasant" no matter
  // what that route actually is — and the Scenic pick wears the Pleasant label.
  //
  // Traced end to end tonight:
  //   [pleasant-door] → mapbox-direct/445m        selection was fine
  //   NO PHANTOM PLEASANT drops it — same road as Direct, correctly
  //   picks = [direct/376m, scenic/mapbox-pleasant/916m]
  //   [scored-write]  pleasant := mapbox-pleasant/916m   ← 15 hours, labelled Pleasant
  //   [sane] dropped pleasant/mapbox-pleasant — 916m exceeds 2× the fastest
  //   → one card
  // The engine found no Pleasant route (right answer, two cards) and then the
  // label assignment invented one out of the Scenic pick, which the display had
  // to throw away — costing the Scenic card too. It also explains the Umbria
  // screenshot where two cards both claimed Pleasant.
  //
  // When fewer than three picks survive, honour what each route was CHOSEN to
  // be instead of where it landed in a sort. Three-pick behaviour is untouched.
  const scenicRouteRef = scenic?.route;

  // THE THREE-PICK CASE WAS STILL POSITIONAL, AND THAT WAS THE PRODUCER
  // (Morgan, Aug 23 — Ossining, traced to this line).
  //
  // The Aug 9 fix above repaired the <3 case and left `orderedLabels[i]`
  // standing for >=3. Instrumenting the single choke point caught it in one
  // run: the selector held a route ranking 12.2, and the card the user saw as
  // Pleasant ranked -55.9, with `replaced=no` — no swap block had fired. The
  // route was not replaced, it was RELABELLED, by its index in a duration sort.
  // That is why three correct one-line selection fixes upstream changed nothing.
  //
  // WHY AN UNMATCHED PICK IS AN INVARIANT VIOLATION, NOT A CASE TO HANDLE.
  // Morgan asked this before approving the fix, and the answer is by
  // construction. `picks` is built in exactly three statements above:
  //     const picks: ClassifiedRoute[] = [direct];
  //     ... picks.push(pleasant)  — or  picks.push({ ...pleasant, Alternative })
  //     ... picks.push(scenic)
  // There is no fourth push site. The Alternative variant is a spread of
  // `pleasant`, so it is a different OBJECT but carries the same `.route`
  // reference, which is why identity is matched on `.route` and not on the
  // card. And reaching `picks.length >= 3` REQUIRES both pushes to have run,
  // so at three picks the set is exactly {direct, pleasant, scenic} — pairwise
  // distinct, because each push is guarded against colliding with the ones
  // before it.
  //
  // So an unmatched route here means someone added a push site without
  // updating this map. It gets a loud confession (rule 17) and KEEPS ITS
  // EXISTING CLASSIFICATION. It must not fall back to `orderedLabels[i]`:
  // that is the exact defect being removed, and a silent fallback would let
  // it grow back in a shape nobody would think to look for.
  //
  // The duration sort above is UNCHANGED — display order is still fastest
  // first. Only the labels stop coming from the index.
  //
  // CONSEQUENCE, stated plainly: until now the >=3 branch GUARANTEED
  // Fast < Pleasant < Scenic by duration, because it assigned labels from that
  // sort. Now that ordering is enforced only by finalLabelRules' Rule 1
  // ("if the scenic route is shorter than the pleasant route they should have
  // opposite labels"), which is left untouched by this change.
  const labelByIdentity = (p: ClassifiedRoute) =>
    p.route === directRouteRef
      ? ("fastest" as const)
      : p.route === pleasant.route
        ? ("pleasant" as const)
        : scenicRouteRef && p.route === scenicRouteRef
          ? ("scenic" as const)
          : null;

  return finalLabelRules(
    picks.map((p, i) => {
      if (picks.length >= 3) {
        const byIdentity = labelByIdentity(p);
        if (byIdentity) return { ...strip(p), classification: byIdentity };
        try {
          // eslint-disable-next-line no-console
          console.error(
            `[picks-invariant] a pick at the >=3 assembly matches none of ` +
            `direct/pleasant/scenic — source=${getRouteSource(p.route) ?? "engine"} ` +
            `dur=${Math.round(p.route.duration / 60)}m idx=${i} ` +
            `keeping classification=${p.classification}. picks is built only from ` +
            `[direct, pleasant (or its Alternative variant), scenic], so this means a ` +
            `new push site was added above without updating the identity map.`,
          );
        } catch { /* a diagnostic must never break a classification */ }
        return strip(p);
      }
      return {
        ...strip(p),
        classification:
          i === 0
            ? ("fastest" as const)
            : scenicRouteRef && p.route === scenicRouteRef
              ? ("scenic" as const)
              : ("pleasant" as const),
      };
    }),
  );

  // ---- FINAL LABEL RULES (Morgan, July 15 evening) ----
  // Two invariants enforced on whatever the paths above produced:
  //
  //  1. "If the scenic route is shorter than the pleasant route — they should
  //     have opposite labels." Scenic is the long way BY DEFINITION; a
  //     discovered corridor that happens to be quicker than the Pleasant pick
  //     swaps labels with it.
  //
  //  2. The Hackley repro: the FAST card read "passes more parks and green
  //     space than the other routes" while a less-green line wore Pleasant.
  //     When the fastest pick is ITSELF meaningfully the greenest (pleasant
  //     score clearly above the Pleasant pick's), the fastest route IS the
  //     pleasant route — it takes the Pleasant label (the UI's Fast slot is
  //     the Google carrier, so the user still sees a Fast card), and the
  //     ex-Pleasant line drops rather than mislabel.
  // Every clause below was VETTED WITH MORGAN individually (July 15, late
  // evening — the new working agreement: no routing-rule change ships
  // without her sign-off on problem + solution).
  /**
   * REPORTING WRAPPER (Morgan, Aug 23) — recording only, no behaviour.
   *
   * The card the user sees as Pleasant is not necessarily the `pleasant`
   * variable this function was handed. That is why three separate
   * winner-selection fixes each left the divergence intact. Every path funnels
   * through here, so this is the one place that can name the producer.
   *
   * CORRECTION (Morgan caught this): the first version of this line blamed
   * `finalLabelRules(positional)` for every relabel, and that was wrong twice
   * over. The POSITIONAL assignment is not inside finalLabelRules at all — it
   * is at the CALL SITE, where the label was chosen before the cards were ever
   * passed in. And finalLabelRules does its own relabelling for entirely
   * different reasons (the supreme rule, the water swap, the scenic-shorter
   * swap), which are rules Morgan wrote deliberately and are not the defect.
   *
   * So the two stages are now separated and reported separately:
   *   `site` names the call site that ASSIGNED the label;
   *   the normalization rules inside finalLabelRulesCore are named only when
   *   they actually moved the label away from what the call site assigned.
   */
  function finalLabelRules(
    cards: ClassifiedRoute[],
    site = "picks assembly",
  ): ClassifiedRoute[] {
    const out = finalLabelRulesCore(cards);
    // Freshness key for the recorded Pleasant pool: a reader can check that at
    // least one of these routes is in what it is displaying before trusting it.
    try { stampPleasantPoolCards(out); } catch { /* never break a classification */ }
    try {
      const finalPleasant = out.find((c) => c.classification === "pleasant");
      const assignedPleasant = cards.find((c) => c.classification === "pleasant");
      const nameOf = (c?: ClassifiedRoute | null) =>
        c ? `${getRouteSource(c.route) ?? "engine"}/${Math.round(c.route.duration / 60)}m` : "none";
      // Did the CALL SITE hand the Pleasant label to something other than the
      // route the selector chose?
      const siteMoved = !!assignedPleasant && assignedPleasant.route !== pleasant.route;
      // Did finalLabelRulesCore then move it again?
      const coreMoved =
        (finalPleasant?.route ?? null) !== (assignedPleasant?.route ?? null);
      const producer = coreMoved
        ? `finalLabelRules(normalization) — overrode ${siteMoved ? `${site} label assignment` : pleasantFrom}`
        : siteMoved
          ? `${site} label assignment — overrode ${pleasantFrom}`
          : pleasantFrom;
      // eslint-disable-next-line no-console
      console.log(
        `[pleasant-final] producer=${producer}` +
        `  assigned=${nameOf(assignedPleasant)}` +
        `  card=${nameOf(finalPleasant)}` +
        `  rank=${finalPleasant ? rankScore(finalPleasant).toFixed(1) : "n/a"}` +
        `  display=${finalPleasant ? finalPleasant.scores.pleasant : "n/a"}` +
        `  direct=${nameOf(direct)}` +
        `  replaced=${pleasantPrev ? `${nameOf(pleasantPrev)} (rank ${rankScore(pleasantPrev).toFixed(1)})` : "no"}` +
        `  selectorHeld=${nameOf(pleasant)} (rank ${rankScore(pleasant).toFixed(1)})` +
        `  roads=${finalPleasant ? roadsOfSteps(finalPleasant.route.steps).slice(0, 70) : "-"}`,
      );
    } catch { /* a diagnostic must never break a classification */ }
    return out;
  }

  function finalLabelRulesCore(cards: ClassifiedRoute[]): ClassifiedRoute[] {
    let outCards = cards;
    const waterOf = (c: ClassifiedRoute) => c.features.waterPct ?? 0;
    // THE SUPREME RULE (Morgan, verbatim, July 15 night — numbers approved):
    // "when the route is NOT on a main road and still the same time or less
    // time as the fastest time, choose that route." Not curated, not
    // waypointed — measured on ANY road (Neperan included). ≤25% main-road
    // share, within 60s of the engine fastest → it IS the Pleasant card
    // (the auto-selected route). Every rule below yields to this one.
    const ruleOneQualifies = (c: { features: FeatureSummary; route: RawRoute }) =>
      !ABLATION.supremeRuleOff &&
      (c.features.highwayPct ?? 100) <= 25 &&
      c.route.duration <= engineFastest.route.duration + 60;
    {
      // Driving only: on foot/bike nearly every route is "not on a main
      // road", so this rule would hand Pleasant to the boring direct
      // sidewalk. Walks keep their own vetted rules.
      // Tie-break among qualifiers (Morgan, both clauses approved):
      //  1. PARK-SHARE — "one spends almost the whole time on it" (Bedford).
      //  2. MOST BACK ROADS — Home Depot repro: the fastest route had "more
      //     back roads than the other routes" at the same 16 minutes and she
      //     asked "it's a better route — why is it not the pleasant one?"
      //     Fewest main-road share wins the title; water/score never outrank
      //     a same-time route with meaningfully better roads.
      //  3. Then score, then duration.
      const parksPerKm = (s: { features: FeatureSummary; route: RawRoute }) =>
        (s.features.parks ?? 0) / Math.max(0.5, s.route.distance / 1000);
      const mainShare = (s: { features: FeatureSummary }) =>
        (s.features.highwayPct ?? 0) + (s.features.majorRoadPct ?? 0) * 0.5;
      const q = (sMode === "driving" ? coherentScored : [])
        .filter(ruleOneQualifies)
        .sort(
          (a, b) =>
            parksPerKm(b) - parksPerKm(a) ||
            mainShare(a) - mainShare(b) ||
            rankScore(b) - rankScore(a) ||
            a.route.duration - b.route.duration,
        )[0];
      const pl = outCards.find((c) => c.classification === "pleasant");
      // FAST MUST STAY FAST (Morgan, Aug 23 — Ossining, verbatim invariant:
      // "the fastest route must remain the Fast/Direct card. A normalization
      // rule must never remove the Fast slot merely because that route also
      // qualifies as Pleasant.").
      //
      // WHAT WENT WRONG. This block moves the Pleasant label onto the supreme
      // rule's winner and slides the old Pleasant to Scenic. It never asked
      // what the winner was ALREADY labelled. On Ossining the winner was the
      // Fast card itself (9 min, 0% highway, 2% main, 91% residential — it
      // genuinely qualifies), so the labels came out
      //
      //   entering  fastest 9m   pleasant 13m   scenic 14m
      //   leaving   pleasant 9m  scenic 13m     scenic 14m      <- no fastest
      //
      // and the display, with no Fast card and two Scenics, collapsed three
      // good distinct routes down to ONE visible card. Proven by ablation:
      // supremeRuleOff alone restores fastest/pleasant/scenic. Rule 1 was
      // never reached, and every candidate had waterPct 0, so no water rule
      // is involved.
      //
      // THE SMALLEST FIX THAT HOLDS THE INVARIANT: the supreme rule may
      // promote any card except the Fast one. It is a rule about which route
      // deserves the Pleasant TITLE, not a licence to spend the Fast slot —
      // and Fast has to keep meaning fastest, so backfilling it with a slower
      // route is not an option either.
      //
      // Nothing else changes: the tie-break, the thresholds, the displacement
      // of the old Pleasant, and the `!holder` append are all untouched, and
      // the rule still fires normally whenever its winner is not the Fast card.
      const fastCard = outCards.find((c) => c.classification === "fastest");
      const winnerIsFastCard = !!q && !!fastCard && fastCard.route === q.route;
      // THE DIRECT SLOT KEEPS ITS ROUTE (Morgan, Aug 23, superseding the
      // re-seat): "I do not want us promoting a different route from the
      // scored pool solely to preserve the word Fast. The route identities and
      // routing choices should come first; the label should describe them
      // honestly."
      //
      // So the supreme rule may not consume the Direct slot, and nothing is
      // backfilled into it either. What changes instead is what the card is
      // CALLED — see the honest-label pass at the end of this function.
      if (winnerIsFastCard && q) {
        // Rule 17 — no silent verdicts, in either direction.
        const who = (r: { route: RawRoute }) =>
          `${getRouteSource(r.route) ?? "engine"}/${Math.round(r.route.duration / 60)}m`;
        // eslint-disable-next-line no-console
        console.log(
          `[supreme-rule] STOOD DOWN — winner ${who(q)} (hwy ${Math.round(q.features.highwayPct ?? 0)}%) ` +
          `already holds the Direct slot, and the slot's route is not up for grabs. ` +
          `Pleasant stays with ${pl ? who(pl) : "no card"}.`,
        );
      }
      if (q && !winnerIsFastCard && (!pl || pl.route !== q.route)) {
        // Unchanged: the card carrying the winner's route. On the re-seat path
        // that is the old Fast card, which is exactly what should become
        // Pleasant — the replacement was inserted as a separate card above and
        // carries a different route, so it is never matched here.
        const holder = outCards.find((c) => c.route === q.route);
        // Displace the old Pleasant WITHOUT dropping it (Morgan: never drop;
        // "call it alternative"): it slides to the Scenic slot, or shows as
        // an Alternative when Scenic is taken.
        const scenicTaken = outCards.some(
          (c) => c.classification === "scenic" && c !== pl,
        );
        outCards = outCards.map((c) => {
          if (c === holder) return { ...c, classification: "pleasant" as const };
          if (c === pl) {
            return scenicTaken
              ? {
                  ...c,
                  classification: "scenic" as const,
                  variantLabel: "Alternative",
                  variantSub: "Another good way",
                }
              : { ...c, classification: "scenic" as const };
          }
          return c;
        });
        if (!holder) {
          outCards = [
            ...outCards,
            { ...strip(q as ClassifiedRoute), classification: "pleasant" as const },
          ];
        }
      }
    }
    const isGreen = (c: ClassifiedRoute) =>
      (c.features.parks ?? 0) >= 2 ||
      waterOf(c) > 10 ||
      (c.features.highwayPct ?? 100) <= 25;

    // Rule 0 — WATER CLAIMS PLEASANT (Morgan: "the nice route that goes by
    // the water is still not selected as pleasant"; thresholds hers). The
    // positional sort labels the MIDDLE duration Pleasant, which can hand
    // the label to an inland route while the water run sits in Scenic. If a
    // card runs substantially more waterfront (+20 pts) than the Pleasant
    // card and is inside the Pleasant time budget, the two swap — and the
    // displaced route is "called alternative" (her word), never dropped.
    {
      const p = outCards.find((c) => c.classification === "pleasant");
      const s = outCards.find((c) => c.classification === "scenic");
      const cap = opts?.pleasantTimeCapSec ?? Number.POSITIVE_INFINITY;
      if (
        sMode === "driving" && // walks keep Morgan's older rule: longer discovered walk IS Scenic
        p &&
        !ruleOneQualifies(p) && // the supreme rule's pick is untouchable
        s &&
        waterOf(s) >= waterOf(p) + 20 &&
        s.route.duration <= cap &&
        (s.scores.pleasant ?? 0) >= (p.scores.pleasant ?? 0) - 10
      ) {
        outCards = outCards.map((c) =>
          c === s
            ? { ...c, classification: "pleasant" as const }
            : c === p
              ? {
                  ...c,
                  classification: "scenic" as const,
                  variantLabel: "Alternative",
                  variantSub: "Quicker, with less waterfront",
                }
              : c,
        );
      }
    }

    // Rule 1 — "if the scenic route is shorter than the pleasant route they
    // should have opposite labels" … EXCEPT when Pleasant is the deliberate
    // WATER pick (Morgan: water wins Pleasant even when a few minutes
    // slower). In that case the quicker non-water route is shown honestly as
    // an "Alternative" (her word) instead of swapping the labels back.
    {
      const p = outCards.find((c) => c.classification === "pleasant");
      const s = outCards.find((c) => c.classification === "scenic");
      if (p && s && s.route.duration < p.route.duration) {
        if (waterOf(p) >= waterOf(s) + 20) {
          outCards = outCards.map((c) =>
            c === s
              ? {
                  ...c,
                  variantLabel: "Alternative",
                  variantSub: "Quicker, with less waterfront",
                }
              : c,
          );
        } else {
          outCards = outCards.map((c) =>
            c === p
              ? { ...c, classification: "scenic" as const }
              : c === s
                ? { ...c, classification: "pleasant" as const }
                : c,
          );
        }
      }
    }

    // Rule 2 — when the fastest route is itself the greenest on offer, "the
    // fastest route in that case should BE the pleasant card" (Morgan,
    // verbatim). NOTHING is ever dropped: the previous Pleasant pick slides
    // to the open Scenic slot (labeled "Alternative" when it isn't honestly
    // scenic — the Hackley twin's last 0.1mi mattered, so it stays visible).
    // If all three slots are occupied, labels stay untouched.
    {
      const fast = outCards.find((c) => c.classification === "fastest");
      const pl = outCards.find((c) => c.classification === "pleasant");
      const sc = outCards.find((c) => c.classification === "scenic");
      if (
        fast &&
        isGreen(fast) &&
        (!pl ||
          (!ruleOneQualifies(pl) && // the supreme rule's pick is untouchable
            (fast.scores.pleasant ?? 0) > (pl.scores.pleasant ?? 0) + 8))
      ) {
        if (!pl) {
          outCards = outCards.map((c) =>
            c === fast ? { ...c, classification: "pleasant" as const } : c,
          );
        } else if (!sc) {
          outCards = outCards.map((c) =>
            c === fast
              ? { ...c, classification: "pleasant" as const }
              : c === pl
                ? {
                    ...c,
                    classification: "scenic" as const,
                    ...(isGreen(pl)
                      ? {}
                      : {
                          variantLabel: "Alternative",
                          variantSub: "Another good way",
                        }),
                  }
                : c,
          );
        }
        // else: all slots full — never drop, never reshuffle.
      }
    }

    // SCENIC HONESTY (Morgan, July 17: "scenic should not be 95% main
    // roads — if there is no scenic option, don't use it"). A Scenic card
    // measured mostly-main-road (>=60%) is a lie; drop the card and show
    // two honest routes instead. Epic scenic highways (PCH etc.) exempt —
    // they ARE the experience.
    {
      const s = outCards.find((c) => c.classification === "scenic");
      if (
        s &&
        // NOTE (Aug 8): our copy calls a route "Mostly on main roads" at
        // MOSTLY_MAIN_ROADS_HWY_PCT (45), but this gate only drops the Scenic
        // card at 60 — so a 45-59% route can wear Scenic while describing
        // itself as a highway route, which is exactly the card Morgan caught
        // on San Jose → Flagstaff. Aligning them to 45 ALSO drops legitimate
        // constructed corridors (the Briarcliff replay loses its Scenic card),
        // so the right answer is a judgement about deliberate routes, not a
        // number I should pick at midnight. Left at 60, documented, decided
        // with Morgan.
        (s.features.highwayPct ?? 0) >= 60 &&
        (s.route.epicHighwayMeters ?? 0) <= 1000
      ) {
        console.log(
          `[scenic] dropped dishonest Scenic card — ${Math.round(s.features.highwayPct ?? 0)}% main roads`,
        );
        outCards = outCards.filter((c) => c !== s);
      }
    }

    // NO TWINS (Morgan, approved July 15 night): Pleasant and Scenic on
    // essentially the same line (>=97% geometric overlap — strict, so the
    // Hackley pair whose last 0.1mi was the water bit still counts as
    // DIFFERENT) collapse to one card. The freed Scenic slot is backfilled
    // with the best DISTINCT remaining candidate — on La Crèche, the park
    // road the twins were crowding out.
    {
      const p = outCards.find((c) => c.classification === "pleasant");
      const s = outCards.find((c) => c.classification === "scenic");
      if (p && s) {
        const pb = geometryBuckets(p.route.coordinates);
        const sb = geometryBuckets(s.route.coordinates);
        const j = jaccard(pb, sb);
        // (Morgan, July 21: "if pleasant and scenic are the same, drop
        // scenic.") A pair can miss the strict 97% bar yet still be THE
        // SAME CARD in the user's eyes — same rounded minutes, same
        // rounded miles, mostly the same roads (the Swan Lake pair: two
        // near-identical 5 min / 0.7 mi lines, twin headlines). Collapse
        // those too: identical shown numbers + >=80% shared geometry.
        const sameShownNumbers =
          Math.round(p.route.duration / 60) === Math.round(s.route.duration / 60) &&
          (p.route.distance / 1609.344).toFixed(1) ===
            (s.route.distance / 1609.344).toFixed(1);
        if (j >= 0.97 || (j >= 0.8 && sameShownNumbers)) {
          const replacement = coherentScored
            .filter(
              (c) =>
                c.route !== p.route &&
                c.route !== s.route &&
                c.route !== direct.route &&
                jaccard(geometryBuckets(c.route.coordinates), pb) <
                  SLOT_DISTINCT_OVERLAP_THRESHOLD &&
                (calmEnough(c) || ruleOneQualifies(c)),
            )
            .sort(
              (a, b) =>
                rankScore(b) - rankScore(a) ||
                a.route.duration - b.route.duration,
            )[0];
          outCards = outCards.filter((c) => c !== s);
          if (replacement) {
            outCards = [
              ...outCards,
              {
                ...strip(replacement as ClassifiedRoute),
                classification: "scenic" as const,
              },
            ];
          }
        }
      }
    }

    // ---- THE PRIMARY PLEASANT CARD IS NOT AN "ALTERNATE" (Morgan, Aug 23) ----
    // "Once a route is selected as the primary Pleasant card, stale candidate-
    // list metadata must not make it appear to users as an Alternate."
    //
    // Deliberately narrow. It clears ONLY a label flagged as candidate-list
    // metadata, ONLY on the card that ends up as the primary Pleasant one.
    // Secondary cards keep their "Alternate"/"Alternative" names, and labels a
    // rule chose on purpose — the borderline Pleasant, the displaced card that
    // slides to Scenic — are untouched because they carry no flag. No routing,
    // ranking, supreme-rule, scenic-normalization or card-count behaviour
    // changes here; this is presentation only, after every decision is made.
    {
      const primaryPleasant = outCards.find((c) => c.classification === "pleasant");
      if (primaryPleasant?.candidateListLabel) {
        // eslint-disable-next-line no-console
        console.log(
          `[cards] cleared candidate-list label "${primaryPleasant.variantLabel}" from the ` +
          `primary Pleasant card (${getRouteSource(primaryPleasant.route) ?? "engine"}/` +
          `${Math.round(primaryPleasant.route.duration / 60)}m) — it is the Pleasant route now.`,
        );
        outCards = outCards.map((c) =>
          c === primaryPleasant
            ? { ...c, variantLabel: undefined, variantSub: undefined, candidateListLabel: undefined }
            : c,
        );
      }
    }

    // ---- FAST REALLY IS FASTEST (Morgan, Aug 23) ----
    // "If an eligible candidate has a shorter duration than the route labelled
    // `fastest`, the final card set is invalid."
    //
    // The Direct slot is seated on the pool minimum up front and no rule below
    // may take it, so this should be unreachable. It is here because rule 17
    // says a decision announces itself, and because an invariant nobody checks
    // is a comment. If this ever prints, the card set is wrong and the log
    // names the route that should have held Fast.
    {
      const first = outCards.find((c) => c.classification === "fastest");
      const quicker = first
        ? rawScored.filter((s) => s.route.duration < first.route.duration)
        : [];
      if (first && quicker.length > 0) {
        const best = quicker.reduce((a, b) => (b.route.duration < a.route.duration ? b : a));
        // eslint-disable-next-line no-console
        console.error(
          `[fast-invariant] VIOLATED — the Fast card is ` +
          `${getRouteSource(first.route) ?? "engine"}/${Math.round(first.route.duration / 60)}m ` +
          `but ${getRouteSource(best.route) ?? "engine"}/${Math.round(best.route.duration / 60)}m ` +
          `is quicker and was eligible. Fast must mean fastest.`,
        );
      }
    }
    return outCards;
  }
}

export function classifyRoutes(
  scored: ScoredRoute[],
  pleasantTolerance: number = 1.35,
  avoidHighways: boolean = false,
  /** When true (the fast path), suppress the "pleasant" label entirely.
   *  Without enriched OSM/POI data the pleasant pick is little more than
   *  a re-ranked fastest route — labeling it would mislead the user.
   *  We still return fastest + scenic so the UI has something to show
   *  while enrichment runs. */
  suppressPleasant: boolean = false,
  /** "Try a different route" support: any candidate whose geometry
   *  overlaps one of these previously-shown polylines by ≥60% is excluded
   *  from the pleasant pick, AND we then take the Nth-best remaining
   *  candidate (skipRank) so each click of "Try again" demonstrably
   *  changes the line on the map. */
  opts?: {
    excludeCoords?: LngLat[][];
    skipRank?: number;
    mode?: "driving" | "walking" | "biking";
    /** Minimum absolute Pleasant time budget in seconds. When set, the
     *  duration gate becomes `fastestDur + max(pleasantBudgetSec, 0.15 *
     *  fastestDur)`. Real-drive framing: a 5-min trip should still be open
     *  to an extra 7 min if that route is nicer (15% of 5 min is only 45s,
     *  not enough budget to switch roads). Long trips fall back to the
     *  percentage. */
    pleasantBudgetSec?: number;
    /** Absolute Pleasant time ceiling (seconds), held fixed across passes.
     *  Computed once from the initial fastest so a later faster Direct can't
     *  shrink the gate and evict a route that already qualified. Overrides
     *  `safeFastestDur + budget` when present. */
    pleasantTimeCapSec?: number;
    /** Google's polyline for the same trip. When set, any candidate whose
     *  geometry overlaps this polyline by ≥60% is dropped from the Pleasant
     *  pool entirely. Pleasant is supposed to be a visibly different road
     *  from Google — if every alternative IS Google's road, we fall back to
     *  surfacing it honestly rather than pretending to differentiate. */
    googleCoords?: LngLat[];
    /** (#127) OSM environment for the route corridor — when provided,
     *  any candidate whose internal segments detour into an OSM-tagged
     *  dead-end way (noexit=yes or service=driveway) is dropped from
     *  the pool BEFORE classification. Start/end addresses on dead-end
     *  streets are exempt (the exception lives inside hasDeadEndDetour). */
    osmEnv?: OsmEnvironment;
    /** PLEASANT LOOP: the user asked for a circle back to where they started.
     *  Set ONLY from explicit loop intent (Index passes it when the trip's
     *  destination is the Pleasant Loop) — never inferred from geometry, because
     *  inferring it would re-open the "the are loops" bug where an ordinary walk
     *  boxes around a block and passes itself off as a route.
     *
     *  A loop is a near-return, makes zero net progress, and its start and end
     *  are the same point — so it trips hasNearReturn, makesCoherentProgress and
     *  hasTightKnot by construction. Those three gates exist to catch loops that
     *  the user did NOT ask for; when the loop IS the request, they must stand
     *  down. Loop quality is enforced separately (zero-ramp standard, busy-road
     *  penalty, dirty-loop retry — all in Index's loopFilter). */
    isLoop?: boolean;
  },
): ClassifiedRoute[] {
  // ============================================================
  // (#124) NEW per-tolerance selector. Each tolerance returns up to
  // 2 ClassifiedRoute objects:
  //   • Scenic  (tol ≥ 1.30): top 2 by pleasantness; scenic-byway
  //                            survivors always rank first. No time cap.
  //   • Pleasant (1.0 < tol < 1.30): filter routes by max(25% over
  //                                   fastest, 10-min floor for trips
  //                                   < 20 min). Then top 2 by
  //                                   pleasantness within.
  //   • Direct  (tol ≤ 1.0): engine fastest + best-pleasantness
  //                            near-fastest (within 5%). Index.tsx
  //                            swaps card 1 for the Google polyline.
  //
  // Within-category distinctiveness: 2nd card must share ≤ 65% of
  // polyline with 1st. If no distinct 2nd exists, return 1.
  // ============================================================
  if (scored.length === 0) return [];

  // ---- Geometry dedupe: collapse near-identical alternatives. ----
  // Two-stage rule:
  //   (a) If two routes share ≥ 92% of their physical path AND have similar
  //       scenic scores (delta ≤ 15), they're effectively the same road —
  //       collapse to the higher-scoring one.
  //   (b) If they share that much path BUT one is meaningfully more scenic
  //       (scenic delta > 15), KEEP BOTH. The differing 20% is precisely
  //       what makes the alternative worth surfacing — collapsing it would
  //       hide a reservoir detour or a tree-lined sub-stretch entirely.
  // Real-world ride note: "if they share 80% of their journey, the one
  // with the more scenic 20% remaining should definitely be there."
  const SCENIC_KEEP_DELTA = 15;
  const buckets = scored.map((s) => geometryBuckets(s.route.coordinates));
  const keep: boolean[] = scored.map(() => true);
  for (let i = 0; i < scored.length; i++) {
    if (!keep[i]) continue;
    for (let j = i + 1; j < scored.length; j++) {
      if (!keep[j]) continue;
      if (jaccard(buckets[i], buckets[j]) >= 0.92) {
        const scenicDelta = Math.abs(scored[i].scores.scenic - scored[j].scores.scenic);
        if (scenicDelta > SCENIC_KEEP_DELTA) {
          // The two share most of their path but have meaningfully different
          // scenic value — both are worth surfacing. Skip the dedupe for
          // this pair.
          continue;
        }
        // Effectively the same road. Keep the one with the higher scenic
        // score (then higher pleasant, then faster) — under-weighting scenic
        // here was why we kept "boring" winners over identical-route
        // siblings whose unique stretch was the prettier one.
        const iScore = scored[i].scores.scenic * 2 + rankScore(scored[i]);
        const jScore = scored[j].scores.scenic * 2 + rankScore(scored[j]);
        const iWins =
          iScore > jScore ||
          (iScore === jScore && scored[i].route.duration <= scored[j].route.duration);
        if (iWins) keep[j] = false;
        else { keep[i] = false; break; }
      }
    }
  }
  const dedupedRawAll = scored.filter((_, i) => keep[i]);
  // NON-FINITE CANDIDATES ARE EXCLUDED FROM RANKING, NOT SCORED LOW
  // (Morgan, Aug 22: "a corrupt candidate should be impossible to select, not
  // merely given a low score").
  //
  // On 22 Aug a NaN-scored route WON the Pleasant slot on a real search,
  // because every NaN comparison is false and the sort simply carried it
  // through. A score of 0 makes that unlikely; removal makes it impossible.
  //
  // THE ONE EXCEPTION, AND WHY. If EVERY candidate is corrupt, removing them
  // all would return nothing at all — turning a scoring bug into a blank
  // screen, which is a worse failure than a bad card. In that case we keep the
  // pool, and the final finite guard in scoreRoute (which forces 0 and logs
  // console.error) is what stops a NaN propagating into the UI. Degradation,
  // not silence: the count is always logged.
  const finiteEnough = (x: ScoredRoute): boolean =>
    Number.isFinite(x.scores?.pleasant) && Number.isFinite(x.scores?.scenic);
  const corrupt = dedupedRawAll.filter((x) => !finiteEnough(x));
  if (corrupt.length) {
    // eslint-disable-next-line no-console
    console.error(
      `[pool] ${corrupt.length} of ${dedupedRawAll.length} candidate(s) had a NON-FINITE score and ` +
      `${corrupt.length === dedupedRawAll.length ? "were KEPT (every candidate was corrupt — refusing to return an empty pool)" : "were EXCLUDED from ranking"}. ` +
      `This is a bug upstream of scoring, not a data condition.`,
    );
    for (const x of corrupt) {
      recordRejection({
        stage: "dedupe", rule: "non-finite-score",
        candidate: x,
        roads: roadsOfSteps(x.route.steps),
        // `x.route.source` — no such property on RawRoute. The tag lives on
        // `__source` and is read through getRouteSource(), exactly as the
        // three other recordRejection sites in this file already do. Until
        // now this line recorded `undefined`, so every non-finite-score
        // rejection was logged with no provenance at all (rule 17: a decider
        // that drops a route must name its victim).
        source: getRouteSource(x.route),
        durationMin: Math.round(x.route.duration / 60),
      });
    }
  }
  const dedupedRaw = corrupt.length && corrupt.length < dedupedRawAll.length
    ? dedupedRawAll.filter(finiteEnough)
    : dedupedRawAll;

  // ---- Global loop/dead-end filter (#86, applied early). ----
  // Real-drive note: Morgan saw a loopy route surface as the Scenic
  // card with "+1 min" while the more-direct Pleasant card said "+2
  // min." The tortuosity/self-overlap filters used to live only in the
  // pleasant-pick branch, so loopy candidates still leaked into the
  // scenic + fastest picks above. Drop them here, before ANY
  // classification, so no card can ever show a loopy line.
  const {
    passes: passesCoherence,
    deadEndLines,
    tortuosityOfEarly,
    TORTUOSITY_GLOBAL_CAP,
    SELFOVERLAP_GLOBAL_CAP,
    RETRACE_CAP_M,
  } = makeCoherenceGate(opts);
  const dedupedClean = dedupedRaw.filter(passesCoherence);
  // STRICT (#115): we no longer fall back to the unfiltered pool when
  // every route is loopy. The old behavior produced the exact bug
  // Morgan reported — when Mapbox returns 3 loopy alternates, the
  // "better something than nothing" fallback surfaced them anyway.
  // Now if all candidates are loopy we keep an empty deduped, and
  // downstream surfaces "fastest only" instead — never a circle.
  // Edge case: when the LITERAL fastest route is loopy too (rare),
  // we keep it as the lone classified fastest entry so the user
  // still has SOMETHING; the pleasant pick path skips entirely.
  const deduped = dedupedClean.length > 0
    ? dedupedClean
    : dedupedRaw.slice(0, 1); // keep at most one (the fastest) so UI isn't blank
  // THE FUNNEL, NAMED (Morgan, Aug 8). San Jose → Flagstaff: the scenic call
  // receives all 15 scored routes — three of them curated scenic-road — and
  // the scenic slot log showed a pool of ONE. Everything upstream of ranking
  // is silent, so a candidate can die here with no confession at all. This
  // says exactly how many survive each gate, and names the sources lost to
  // the coherence filter (the one that reduces dedupedRaw → dedupedClean).
  if (dedupedRaw.length !== dedupedClean.length || scored.length !== dedupedRaw.length) {
    const lostSources = (from: ScoredRoute[], to: ScoredRoute[]) => {
      const kept = new Set(to.map((s) => s.route));
      const counts: Record<string, number> = {};
      for (const s of from) {
        if (kept.has(s.route)) continue;
        const k = getRouteSource(s.route) ?? "engine";
        counts[k] = (counts[k] ?? 0) + 1;
      }
      return Object.entries(counts).map(([k, n]) => `${k}:${n}`).join(" ") || "(none)";
    };
    console.log(
      `[pool] scored=${scored.length} → dedupe=${dedupedRaw.length} (lost ${lostSources(scored, dedupedRaw)})` +
      ` → coherence=${dedupedClean.length} (lost ${lostSources(dedupedRaw, dedupedClean)})`,
    );
  }
  // ATTRITION ACCOUNTING (Morgan, Aug 22). These blocks used to sit INSIDE the
  // log guard above, which only fires when something was lost. A corridor that
  // lost nothing at dedupe or coherence therefore recorded no stage at all —
  // and formatAttritionSummary reads survivors as `stages.at(-1)?.leaving ?? 0`,
  // i.e. ZERO for those corridors. That understates "reached ranking" and
  // overstates the coherence kill rate. Instrumentation only: no candidate's
  // fate changes here.
  recordStage("dedupe", scored.length, dedupedRaw.length);
  recordStage("coherence", dedupedRaw.length, dedupedClean.length);
  // EVERY candidate measured, pass or fail (Morgan, Aug 22: sweep selfOverlap
  // and tortuosity over a larger corridor set). The rejection list shows only
  // the tail above the line; a threshold can only be judged against the whole
  // distribution. Costs two geometry passes per candidate and runs ONLY when
  // attrition is enabled, which production never does.
  // `passed` is THIS rule's own verdict (value < its own threshold), never
  // "survived coherence" — a candidate killed by uTurn may have perfectly
  // sane tortuosity, and counting it as a tortuosity failure would invent a
  // failure rate that gate never had.
  if (attritionEnabled()) {
    for (const x of dedupedRaw) {
      const tortV = tortuosityOfEarly(x.route.coordinates);
      const overV = selfOverlapFraction(x.route.coordinates);
      const roads = roadsOfSteps(x.route.steps);
      recordMeasurement({
        rule: "tortuosity", value: tortV, threshold: TORTUOSITY_GLOBAL_CAP,
        passed: tortV < TORTUOSITY_GLOBAL_CAP, roads,
      });
      recordMeasurement({
        rule: "selfOverlap", value: overV, threshold: SELFOVERLAP_GLOBAL_CAP,
        passed: overV < SELFOVERLAP_GLOBAL_CAP, roads,
      });
    }
  }
    {
      // Dedupe is a black box in the log line above — name what it merged.
      const kept = new Set(dedupedRaw.map((x) => x.route));
      for (const x of scored) {
        if (kept.has(x.route)) continue;
        recordRejection({
          stage: "dedupe", rule: "duplicate-geometry",
          candidate: x,
          roads: roadsOfSteps(x.route.steps),
          source: getRouteSource(x.route) ?? "engine",
          durationMin: x.route.duration / 60,
        });
      }
    }
    // WHICH GATE (Morgan, Aug 8): on San Jose → Flagstaff the coherence block
    // rejected 13 of 14 candidates INCLUDING mapbox-direct — a straight run
    // down I-40 is not incoherent, so a check is misfiring at road-trip
    // length and the app has been limping on the keep-one fallback. Attribute
    // each rejection to the first gate that failed it.
    {
      const kept = new Set(dedupedClean.map((s) => s.route));
      const blame: Record<string, number> = {};
      for (const s of dedupedRaw) {
        if (kept.has(s.route)) continue;
        const c = s.route.coordinates;
        // Morgan, Aug 22: "If `coherence` rejects something for self-overlap,
        // tortuosity, backtracking, etc., show the actual measurement,
        // threshold, and route it killed." Same predicates, same order — the
        // measured value is now carried out alongside the verdict.
        const tort = tortuosityOfEarly(c);
        const overlap = selfOverlapFraction(c);
        let why: string;
        let measured: number | undefined;
        let threshold: number | undefined;
        if (tort >= TORTUOSITY_GLOBAL_CAP) {
          why = "tortuosity"; measured = tort; threshold = TORTUOSITY_GLOBAL_CAP;
        } else if (overlap >= SELFOVERLAP_GLOBAL_CAP) {
          why = "selfOverlap"; measured = overlap; threshold = SELFOVERLAP_GLOBAL_CAP;
        } else if (!makesCoherentProgress(c, c[0], c[c.length - 1], { maxBacktrackFraction: 0.30 })) {
          why = "backtrack"; threshold = 0.30;
        } else if (hasUTurn(c)) { why = "uTurn"; }
        else if (hasNearReturn(c)) { why = "nearReturn"; }
        else if (hasNeighborhoodDetour(c)) { why = "neighborhoodDetour"; }
        else if (hasDeadEndDetour(c, deadEndLines)) { why = "deadEnd"; }
        else if (hasTightKnot(c)) { why = "tightKnot"; }
        else if (retracedMeters(c) >= RETRACE_CAP_M) {
          why = "retrace"; measured = retracedMeters(c); threshold = RETRACE_CAP_M;
        }
        else { why = "earlier-gate"; }
        blame[why] = (blame[why] ?? 0) + 1;
        recordRejection({
          stage: "coherence", rule: why, measured, threshold,
          candidate: s,
          roads: roadsOfSteps(s.route.steps),
          source: getRouteSource(s.route) ?? "engine",
          durationMin: s.route.duration / 60,
        });
      }
      const summary = Object.entries(blame).map(([k, n]) => `${k}:${n}`).join(" ");
      if (summary) console.log(`[pool] coherence rejects by gate — ${summary}`);
    }
  // The keep-one fallback above puts a candidate BACK into the pool after
  // coherence emptied it. Record it as its own stage so a survivor that
  // reappears is accounted for rather than materialising silently.
  if (dedupedClean.length === 0 && deduped.length > 0) {
    recordStage("keep-one-fallback", 0, deduped.length);
  }

  const fastestDur = Math.min(...deduped.map((s) => s.route.duration));
  const fastest = deduped.reduce((a, b) =>
    a.route.duration <= b.route.duration ? a : b,
  );

  // ============================================================
  // (#124) NEW per-tolerance selection. Drops in here as the entry
  // path; the long legacy body below is unreachable and will be
  // cleaned up in a follow-up pass. Keeping it temporarily so the
  // file remains diff-friendly during the rewrite.
  // ============================================================
  {
    // Step A: UNSAFE filter (#124). Routes flagged as physically
    // dangerous in the user's travel mode (walking on motorway
    // shoulder, biking on high-speed trunk with no protection)
    // get dropped from every category's pool. If literally every
    // candidate is unsafe (very rare — only if Mapbox returned
    // nothing better), we keep one so the UI isn't blank, and
    // downstream framing should handle it gracefully.
    const safePool = deduped.filter((s) => !s.unsafe);
    // When EVERY candidate is unsafe (rare — e.g. the only path to the
    // destination touches an arterial), don't hand back an arbitrary route:
    // keep the SAFEST one. The pleasant score already absorbs the heavy
    // walk-safety penalty, so the highest-pleasant route is the least
    // dangerous available.
    const poolNew =
      safePool.length > 0
        ? safePool
        : deduped.length > 0
          ? [[...deduped].sort((a, b) => rankScore(b) - rankScore(a))[0]]
          : [];

    if (poolNew.length === 0) return [];

    // Apply the existing exclude-coords / skipRank "Try again" rule
    // to the pool before category selection. Same semantics as the
    // legacy code path.
    let activePool = poolNew;
    const excludeCoordsList = opts?.excludeCoords ?? [];
    if (excludeCoordsList.length > 0) {
      const excludeBuckets = excludeCoordsList.map((c) => geometryBuckets(c));
      const filtered = activePool.filter((s) => {
        const sb = geometryBuckets(s.route.coordinates);
        return !excludeBuckets.some((eb) => jaccard(sb, eb) >= 0.6);
      });
      if (filtered.length > 0) activePool = filtered;
    }
    const skipRank = Math.max(0, opts?.skipRank ?? 0);

    // The fastest within the (post-unsafe) pool — used by Direct and
    // by Pleasant's time-cap math.
    const safeFastestDur = Math.min(...activePool.map((s) => s.route.duration));
    const safeFastest = activePool.reduce((a, b) =>
      a.route.duration <= b.route.duration ? a : b,
    );

    // (#142) The LITERAL engine fastest — taken from the raw scored
    // input BEFORE any filtering. Used for honest `sameAsFastest`
    // detection: a card should only claim "most direct here" / "same
    // as direct" if its polyline is genuinely close to the engine's
    // #1 fastest route, NOT just to the fastest-among-survivors.
    //
    // Background: Pleasant's time + avoidHighways filters can remove
    // the literal engine fastest, leaving a slower (and possibly
    // loopier) alternative as "fastest survivor." Surfacing that
    // alternative with `sameAsFastest=true` mislabels it. Compare
    // polyline geometry to the literal engine fastest instead, so
    // sameAsFastest only fires when the surfaced route really IS
    // (essentially) the same path the engine called fastest.
    const engineFastest = scored.length > 0
      ? scored.reduce((a, b) => a.route.duration <= b.route.duration ? a : b)
      : null;
    const engineFastestBuckets = engineFastest
      ? geometryBuckets(engineFastest.route.coordinates)
      : null;
    const SAME_AS_FASTEST_JACCARD = 0.85;
    // RULE 3, AS WRITTEN: "IDENTICAL routes only — not 'similar,' not a
    // percentage overlap I invented. Same road = one card." (Morgan, CLAUDE.md,
    // and again Aug 8: "i already asked for that overlap threshold to be
    // removed.")
    //
    // This used a jaccard >= 0.85 bucket overlap — precisely the invented
    // percentage the rule bans — and on San Jose → Flagstaff it did exactly the
    // damage the rule exists to prevent: valhalla-balanced, 12h31 and 750 miles
    // with 118 miles of completely different road, overlapped I-40 by more than
    // 85% of its SHAPE, got stamped "same as fastest", and the no-hollow filter
    // threw the Pleasant card away. The engine had produced three cards; the
    // driver saw two.
    //
    // routesAreSameRoad (routeTwins.ts, built this morning for this exact rule)
    // is geometry-first IDENTITY: comparable length, then mean deviation
    // point-to-segment with a scale-aware tolerance. Same road means same road.
    const isSameAsEngineFastest = (s: ScoredRoute): boolean => {
      if (!engineFastest) return false;
      if (s === engineFastest || s.route === engineFastest.route) return true;
      return routesAreSameRoad(
        { coordinates: s.route.coordinates, distance: s.route.distance },
        {
          coordinates: engineFastest.route.coordinates,
          distance: engineFastest.route.distance,
        },
      );
    };

    /** Pick a distinct pair from a ranked list. The 2nd card must
     *  share ≤ 65% of polyline with the 1st (#124 35% distinctiveness
     *  threshold — human-perceived difference, not mathematical
     *  uniqueness). If no 2nd card qualifies, return just the 1st. */
    const pickDistinctPair = (ranked: ScoredRoute[]): ScoredRoute[] => {
      if (ranked.length === 0) return [];
      const first = ranked[Math.min(skipRank, ranked.length - 1)];
      const fb = geometryBuckets(first.route.coordinates);
      for (let i = 0; i < ranked.length; i++) {
        if (ranked[i] === first) continue;
        const overlap = jaccard(fb, geometryBuckets(ranked[i].route.coordinates));
        if (overlap <= 0.65) return [first, ranked[i]];
      }
      return [first];
    };

    // ---- SCENIC GUARANTEE — a Scenic card that does NOT live in Pleasant. --
    // Morgan's rule: "we should just have a scenic card, not have it live in
    // pleasant." A route we DELIBERATELY discovered as the long way (tagged
    // "scenic-corridor" — the along-corridor / coastline / scenic-tag passes)
    // is the hour-plus, no-time-limit Scenic offer. It is exempt from every
    // time cap the tolerance branches apply (the big extra time IS the offer).
    // We append it to whatever a tolerance branch decided, as long as it isn't
    // already on a card and is meaningfully longer than the direct route. This
    // is why Maine→Boston (no Pleasant pick survives the cap) still gets a
    // Scenic "long way" card instead of "no calmer route on this trip."
    const isScenicCorridorSrc = (s: ScoredRoute): boolean =>
      getRouteSource(s.route) === "scenic-corridor";
    const withScenic = (cards: ClassifiedRoute[]): ClassifiedRoute[] => {
      if (cards.some((c) => c.classification === "scenic")) return cards;
      const corridor = activePool.filter(isScenicCorridorSrc);
      const directRoute =
        cards.find((c) => c.classification === "fastest")?.route ?? safeFastest.route;
      const pick = corridor
        .filter((s) => !cards.some((c) => c.route === s.route))
        .filter((s) => s.route.duration > directRoute.duration * 1.15)
        .sort((a, b) => a.route.duration - b.route.duration)[0];
      if (pick) {
        cards.push({ ...pick, classification: "scenic", ...categorizePleasant(pick) });
      }
      return cards;
    };

    // ---- DIRECT tolerance (≤ 1.0): fastest + best-pleasant near-fastest. ----
    // Card 1: the engine's fastest route. Index.tsx swaps this for
    //   Google's polyline when Google's payload is available.
    // Card 2: best-pleasant among routes within 5% of fastest's
    //   duration. Skipped if no distinct alternative exists.
    if (pleasantTolerance <= 1.0) {
      const NEAR_FAST = 1.05;
      const nearFastest = activePool
        .filter((s) => s.route !== safeFastest.route)
        .filter((s) => s.route.duration <= safeFastestDur * NEAR_FAST)
        .sort((a, b) => rankScore(b) - rankScore(a));
      const fb = geometryBuckets(safeFastest.route.coordinates);
      const distinctNear = nearFastest.find((s) =>
        jaccard(fb, geometryBuckets(s.route.coordinates)) <= 0.65,
      );
      const out: ClassifiedRoute[] = [];
      out.push({ ...safeFastest, classification: "fastest" });
      if (distinctNear) {
        out.push({
          ...distinctNear,
          classification: "fastest",
          variantLabel: "Quicker pleasant option",
        });
      }
      return withScenic(out);
    }

    // ---- PLEASANT tolerance (1.0 < tol < 1.30): time-cap + top 2 by pleasant. ----
    // Time cap: max(25% over fastest, 10-min floor for trips < 20 min).
    // Pleasantness is now independent of time — selection ranks by
    // raw pleasant score within the eligible (time-capped) pool.
    if (pleasantTolerance < 1.30) {
      // DECOUPLE the budget from the moving fastest. The budget is computed ONCE
      // up front (generateRoutes passes opts.pleasantBudgetSec, from the initial
      // fastest) and must NOT shrink when a faster Direct folds in later. Real
      // bug (Briarcliff→Rhinebeck): a 116-min Pleasant route qualified at first
      // paint (Direct 90m → cap 120m), then Valhalla added a 78-min route, the
      // recomputed cap dropped to 104m, and the Pleasant route was kicked out a
      // second later. Using the stable passed budget keeps it qualified.
      const budget = opts?.pleasantBudgetSec ?? pleasantBudgetSeconds(safeFastestDur);
      // Lock the ceiling to the INITIAL fastest (passed once from generateRoutes
      // as pleasantTimeCapSec), not the live pool fastest. Otherwise both terms
      // move: when Valhalla folds in a 78m route, safeFastestDur drops from 80m
      // to 78m and the +20% gate shrinks from 96m to 93.6m — evicting the 95m
      // Albany Post Road route (which is +18.75%, genuinely within 20% of the
      // 80m Direct the user actually sees). The cap must reflect the displayed
      // Direct and stay put. Fall back to the live computation when unset.
      const timeCap = opts?.pleasantTimeCapSec ?? (safeFastestDur + budget);
      const eligible = activePool.filter((s) => s.route.duration <= timeCap);
      // A "calmer alternative" is RELATIVE to Direct, not an absolute 25%.
      // THE BUG (Briarcliff→Rhinebeck, found via replay harness): when every
      // in-budget route is over 25% highway, this declared "no alternative" and
      // flagged the genuinely-calmer pick as sameAsFastest — dropping it. But a
      // 70%-highway route IS a real Pleasant pick when Direct is ~100% highway.
      // So count a route as a calm alternative if it's under 25% OR meaningfully
      // (≥15pp) less highway than the fastest.
      // A route only makes a highway CLAIM if we have road-class evidence for
      // it. Hoisted above the Direct reference below, which needs the same test.
      const hasRoadClassEvidence = (s: ScoredRoute): boolean => {
        const r = s.route as {
          roadClassMeters?: Record<string, number>;
          motorwayMeters?: number;
        };
        return (r.motorwayMeters ?? 0) > 0 ||
          Object.keys(r.roadClassMeters ?? {}).length > 0;
      };
      // THE YARDSTICK HAS TO BE MEASURED TOO (Morgan, Aug 9).
      //
      // The rule below — a candidate can't claim "calmer" on a number we never
      // measured — was only ever applied to the CANDIDATES. The reference it
      // compares them against is engineFastest, and that can itself be the
      // Google route, which carries no road-class data at all. Briarcliff →
      // Rhinebeck showed it plainly the moment Google joined the pool:
      //
      //   calm(avoidHighways=true, directHwy=100%)=2   ← before
      //   calm(avoidHighways=true, directHwy=0%)=3     ← after
      //
      // Direct did not become a back road. We simply started measuring against
      // a route with no measurements. Every "≥15pp less highway than Direct"
      // comparison is then against zero, which nothing can beat, and the
      // off-highway-miles test compares against a Direct that reads as 100%
      // small roads. Same defect as the Pleasant slot theft, one layer over.
      //
      // So the yardstick is the fastest route we actually measured. Duration
      // still comes from engineFastest further down — Google's timings are
      // fine; it is only the road-class numbers that are missing.
      const directRef =
        engineFastest && hasRoadClassEvidence(engineFastest)
          ? engineFastest
          : (activePool
              .filter(hasRoadClassEvidence)
              .sort((a, b) => a.route.duration - b.route.duration)[0] ?? engineFastest ?? null);
      if (engineFastest && directRef && directRef !== engineFastest) {
        console.log(
          `[pleasant] Direct reference is unmeasured (${getRouteSource(engineFastest.route) ?? "engine"})` +
          ` — measuring against ${getRouteSource(directRef.route) ?? "engine"}` +
          ` (hwy ${Math.round(directRef.features.highwayPct ?? 0)}%)`,
        );
      }
      const directHwyPct = directRef?.features.highwayPct ?? 100;
      // A route that runs a scenic 'epic' parkway (the Taconic, Blue Ridge, etc.)
      // counts as a calm/pleasant alternative even though it reads as 'highway' by
      // road class — that scenic drive IS the experience (Morgan: "not all highways
      // are the same"). Without this, Briarcliff→Rhinebeck's Taconic route was
      // filtered out of the Pleasant pool and the slot collapsed.
      const runsScenicParkway = (s: ScoredRoute) => ((s.route.epicHighwayMeters ?? 0) > 1000 &&
       (!ABLATION.epicProportional ||
        (s.route.epicHighwayMeters ?? 0) >= (s.route.distance ?? 0) * 0.25));
      // PROVENANCE BEATS ROAD CLASS (rule 16: "Filters must know WHO BUILT the
      // route; geometry can't judge taste"). A CURATED drive — a road a human
      // chose on purpose — is a calm alternative by definition, whatever OSM
      // calls its road class. San Jose → Flagstaff, Aug 8: the engine injected
      // Historic Route 66 (Topock/Oatman/Kingman/Seligman and Ash Fork/
      // Williams/Flagstaff) — the definitive great drive into Flagstaff — and
      // the Pleasant slot threw it out for reading as "highway," leaving the
      // driver a 11h interstate and a 22h scenic-corridor and nothing in
      // between. That is the exact complaint two drivers wrote in about.
      // Same trust class as the epic-parkway exemption directly above.
      const isCuratedDrive = (s: ScoredRoute) => {
        const src = getRouteSource(s.route);
        return src === "scenic-road" || src === "coastal-pch";
      };
      // ZERO PERCENT HIGHWAY IS NOT A CLAIM, IT IS A MISSING FIELD (Morgan,
      // Aug 8). San Jose → Flagstaff:
      //   [pleasant] ... calm(directHwy=100%)=1 · top: google-direct/677m/hwy0%
      // google-direct reports 0% highway because Google's response carries no
      // road-class metadata at all — not because it avoids highways. It is the
      // SAME I-40 as Direct. So the calm filter crowned the one candidate it
      // knew nothing about, the Pleasant card collapsed onto Direct as a twin,
      // and the driver got a single card.
      //
      // A route only makes a highway CLAIM if we have road-class evidence for
      // it. Without evidence it can still win Pleasant on pleasantness — it
      // just cannot qualify as "calmer" on a number we never measured.
      // (hasRoadClassEvidence is defined above, with the Direct reference that
      // needs the same test.)
      // MILES OFF THE BIG ROAD, NOT PERCENTAGE POINTS (Morgan, Aug 8, looking
      // at the Flagstaff cards: "THIS is the one that should be pleasant").
      // valhalla-balanced was 86% highway against Direct's 100% — fourteen
      // points, one short of the 15-point rule, so the Pleasant slot collapsed
      // onto Direct and the middle route got pushed into Scenic instead.
      //
      // But 14% of an 841-mile drive is a hundred and eighteen miles off the
      // interstate. The percentage is small only because the trip is enormous;
      // what the driver experiences is the distance. So a route also qualifies
      // as calmer when it puts MEANINGFULLY MORE MILES on smaller roads than
      // Direct does — scaled to the trip, floored at 2 km so a half-kilometre
      // of side street never qualifies on a city hop.
      const offHighwayM = (s: ScoredRoute) =>
        s.route.distance * (1 - Math.min(100, Math.max(0, s.features.highwayPct ?? 0)) / 100);
      const directOffHighwayM = directRef
        ? directRef.route.distance *
          (1 - Math.min(100, Math.max(0, directHwyPct)) / 100)
        : 0;
      const directDistanceM = directRef?.route.distance ?? 0;
      const calmDistanceFloorM = Math.max(2_000, directDistanceM * 0.03);
      const putsRealMilesOffHighway = (s: ScoredRoute) =>
        hasRoadClassEvidence(s) &&
        offHighwayM(s) - directOffHighwayM >= calmDistanceFloorM;
      const isCalmerAlt = (s: ScoredRoute) =>
        (hasRoadClassEvidence(s) &&
          ((s.features.highwayPct ?? 0) < 25 ||
            (s.features.highwayPct ?? 0) <= directHwyPct - 15)) ||
        putsRealMilesOffHighway(s) ||
        runsScenicParkway(s) ||
        isCuratedDrive(s);
      const noHighway = avoidHighways ? eligible.filter(isCalmerAlt) : eligible;
      // Track whether we fell back to the unfiltered eligible set because
      // every in-budget option was highway-heavy. In that case, the
      // Pleasant slot is structurally collapsed onto the fastest path
      // and must carry the `sameAsFastest` flag so the UI frames it
      // honestly ("no calmer route here") rather than pretending it
      // found something meaningfully different.
      const noAlternativeAvailable = avoidHighways && noHighway.length === 0;
      const pleasantPoolRaw = noHighway.length > 0 ? noHighway : eligible;
      // A ROUTE CANNOT BE NAMED PLEASANT ON DATA NOBODY MEASURED.
      //
      // Morgan's rule, from the Hackley log (`pleasant/src=google-direct/13m/
      // 4.5mi`) — but it was only ever enforced in the DISPLAY, as isUnmeasured
      // in Index.tsx. The engine still let an unmeasured route win its Pleasant
      // slot, and the two layers disagreeing is what emptied the card on
      // Briarcliff → Rhinebeck. The [slots] fate log caught it exactly:
      //
      //   before: [fastest/unknown pleasant/mapbox-pleasant scenic/scenic-road]
      //           → pleasant=mapbox-pleasant/116m
      //   after:  [fastest/unknown pleasant/google-direct  scenic/scenic-road]
      //           → pleasant=none
      //
      // Google's candidate carries no roadClassMeters and no step classes, so
      // every highway measure reads 0% and it looks maximally calm — it took
      // the slot from mapbox-pleasant/116m the moment it joined the pool at
      // 13.5s. The display then refused to name an unmeasured route Pleasant,
      // correctly, and the driver was left with two cards. The good route was
      // there the whole time; it had just been evicted by a route with no data.
      //
      // Enforcing the same rule here means the engine picks the best MEASURED
      // route, which is the one the display will accept. Google keeps the
      // Direct slot, which is what it honestly is.
      // The predicate is Index.tsx's isUnmeasured, character for character.
      // NOT hasRoadClassEvidence above: that one demands motorwayMeters > 0,
      // which a genuinely calm route legitimately fails — it is the right test
      // for "does this route make a HIGHWAY claim", the wrong one for "did
      // anybody measure this route at all". Using it here emptied the pool for
      // every fixture without motorway metres and cost 4 tests. Two layers
      // asking the same question have to ask it the same way, which was the
      // entire point of the fix.
      const isMeasured = (s: ScoredRoute): boolean => {
        const r = s.route as {
          roadClassMeters?: Record<string, number>;
          steps?: Array<{ classes?: string[] }>;
        };
        return Object.keys(r.roadClassMeters ?? {}).length > 0 ||
          (r.steps ?? []).some((st) => (st.classes ?? []).length > 0);
      };
      const measuredOnly = pleasantPoolRaw.filter(isMeasured);
      if (measuredOnly.length !== pleasantPoolRaw.length) {
        const lost = pleasantPoolRaw
          .filter((s) => !isMeasured(s))
          .map((s) => getRouteSource(s.route) ?? "engine");
        console.log(
          `[pleasant] unmeasured routes cannot be named Pleasant — dropped ${lost.join(" ")}` +
          ` (${measuredOnly.length} measured left)`,
        );
      }
      // Never let this empty the slot: if NOTHING carries road data (offline
      // fixtures, an engine outage), fall back to the unfiltered pool rather
      // than deleting the card. The rule exists to stop an unmeasured route
      // BEATING a measured one, not to punish a trip where nobody has data.
      const pleasantPool = measuredOnly.length > 0 ? measuredOnly : pleasantPoolRaw;
      // Hand the display the same ranked set (see pleasantPoolSnapshot).
      // classifyThreeCards runs this three times — 1.0/avoidHighways=false for
      // Direct, 1.20/true for Pleasant, Infinity/true for Scenic. Only the
      // Pleasant pass is the set the display's Pleasant slot should see, so
      // record on the finite avoid-highways pass and let the others alone.
      if (avoidHighways && Number.isFinite(pleasantTolerance)) {
        recordPleasantPool(pleasantPool.slice().sort((a, b) => rankScore(b) - rankScore(a)));
      }

      // CONFESSION (rule 17 — "if a new decider can drop or demote a route, it
      // MUST log a one-line confession"). The Pleasant slot was the one decider
      // with NO fate log: on San Jose → Flagstaff (Aug 8) the console said
      // "reclassified cards: fastest, pleasant, scenic" and then showed only
      // Fast + Scenic, with nothing to say which gate ate the middle card.
      // Prints the whole funnel — pool → time cap → calm filter — and the three
      // best survivors with the numbers each gate judges them on.
      {
        const mins = (s: number) => `${Math.round(s / 60)}m`;
        const top = pleasantPool
          .slice()
          .sort((a, b) => rankScore(b) - rankScore(a))
          .slice(0, 3)
          .map((s) =>
            `${getRouteSource(s.route) ?? "engine"}/${mins(s.route.duration)}/` +
            `hwy${hasRoadClassEvidence(s) ? `${Math.round(s.features.highwayPct ?? 0)}%` : "?"}` +
            `/p${Math.round(s.scores.pleasant)}`,
          )
          .join("  |  ");
        const curatedIn = pleasantPool.filter(isCuratedDrive).length;
        console.log(
          `[pleasant] pool=${activePool.length} → cap ${mins(timeCap)} → eligible=${eligible.length}` +
          ` → calm(avoidHighways=${avoidHighways}, directHwy=${Math.round(directHwyPct)}%)=${noHighway.length}` +
          `${curatedIn > 0 ? ` (${curatedIn} curated kept by provenance)` : ""}` +
          `${noAlternativeAvailable ? " ⚠ NO CALMER ALTERNATIVE — slot collapses onto Direct" : ""}` +
          `${top ? ` · top: ${top}` : " · top: (empty)"}`,
        );
      }

      if (pleasantPool.length === 0) {
        // Honesty: nothing fit the time cap. Surface the fastest
        // safe route as a single card so the user gets something —
        // plus the discovered "long way" Scenic card if one exists.
        return withScenic([{ ...safeFastest, classification: "fastest", sameAsFastest: true }]);
      }
      // Score-bar guard: don't surface a route that scored very low
      // as a "Pleasant" pick — the label would mislead the user. If
      // the top eligible's pleasant score is below the bar (and isn't
      // visibly different from fastest), fall back to fastest-with-flag.
      // Rank by pleasant score, but break ties with the GROUND-TRUTH calm
      // signal — lower highway %. The enriched pleasant score is flat (~23 for
      // everything) whenever the OSM corridor fetch fails/times out on long
      // trips, so without this the engine picks an arbitrary tied route — often
      // a highway one, which then fails the "calm enough" gate and the Pleasant
      // slot collapses. Highway % comes straight from Mapbox and is always
      // reliable, so it's the right tiebreaker: the genuinely calmer road wins.
      // DIRECTNESS CURVE — the Pleasant slot is "reasonably fast but pleasant"
      // (Morgan's original three-route vision), so within the eligible pool a
      // route pays for the time it costs. Bill Schaub, Aug 7: "much too far off
      // from a direct route… there could be better routes that are not too
      // long." Mikolai Altenberg, Aug 8: San Jose→Flagstaff "nearly doubled the
      // trip time" while ignoring a nicer near-direct option.
      //
      // Shape: free up to +15% over the displayed Direct, gentle to +40%, steep
      // past that. Points are on the same scale as scores.pleasant, so a
      // genuinely lovelier route still wins — it just has to be lovelier by
      // more than the detour costs. This is a RANKING lean inside a pool that
      // already passed the time cap, NOT a new ceiling: nothing is dropped
      // here, and the Scenic slot (rule 1, no cap on curated) never sees it.
      // The DISPLAYED Direct is the yardstick — the same one the time cap is
      // locked to, so the percentage in the log matches the "+X hrs" the card
      // shows. Falls back to the pool's fastest when the engine pool is empty.
      const directDurRef = engineFastest?.route.duration ?? safeFastestDur;
      const directnessPenalty = (s: ScoredRoute): number => {
        if (!(directDurRef > 0)) return 0;
        const over = s.route.duration / directDurRef - 1;
        if (over <= 0.15) return 0;
        const gentle = Math.min(over, 0.40) - 0.15; // 0 … 0.25
        const steep = Math.max(0, over - 0.40);
        return gentle * 40 + steep * 120;
      };
      const effectivePleasant = (s: ScoredRoute) =>
        rankScore(s) - directnessPenalty(s);
      const ranked = pleasantPool.slice().sort(
        (a, b) =>
          // Scenic-parkway routes (the Taconic etc.) win the Pleasant slot first —
          // that scenic drive is the whole point, even a touch slower than the
          // fastest non-scenic alternative.
          ((runsScenicParkway(b) ? 1 : 0) - (runsScenicParkway(a) ? 1 : 0)) ||
          (effectivePleasant(b) - effectivePleasant(a)) ||
          ((a.features.highwayPct ?? 100) - (b.features.highwayPct ?? 100)),
      );
      // CONFESSION (rule 17): say so whenever the curve changed the winner.
      if (ranked.length > 1) {
        const rawTop = pleasantPool.slice().sort(
          (a, b) =>
            ((runsScenicParkway(b) ? 1 : 0) - (runsScenicParkway(a) ? 1 : 0)) ||
            (rankScore(b) - rankScore(a)),
        )[0];
        if (rawTop && rawTop !== ranked[0]) {
          console.log(
            `[directness] pleasant slot: preferred ${getRouteSource(ranked[0].route) ?? "engine"} ` +
            `(+${Math.round((ranked[0].route.duration / directDurRef - 1) * 100)}%, ` +
            `pleasant ${ranked[0].scores.pleasant.toFixed(0)}) over ` +
            `${getRouteSource(rawTop.route) ?? "engine"} ` +
            `(+${Math.round((rawTop.route.duration / directDurRef - 1) * 100)}%, ` +
            `pleasant ${rawTop.scores.pleasant.toFixed(0)})`,
          );
        }
      }
      // Recording only — nothing below reads this. Written after `ranked` so it
      // captures the same values the sort above compared, in the same order.
      if (attritionEnabled()) {
        const __seq = ++SEL_SEQ;
        PLEASANT_SELECTIONS.push({
          seq: __seq,
          // classifyThreeCards distinguishes its three invocations by tolerance:
          // 1.0 direct, 1.20 pleasant, Infinity scenic.
          pass: pleasantTolerance === 1.0 ? "direct"
              : Number.isFinite(pleasantTolerance) ? `pleasant(tol ${pleasantTolerance})`
              : "scenic",
          poolSize: pleasantPool.length,
          top: ranked[0] ? roadsOfSteps(ranked[0].route.steps) : null,
          contenders: ranked.map((s2) => ({
            route: s2.route,
            roads: roadsOfSteps(s2.route.steps),
            preFloor: (s2.scores as { pleasantPreFloor?: number }).pleasantPreFloor,
            display: s2.scores.pleasant,
            directness: directnessPenalty(s2),
            effective: effectivePleasant(s2),
            scenicParkway: runsScenicParkway(s2),
            highwayPct: s2.features.highwayPct ?? 100,
            durationMin: Math.round(s2.route.duration / 60),
          })),
        });
      }
      const pair = pickDistinctPair(ranked);

      // (#143 reverted — the "Pleasant must beat Direct by meaningful
      // margin" check is the right concept but my implementation broke
      // 3 integration tests where Pleasant correctly surfaces a
      // backroad over the Taconic. The gain calculation was firing
      // wrongly. Reverting until I can debug what's actually happening
      // in those tests — the model needs to stay correct on the
      // Taconic-vs-backroad promise.)
      if (
        !suppressPleasant && pair.length === 0
      ) {
        return withScenic([{ ...safeFastest, classification: "fastest", sameAsFastest: true }]);
      }
      return withScenic(pair.map((s, i) => {
        // (#142) Honest sameAsFastest detection. Two conditions:
        //  (a) Pool collapsed because no calmer alternative was available
        //      (avoidHighways AND no non-highway candidate survived).
        //  (b) The surfaced route's polyline matches the LITERAL engine
        //      fastest (jaccard >= 0.85), not just the filtered-survivor
        //      fastest. This is what makes the "Most direct here" label
        //      truthful — we only claim "same as direct" when the route
        //      really is geometrically the engine's #1 fastest path.
        //      Old logic compared to safeFastest (post-filter), which
        //      mislabeled loopy alternatives as direct.
        const collapsed = noAlternativeAvailable || isSameAsEngineFastest(s);
        return {
          ...s,
          classification: "pleasant" as const,
          ...categorizePleasant(s),
          ...(collapsed ? { sameAsFastest: true as const } : {}),
          ...(i === 1 ? { variantLabel: "Alternate", variantSub: "A different pleasant path", candidateListLabel: true as const } : {}),
        };
      }));
    }

    // ---- SCENIC tolerance (≥ 1.30): byway-first then pleasant, distinct, no time cap. ----
    // Scenic byway designation wins over all other ranking factors —
    // among safety-survivors, a route that touches an OSM-tagged
    // scenic byway always outranks a non-byway route. Within the byway
    // and non-byway buckets, sort by pleasantness.
    // (Morgan, route-philosophy pass) Scenic may run up to +30% of the
    // fastest time — beyond that it stops being "the nicer way there" and
    // becomes a different trip. Cap the pool before ranking.
    const scenicCap = safeFastestDur * 1.30;
    // CURATED-GEM EXCEPTION (revertible via SCENIC_CURATED_DETOUR). A route that
    // genuinely DRIVES a curated 'great road' for a real stretch (>= ~0.75 mi)
    // may exceed the usual +30% cap in the SCENIC slot — a deliberate
    // backcountry detour (Round Hill Road) is exactly what Scenic is for. Bounded
    // so it can't balloon: up to ~double the fastest time, +20–45 min.
    const sMode = opts?.mode ?? "driving";
    const curatedMemo = new Map<ScoredRoute, number>();
    const curatedMeters = (s: ScoredRoute): number => {
      if (!CURATED_WINS_SCENIC_SLOT) return 0;
      const cached = curatedMemo.get(s);
      if (cached !== undefined) return cached;
      const t = curatedRoadsTravelled(
        { coordinates: s.route.coordinates, steps: s.route.steps, distance: s.route.distance },
        sMode,
        { minMeters: 1200 },
      );
      const m = t.length ? t[0].meters : 0;
      curatedMemo.set(s, m);
      return m;
    };
    // 2× THE DIRECT TIME, NOT +45 MINUTES (Morgan, Aug 2 — her chosen cap for
    // the "Scenic HAS to take the great road / hug the coast" rule).
    //
    // The old ceiling was fastest + min(45min, …): on Holland → Traverse City
    // the shoreline runs ~66 min over the 2h38 direct, so even when the coastal
    // candidate WAS generated, this line cut it before ranking ever saw it —
    // the third distinct mechanism that killed that trip's coast. On long
    // drives a fixed minute ceiling always shrinks relative to the trip, which
    // is backwards; twice the direct time scales with the drive.
    //
    // NOTE: this is a deliberate exception to the "no time ceiling on Scenic"
    // rule in CLAUDE.md, chosen by Morgan with that rule in front of her —
    // recorded there too, so a future session doesn't "fix" it back.
    const curatedScenicCap = safeFastestDur * 2;
    // SUPER-SCENIC: routes we DELIBERATELY discovered as "the long way" (the
    // along-corridor / coastline / scenic-tag passes, tagged "scenic-corridor")
    // are the hour-plus-no-limit option. They bypass the usual +30% cap — the
    // big extra time IS the offer — bounded only by a sanity guard (3× fastest)
    // so we never surface a genuinely absurd route.
    const isScenicCorridor = (s: ScoredRoute): boolean =>
      getRouteSource(s.route) === "scenic-corridor";
    // COASTAL ROUTES ARE FIRST-CLASS SCENIC (Morgan, Aug 2 — the Holland →
    // Traverse City log that finally settled it: "[coastal] injected routes=1"
    // followed by "scenic/src=valhalla-adventure"). The coastal injection was
    // building the shoreline route and this ranking then treated it as a
    // nobody, because it only recognized two pedigrees: step-confirmed curated
    // meters and "scenic-corridor". A route deliberately threaded down the
    // coast is the strongest scenic claim we have — "if there is a coast
    // within reasonable distance it HAS to hug that coast."
    const isCoastal = (s: ScoredRoute): boolean =>
      getRouteSource(s.route) === "coastal-pch";
    const superScenicCap = safeFastestDur * 3;
    // RULE 1, FINAL FORM (CLAUDE.md, restored Aug 3 — SUPERSEDES the Aug 2
    // comments above, which record the amended era this line retires):
    // "Curated/constructed routes (scenic-road, coastal-pch, scenic-corridor,
    // calm-corridor) have NO duration cap anywhere: scoring eligibility,
    // display filters, none." A route BUILT from curation is eligible at any
    // length — the card shows the honest +hours and the driver decides.
    // The Aug 3 restore missed this gate; found Aug 6 diagnosing Back Bay →
    // New City direct-only (task #63): a Taconic-threaded scenic-road
    // candidate had to step-confirm curated meters AND fit 2×, or fit the
    // anonymous 1.3× — two quiet ways to die on a long trip. Anonymous
    // engine candidates keep scenicCap, exactly as rule 1 prescribes.
    // CURATED_SOURCES / isCuratedSource now live at module scope (see the top
    // of this file). They were defined HERE, locally, which is why the second
    // scenic picker below couldn't see them and fell back to ranking on
    // score alone — handing the Scenic slot to mapbox-pleasant over a curated
    // scenic-road. One definition, both callers (rule 20).
    const scenicEligible = activePool.filter(
      (s) =>
        // HARD BAN (Morgan, Aug 2): a route that travels a road someone marked
        // Avoid can never be the Scenic card. Flag set in scoreRoute where the
        // community avoid penalty fires. If every candidate is banned, the
        // existing empty-pair fallback shows Direct only — by design.
        !(s.route as { userAvoided?: boolean }).userAvoided &&
        (isCuratedSource(s) ||
          s.route.duration <= scenicCap ||
          (curatedMeters(s) > 0 && s.route.duration <= curatedScenicCap) ||
          ((isCoastal(s) || isScenicCorridor(s)) && s.route.duration <= superScenicCap)),
    );
    const rankedScenic = scenicEligible.slice().sort((a, b) => {
      // A genuine curated 'great road' drive — or a deliberately-discovered
      // super-scenic "long way" — wins the Scenic slot outright.
      // The coast outranks everything, including curated inland roads — same
      // precedence the generation side already enforces (COAST OUTRANKS INLAND
      // in generateRoutes). Without this, an inland curated road with big
      // step-confirmed meters could still out-rank the shoreline here.
      const acoast = isCoastal(a) ? 1 : 0;
      const bcoast = isCoastal(b) ? 1 : 0;
      if (acoast !== bcoast) return bcoast - acoast;
      // TIER 2: anything deliberate outranks an anonymous engine candidate.
      // scenic-road belongs here BY PROVENANCE, not only by step-confirmed
      // curated meters. curatedRoadsTravelled needs the step text to match the
      // index, and on long trips it frequently returns nothing — so a route we
      // BUILT from a curated road was scoring as an anonymous candidate and
      // losing tier 2 to a machine corridor before the curated tiebreak below
      // ever ran. (Found Aug 8 by probing the San Jose → Flagstaff shapes:
      // curatedMeters came back empty for a route literally threaded down
      // Historic Route 66.)
      const isCuratedRoad = (s: ScoredRoute) =>
        curatedMeters(s) > 0 || getRouteSource(s.route) === "scenic-road";
      const ac = isCuratedRoad(a) || isScenicCorridor(a) ? 1 : 0;
      const bc = isCuratedRoad(b) || isScenicCorridor(b) ? 1 : 0;
      if (ac !== bc) return bc - ac;
      // TIER 2b — A HUMAN'S ROAD BEATS A MACHINE'S CORRIDOR (rule 16, Morgan,
      // Aug 8). San Jose → Flagstaff: the engine injected Historic Route 66 —
      // the All-American Road, curated by hand — and the Scenic card showed a
      // scenic-corridor instead: 22 hours and 1,000 miles against a 720-mile
      // trip. Curated meters and scenic-corridor sat in the SAME tier above,
      // and the curated-meters comparison below never fires when one side is a
      // corridor (its meters are 0), so the tie fell through to raw pleasant
      // score and the machine won.
      //
      // scenic-corridor / calm-corridor are MACHINE-picked waypoints — the
      // same class the ring filter exists to police. A road a person chose is
      // a stronger scenic claim than a corridor we assembled. Coast still
      // outranks both (tier 1, above), unchanged.
      const acur = isCuratedRoad(a) ? 1 : 0;
      const bcur = isCuratedRoad(b) ? 1 : 0;
      if (acur !== bcur) return bcur - acur;
      const acm = curatedMeters(a);
      const bcm = curatedMeters(b);
      if (acm > 0 && bcm > 0 && Math.abs(acm - bcm) > 400) return bcm - acm;
      const aByway = (a.features.scenicBywayKm ?? 0) > 0 ? 1 : 0;
      const bByway = (b.features.scenicBywayKm ?? 0) > 0 ? 1 : 0;
      if (aByway !== bByway) return bByway - aByway;
      // MEASURED SCENERY DECIDES BETWEEN PEERS (Aug 27 2026).
      //
      // Everything above this line is provenance — coast, curated road,
      // deliberate corridor, designated byway — and none of it moves. What
      // moves is the LAST tie-break, which until now was `rankScore`: the
      // pleasant score, 58% of which is `scenicScore(features)`, built from
      // Overpass counts that fold farmland/meadow/orchard into one `parks`
      // number and report "never looked" as zero (rule 20). Between two
      // same-tier candidates that was the sole decider, and it was the one
      // number on the object we know least about.
      //
      // `compareMeasuredScenery` uses our own coverage-gated layer instead,
      // and only when BOTH routes are measured and the gap clears the same
      // +10%/mile bar the minutes rule uses. Otherwise it returns 0 and
      // rankScore decides exactly as it did yesterday. No candidate is added
      // or removed; no time cap, safety gate or coherence verdict is touched.
      const measured = compareMeasuredScenery(a, b);
      if (measured !== 0) return measured;
      return rankScore(b) - rankScore(a);
    });
    // NO SILENT VERDICTS (rule 17). The measured tie-break can change the
    // Scenic card, so it confesses when it does — naming both routes and the
    // size of the win. Computed by re-ranking WITHOUT the tie-break, which is
    // the only honest way to say "this is what it changed".
    {
      const withoutMeasured = scenicEligible.slice().sort((a, b) => {
        const acoast = isCoastal(a) ? 1 : 0;
        const bcoast = isCoastal(b) ? 1 : 0;
        if (acoast !== bcoast) return bcoast - acoast;
        const isCuratedRoad = (s: ScoredRoute) =>
          curatedMeters(s) > 0 || getRouteSource(s.route) === "scenic-road";
        const ac = isCuratedRoad(a) || isScenicCorridor(a) ? 1 : 0;
        const bc = isCuratedRoad(b) || isScenicCorridor(b) ? 1 : 0;
        if (ac !== bc) return bc - ac;
        const acur = isCuratedRoad(a) ? 1 : 0;
        const bcur = isCuratedRoad(b) ? 1 : 0;
        if (acur !== bcur) return bcur - acur;
        const acm = curatedMeters(a);
        const bcm = curatedMeters(b);
        if (acm > 0 && bcm > 0 && Math.abs(acm - bcm) > 400) return bcm - acm;
        const aByway = (a.features.scenicBywayKm ?? 0) > 0 ? 1 : 0;
        const bByway = (b.features.scenicBywayKm ?? 0) > 0 ? 1 : 0;
        if (aByway !== bByway) return bByway - aByway;
        return rankScore(b) - rankScore(a);
      });
      const before = withoutMeasured[0];
      const after = rankedScenic[0];
      if (before && after && before !== after) {
        const gain = measuredSceneryGainPct(after, before);
        console.log(
          `[scenic-measured] measured scenery moved the Scenic slot: ` +
          `${getRouteSource(before.route) ?? "engine"}/${Math.round(before.route.duration / 60)}m → ` +
          `${getRouteSource(after.route) ?? "engine"}/${Math.round(after.route.duration / 60)}m ` +
          `(+${gain === Infinity ? "∞" : gain}% scenery/mi from our own layer)`,
        );
      }
    }
    // Companion to the pool-side "[coastal] POOL:" line in generateRoutes —
    // together they pin every vanish to one of three verdicts: never reached
    // the pool, reached it but failed eligibility (capped/avoided), or was
    // eligible and lost the ranking. No fourth place left to hide.
    console.log(
      `[coastal] SLOT: inPool=${activePool.filter(isCoastal).length}` +
        ` eligible=${scenicEligible.filter(isCoastal).length}` +
        ` top=${rankedScenic[0] ? getRouteSource(rankedScenic[0].route) ?? "?" : "none"}` +
        ` cap3x=${Math.round(superScenicCap / 60)}m`,
    );
    // WHO WAS EVEN IN THE ROOM (Morgan, Aug 8). On San Jose → Flagstaff the
    // curated-lost confession never fired, which means no scenic-road
    // candidate was ELIGIBLE — but the engine logged three scenic-road
    // injections seconds earlier. So the loss happens upstream of ranking and
    // the slot log has to name the pool, not just the winner.
    {
      const bySource = (list: ScoredRoute[]) => {
        const counts: Record<string, number> = {};
        for (const s of list) {
          const k = getRouteSource(s.route) ?? "engine";
          counts[k] = (counts[k] ?? 0) + 1;
        }
        return Object.entries(counts).map(([k, n]) => `${k}:${n}`).join(" ") || "(none)";
      };
      console.log(
        `[scenic] pool=[${bySource(activePool)}] → eligible=[${bySource(scenicEligible)}]`,
      );
    }
    // CONFESSION (rule 17): when a curated road was eligible and did NOT win
    // the Scenic slot, say so and name what beat it. Silence here is how
    // Route 66 lost to a corridor for a week without anyone knowing.
    {
      const curatedEligible = scenicEligible.filter(
        (s) => curatedMeters(s) > 0 || getRouteSource(s.route) === "scenic-road",
      );
      const winner = rankedScenic[0];
      if (curatedEligible.length > 0 && winner && !curatedEligible.includes(winner)) {
        console.log(
          `[scenic] ⚠ curated road eligible but LOST to ` +
          `${getRouteSource(winner.route) ?? "engine"} — curated: ` +
          curatedEligible
            .map((s) => `${getRouteSource(s.route) ?? "engine"}/${Math.round(s.route.duration / 60)}m`)
            .join(", "),
        );
      }
    }
    const scenicPair = pickDistinctPair(rankedScenic);
    if (scenicPair.length === 0) {
      return withScenic([{ ...safeFastest, classification: "fastest", sameAsFastest: true }]);
    }
    return scenicPair.map((s, i) => ({
      ...s,
      classification: "scenic" as const,
      ...categorizePleasant(s),
      ...(i === 1 ? { variantLabel: "Alternate", variantSub: "A different scenic path", candidateListLabel: true as const } : {}),
    }));
  }

  // ---- "Fastest route" tolerance: honor the label literally. ----
  // When the user picks the Fastest tolerance preset (multiplier ≤ 1.0),
  // the primary card must actually BE the fastest route — not a re-ranked
  // "pleasant" pick that happens to fit within the duration cap. Anything
  // else makes the preset description ("the quickest route") a lie.
  if (pleasantTolerance <= 1.0) {
    return [{ ...fastest, classification: "fastest" }];
  }
  // SCENIC ROUTE — strongly prefer highway-free. Pick from the strictly
  // motorway-free pool when one exists; otherwise fall back to whichever
  // candidate has the LEAST motorway, then break ties by scenic score.
  // The route explainer (edge function) is responsible for honestly framing
  // any unavoidable motorway as "avoided as much as possible".
  const scenicCandidates = deduped.filter((s) => s.features.highwayPct < 5);
  let scenic: ScoredRoute;
  // THE SECOND SCENIC PICKER (Morgan, Aug 2 — the contradiction log: every
  // SLOT line said top=coastal-pch, the diag said scenic:coastal-pch, and the
  // rendered card said valhalla-adventure). This selection path decides the
  // DISPLAYED card on the default-tolerance passes, and its <5% highway
  // filter threw out the coastal thread (22% — the US-31 connectors between
  // shoreline towns). Rule 2's own words cover this: epic highways are exempt
  // "because there the highway IS the attraction" — the shore road is exactly
  // that. Coast wins here too, same precedence as the newer path, 3× bound.
  const coastalScenic = deduped
    .filter(
      (s) =>
        getRouteSource(s.route) === "coastal-pch" &&
        !(s.route as { userAvoided?: boolean }).userAvoided &&
        s.route.duration <= fastestDur * 3,
    )
    .sort((a, b) => a.route.duration - b.route.duration)[0];
  if (coastalScenic) {
    scenic = coastalScenic;
  } else if (scenicCandidates.length > 0) {
    // A HUMAN'S ROAD BEATS A MACHINE'S SCORE (rule 16).
    //
    // This was a straight `max by scores.scenic` across every candidate, which
    // put a curated scenic-road and an anonymous mapbox-pleasant on the same
    // footing and handed the Scenic slot to whichever the scorer happened to
    // like. Coast already won here on PROVENANCE, three lines up; the rest of
    // curation didn't, for no reason anyone chose.
    //
    // Score still decides — but WITHIN the curated tier first. Machine routes
    // only get the slot when no curated candidate survived, which is the same
    // precedence the generation side and the ratchet already enforce.
    const curated = scenicCandidates.filter(isCuratedSource);
    const pool = curated.length > 0 ? curated : scenicCandidates;
    scenic = pool.reduce((a, b) =>
      a.scores.scenic >= b.scores.scenic ? a : b,
    );
    if (curated.length > 0) {
      // NO SILENT VERDICTS (rule 17). This selector contradicted the other one
      // for days before anyone could see it; say what it did and why.
      console.log(
        `[scenic] slot=curated ${getRouteSource(scenic.route)} ` +
          `(${curated.length} curated of ${scenicCandidates.length} candidates)`,
      );
    }
  } else {
    // No truly highway-free option — pick the one with the least motorway,
    // tiebreaking by scenic score so we still surface something pretty.
    scenic = deduped.reduce((a, b) => {
      const am = a.route.motorwayMeters ?? 0;
      const bm = b.route.motorwayMeters ?? 0;
      if (am !== bm) return am < bm ? a : b;
      return a.scores.scenic >= b.scores.scenic ? a : b;
    });
  }
  // EPIC HIGHWAY EXCEPTION: a route that runs a meaningful stretch of a
  // legendary scenic highway is allowed into the scenic & pleasant pools
  // even if its overall highway% is high — that's exactly what makes it epic.
  const isEpic = (s: ScoredRoute) => ((s.route.epicHighwayMeters ?? 0) > 1000 &&
       (!ABLATION.epicProportional ||
        (s.route.epicHighwayMeters ?? 0) >= (s.route.distance ?? 0) * 0.25));
  const fastestDist = Math.min(...deduped.map((s) => s.route.distance));
  // Distance cap (separate from duration cap): even when a long lake-loop
  // detour takes only ~15% more time, it can be 40% more distance — that's
  // a "crazy detour" the user doesn't want at Balanced. Tie the cap to
  // tolerance so Scenic still allows big loops.
  // The most-pleasant route always wins, regardless of detour tolerance —
  // the user sees the time cost on the card's "+X min" pill and decides
  // for themselves. Tolerance still drives how aggressively we hunt for
  // candidates upstream (more scenic anchors at Scenic), but it no longer
  // filters the pleasantPick itself.
  //
  // Real-drive note (#48): "the actually more pleasant route should be
  // the first one suggested regardless of results."
  //
  // The previous Balanced cap (1.15× duration, 1.18× distance) was
  // silently dropping the Tarrytown reservoir route when it landed even
  // a minute or two outside the budget — exactly the route the user
  // would have preferred.
  //
  // We keep a sanity guard on distance (3× the fastest) so a route that
  // genuinely doubles or triples the trip isn't surfaced as the default
  // pick — that would be absurd. But duration is unconstrained: traffic,
  // residential speeds, and stop signs can stretch time without
  // distorting the spatial route the user actually wants.
  const scenicTolEarly = pleasantTolerance >= 1.50;
  const distanceCap = 3.0;
  // Pleasant time budget (#91). Real-drive framing: Pleasant isn't a
  // detour — it's "when there are two ways to get there, take the nicer
  // one." Absolute budget = max(7 min, 15% of trip length):
  //   • short trips → floor of 7 min so a 5-min errand can still swap
  //     onto a 12-min nicer road if one exists
  //   • long trips → 15% of fastest (so a 60-min trip allows +9 min)
  // Scenic ignores the budget (its own multiplier in pleasantTolerance
  // handles the upper bound at 1.80× fastest).
  // (#167) Percentage component widened 15% → 25% so longer backroad
  // alternatives can compete on long trips. Combined with the absolute
  // 15-min floor in generateRoutes.ts, this gives a Hudson-style 90-min
  // trip a 23-min budget for Pleasant alternatives — wide enough for a
  // Route 9 / river-road option to qualify.
  const pleasantBudgetSec = opts?.pleasantBudgetSec;
  // (Morgan, route-philosophy pass) Pleasant cap = fastest + max(+20 min,
  // 20% of the trip). generateRoutes already folds the +20 floor into
  // pleasantBudgetSec; the 20% here keeps long trips consistent with that.
  const absoluteDurationCap = pleasantBudgetSec != null
    ? fastestDur + Math.max(pleasantBudgetSec, 0.20 * fastestDur)
    : Number.POSITIVE_INFINITY;
  const eligible = deduped.filter((s) =>
    s.route.duration <= absoluteDurationCap &&
    s.route.distance <= fastestDist * distanceCap,
  );
  // (#173) Replaced absolute highway% < 25 threshold with a relative
  // "at least 15pp less highway than Direct" rule. Audit Finding #3:
  // on long highway trips (Macpherson 78%, Hudson 100%) no candidate
  // was under 25% so the old fallback returned the un-filtered pool,
  // letting highway-heavy routes win the Pleasant slot. New rule:
  // Pleasant must be meaningfully less-highway than Direct. Direct
  // 78% → Pleasant max 63%. Direct 30% → Pleasant max 15%. Removed
  // the `highwaySafe.length > 0 ? ... : eligible` fallback so when
  // nothing qualifies, the pool stays empty and Pleasant honestly
  // collapses to Direct (via classifyThreeCards' fallbackCard +
  // displayedScored dedup).
  const HIGHWAY_DELTA_PP = 15;
  const directHwy = Math.min(
    100,
    Math.max(
      0,
      ...deduped
        .filter((s) => s.route.duration === fastestDur)
        .map((s) => s.features.highwayPct ?? 0),
      0,
    ),
  );
  const highwayCap = Math.max(0, directHwy - HIGHWAY_DELTA_PP);
  const highwaySafe = avoidHighways
    ? eligible.filter((s) => (s.features.highwayPct ?? 0) <= highwayCap || isEpic(s))
    : eligible;
  // TUNNEL FILTER REMOVED (Morgan, Aug 11): a ≥3% tunnel share was ejecting
  // whole routes from this pool — 3% is nothing in the mountains. See the
  // matching note at the (also removed) tunnel scoring penalty.
  // SCENIC-tolerance arterial gate: at scenic tolerance, exclude any
  // candidate dominated by motorways/major arterials from the pleasant
  // pool. The user spent their detour budget — they get pleasantness across
  // the whole route, not a "fastest with a scenic detour" compromise.
  const scenicTol = pleasantTolerance >= 1.30;
  const baseAfterTunnel = highwaySafe.length > 0 ? highwaySafe : eligible;
  const arterialFiltered = scenicTol
    ? baseAfterTunnel.filter((s) =>
        (isEpic(s)) || (
          s.features.highwayPct < 12 &&
          (s.features.majorRoadPct ?? 0) < 25 &&
          (!s.segmented || (s.segmented.unpleasantDistanceRatio ?? 0) < 0.30)
        ),
      )
    : baseAfterTunnel;
  const isWalking = opts?.mode === "walking";
  const allowOutAndBack = pleasantTolerance >= 1.30 && !isWalking;
  const beforeOverlap = arterialFiltered.length > 0 ? arterialFiltered : baseAfterTunnel;
  // Self-overlap filter (#82): even on Scenic tolerance we cap at
  // 25% — that allows legitimate "drive out to a lookout and back"
  // patterns but rejects routes that loop through the same district
  // three times (the Tarrytown downtown loop bug). Lower tolerances
  // stay at 12%.
  // Even on Scenic ("drive out to the lookout and back"), we now cap at
  // 3% — strict no-circles per Morgan's rule. The legitimate scenic
  // out-and-back (highway pullout) lands well under 3%; a real loop
  // through the same district lands 20%+. No tolerance for circles
  // regardless of setting.
  const overlapCap = 0.03;
  // Tortuosity filter (#84, tightened #86): real-drive ask "no dead-end
  // streets and no loops." Routes whose polyline length is dramatically
  // longer than the great-circle distance start→end are doing wasteful
  // shapes (loops, dead-end visits, downtown spirals) regardless of
  // whether they share polyline coords. Direct route ratio ~1.0,
  // residential backroad ~1.3-1.5, reservoir backroad ~1.5-1.7, loopy
  // downtown ≥ 1.9. Cap at 1.8 — the reservoir route still passes (it
  // sits ~1.5 in our Tarrytown test corridor) but the loopy alternates
  // Morgan saw at 2.0+ get dropped from the pool entirely.
  const TORTUOSITY_HARD_CAP = 1.8;
  const tortuosityOf = (coords: LngLat[]): number => {
    if (coords.length < 2) return 1;
    const polyLen = totalDistance(coords);
    const directLen = haversine(coords[0], coords[coords.length - 1]);
    if (directLen <= 0) return 1;
    return polyLen / directLen;
  };
  const overlapFiltered = beforeOverlap.filter((s) => {
    if (selfOverlapFraction(s.route.coordinates) >= overlapCap) return false;
    if (tortuosityOf(s.route.coordinates) >= TORTUOSITY_HARD_CAP) return false;
    // (#126) Absolute-distance near-return detector — catches small
    // in-town loops that the percentage threshold above misses.
    if (hasNearReturn(s.route.coordinates)) return false;
    // (#136) Sliding-window tortuosity — drops the Tarrytown-rectangle
    // shape (zigzag through a few downtown blocks).
    if (hasNeighborhoodDetour(s.route.coordinates)) return false;
    // (#127) OSM-tagged dead-end detour drop, with start/end exception.
    if (hasDeadEndDetour(s.route.coordinates, deadEndLines)) return false;
    return true;
  });
  let pool = overlapFiltered.length > 0 ? overlapFiltered : beforeOverlap;

  // "Try a different route" support: drop candidates that overlap any of
  // the previously shown routes (so the new pick is geometrically different)
  // and then skip the top N picks so each click steps down to a lower-ranked
  // but still pleasant alternative.
  const excludeCoords = opts?.excludeCoords ?? [];
  const skipRank = Math.max(0, opts?.skipRank ?? 0);
  if (excludeCoords.length > 0) {
    const excludeBuckets = excludeCoords.map((c) => geometryBuckets(c));
    const filtered = pool.filter((s) => {
      const sb = geometryBuckets(s.route.coordinates);
      return !excludeBuckets.some((eb) => jaccard(sb, eb) >= 0.6);
    });
    if (filtered.length > 0) pool = filtered;
  }

  // Google-overlap filter (#91 / #92). Real-drive framing: "Pleasant
  // should try NOT to be the Google route — but somewhat different
  // is OK." If at least 35% of a candidate's polyline is different
  // from Google's, that's acceptable; we only drop candidates that
  // share ≥ 65% of their geometry with Google. This is more lenient
  // than the original 60% threshold, per Morgan's framing — we don't
  // need a wildly-different route, just a visibly-different one.
  // Falls back to the unfiltered pool if every candidate is too
  // similar (so we still surface SOMETHING honestly).
  const GOOGLE_OVERLAP_THRESHOLD = 0.65;
  const googleCoords = opts?.googleCoords;
  if (googleCoords && googleCoords.length >= 2 && !scenicTol) {
    const googleBuckets = geometryBuckets(googleCoords);
    const nonGoogle = pool.filter((s) => {
      const sb = geometryBuckets(s.route.coordinates);
      return jaccard(sb, googleBuckets) < GOOGLE_OVERLAP_THRESHOLD;
    });
    if (nonGoogle.length > 0) pool = nonGoogle;
  }
  // EPIC-HIGHWAY PRIORITY: when the user is at scenic tolerance and a
  // candidate runs a meaningful stretch of a legendary highway (PCH/CA-1,
  // Blue Ridge Parkway, etc.), it should be the pleasant pick — that's
  // exactly the trip they asked for. Otherwise the inland low-highway
  // alternative often wins on raw pleasant score even though the coast is
  // the whole point. Within the epic pool we still rank by pleasantness so
  // we pick the longest/best epic stretch.
  const ranked = pool.slice().sort((a, b) => {
    if (scenicTol) {
      const ae = isEpic(a) ? 1 : 0;
      const be = isEpic(b) ? 1 : 0;
      if (ae !== be) return be - ae;
      if (ae === 1 && be === 1) {
        const am = a.route.epicHighwayMeters ?? 0;
        const bm = b.route.epicHighwayMeters ?? 0;
        if (Math.abs(am - bm) > 500) return bm - am;
      }
    }
    return rankScore(b) - rankScore(a);
  });
  const pickIdx = Math.min(skipRank, ranked.length - 1);
  const pleasant = ranked[pickIdx] ?? ranked[0];

  // We never surface a "Fastest" card on its own. The user always wants the
  // pleasant route. If the most-pleasant pick happens to BE the fastest
  // route (or no real alternative cleared the score bar), we relabel it as
  // pleasant rather than showing a separate "fastest" entry.
  const fastestBuckets = geometryBuckets(fastest.route.coordinates);
  const pleasantBuckets = geometryBuckets(pleasant.route.coordinates);
  const isSameRoute =
    pleasant.route === fastest.route ||
    jaccard(fastestBuckets, pleasantBuckets) >= 0.8;

  // Honesty guardrail: if our best "pleasant" candidate didn't clear the
  // score bar, fall back to the fastest route but still label it pleasant.
  const minScore =
    pleasantTolerance >= 1.30 ? 42 :
    pleasantTolerance >= 1.20 ? 50 :
    MIN_PLEASANT_SCORE;
  // If every candidate is objectively mediocre, still pick the least-bad
  // route instead of falling back to the fastest one. This matters most for
  // suburban walks where the direct route is a long arterial stretch and the
  // safer side-street/trail route may score below the normal "pleasant" bar.
  const improvesOnFastest = pleasant.scores.pleasant > fastest.scores.pleasant + 8;
  const meetsScoreBar = pleasant.scores.pleasant >= minScore || improvesOnFastest;
  const highwayOk =
    !avoidHighways || highwaySafe.length > 0 || pleasant.features.highwayPct < 25 || isEpic(pleasant);
  // (Morgan, recurring: "the fastest route is obviously the pleasant route in
  // this case.") The fastest route is often ALREADY the nicest way. Only surface
  // a DISTINCT Pleasant route when it genuinely beats fastest on a real
  // dimension — meaningfully less main-road/highway, OR a meaningfully higher
  // pleasant score. If it beats fastest on neither, the fastest IS the pleasant
  // route: collapse onto it ("Fastest & Pleasant") instead of manufacturing a
  // longer, busier alternative just to fill the slot.
  const fastArterialShare = (fastest.features.highwayPct ?? 0) + (fastest.features.majorRoadPct ?? 0);
  const pickArterialShare = (pleasant.features.highwayPct ?? 0) + (pleasant.features.majorRoadPct ?? 0);
  const genuinelyBetterThanFastest =
    pickArterialShare <= fastArterialShare - 5 ||
    pleasant.scores.pleasant >= fastest.scores.pleasant + 3;
  const pleasantWorthShowing =
    !suppressPleasant && meetsScoreBar && highwayOk && !isSameRoute && genuinelyBetterThanFastest;

  // Prefer ANY genuinely-different alternative over the direct route, even
  // when the top-ranked pleasant candidate happens to share its geometry
  // with fastest. We only collapse to "fastest" if there is literally no
  // other reasonable line in the pool. This keeps Balanced from showing
  // the same polyline as the direct route unless utterly necessary.
  let pleasantPick = pleasantWorthShowing ? pleasant : fastest;
  // Scenic/Adventure should never silently fall back to the direct arterial
  // if a less-bad alternative exists. Previous fixes lowered US/state-route
  // scores, but this fallback still re-labeled the fastest US-9 route as
  // "pleasant" whenever every candidate missed the normal score bar.
  if (scenicTol && !isEpic(fastest)) {
    const fastestHwy = fastest.features.highwayPct;
    const leastArterial = deduped
      .filter((r) => r.route.duration <= fastestDur * pleasantTolerance && r.route.distance <= fastestDist * distanceCap)
      .filter((r) => r !== fastest)
      .sort((a, b) => {
        const ah = a.features.highwayPct;
        const bh = b.features.highwayPct;
        if (Math.abs(ah - bh) > 2) return ah - bh;
        return rankScore(b) - rankScore(a);
      })[0];
    if (leastArterial && fastestHwy >= 12 && leastArterial.features.highwayPct < fastestHwy - 5) {
      pleasantPick = leastArterial;
    }
  }
  if (!suppressPleasant && (!pleasantWorthShowing || isSameRoute)) {
    const minAltScore = Math.max(MIN_PLEASANT_SCORE - 10, 30);
    const alt = ranked.find((r) => {
      if (r.route === fastest.route) return false;
      const rb = geometryBuckets(r.route.coordinates);
      if (jaccard(rb, fastestBuckets) >= 0.7) return false;
      if (avoidHighways && r.features.highwayPct >= 25 && !isEpic(r)) return false;
      return r.scores.pleasant >= minAltScore;
    });
    if (alt) pleasantPick = alt;
  }

  // ---- "Same as Fastest" diagnostics ----
  // When the pleasant pick collapsed onto the fastest route (no real
  // alternative survived), capture WHY so the UI can render an honest
  // explanation instead of two cards that look identical.
  const collapsedToFastest =
    pleasantPick.route === fastest.route ||
    jaccard(geometryBuckets(pleasantPick.route.coordinates), fastestBuckets) >= 0.8;
  let sameAsFastestReason: ClassifiedRoute["sameAsFastestReason"] | undefined;
  if (collapsedToFastest) {
    // Top non-fastest candidate (by pleasant score) for diagnostics.
    const altOnly = ranked.filter((r) => r.route !== fastest.route);
    const bestAlt = altOnly[0];
    const bestAltOverlap = bestAlt
      ? jaccard(geometryBuckets(bestAlt.route.coordinates), fastestBuckets)
      : undefined;
    let code: NonNullable<ClassifiedRoute["sameAsFastestReason"]>["code"] = "unknown";
    if (deduped.length <= 1) {
      code = "no_alternatives";
    } else if (bestAlt && bestAltOverlap != null && bestAltOverlap >= 0.8) {
      code = "polyline_match";
    } else if (pool.length <= 1 || (avoidHighways && highwaySafe.length === 0)) {
      code = "filtered_out";
    } else if (bestAlt && bestAlt.scores.pleasant < minScore) {
      code = "score_degenerate";
    } else if (bestAlt) {
      // Survived filters & passed the bar but still lost to fastest's score.
      code = "score_degenerate";
    }
    sameAsFastestReason = {
      code,
      candidateCount: deduped.length,
      bestAltPleasantScore: bestAlt?.scores.pleasant,
      minPleasantScore: minScore,
      bestAltOverlap,
    };
  }

  const out: ClassifiedRoute[] = [];
  out.push({
    ...pleasantPick,
    classification: "pleasant",
    ...categorizePleasant(pleasantPick),
    ...(collapsedToFastest ? { sameAsFastest: true, sameAsFastestReason } : {}),
  });

  // ---- LONG-DISTANCE: surface a SECOND pleasant variant when one exists. ----
  // For long trips (e.g. Portland → SF) Mapbox often returns multiple
  // genuinely different pleasant routes — a coastal one, an inland mountain
  // one, etc. Instead of forcing the user to pick "Pleasant" or "Scenic",
  // we offer them as labeled variants ("Ocean view", "Mountain pass").
  const longTrip = pleasantPick.route.distance >= 200_000; // ~125 mi
  let secondPleasant: ScoredRoute | null = null;
  if (longTrip && pleasantWorthShowing) {
    const pleasantBuckets2 = geometryBuckets(pleasantPick.route.coordinates);
    const pickedCat = categorizePleasant(pleasantPick).variantLabel;
    // Candidates: high-scoring, not the same geometry, ideally a different
    // landscape category from the picked one.
    const candidates = pool
      .filter((s) => s.route !== pleasantPick.route)
      .filter((s) =>
        jaccard(geometryBuckets(s.route.coordinates), pleasantBuckets2) < 0.6,
      )
      .filter((s) => s.scores.pleasant >= Math.max(minScore, pleasantPick.scores.pleasant - 12));
    // Prefer one with a DIFFERENT landscape category.
    const differentCat = candidates.find(
      (c) => categorizePleasant(c).variantLabel !== pickedCat,
    );
    secondPleasant = differentCat ?? candidates[0] ?? null;
  }
  if (secondPleasant) {
    out.push({ ...secondPleasant, classification: "pleasant", ...categorizePleasant(secondPleasant) });
  }

  // Add Scenic only when it is meaningfully different from the pleasant
  // pick (different geometry) AND not the same as the fastest stand-in.
  // Skip Scenic when we already surfaced a 2nd pleasant variant (would be
  // redundant on a long trip with two flavors of pleasantness).
  if (!secondPleasant) {
    // SCENIC must feel meaningfully more of an adventure than BOTH the
    // fastest and the pleasant pick. Distinctness is measured against
    // fastest (the user's mental baseline) and the duration floor is
    // anchored to fastest, not pleasant — so a scenic card always reads
    // as a real detour even when pleasant ≈ fastest.
    const fastestDur = fastest.route.duration;
    // Require at least +25% over fastest, AND at least +33% over Wander —
    // so Wander is ~25% shorter than Adventure (1 / 1.33 ≈ 0.75). This
    // creates a clear time hierarchy: Direct < Wander < Adventure, with
    // Wander always materially shorter than the scenic alternative.
    const minScenicDur = Math.max(fastestDur * 1.25, pleasantPick.route.duration * 1.33);
    const fastestB = fastestBuckets;
    const pleasantB = geometryBuckets(pleasantPick.route.coordinates);
    // Tightened: scenic must share <35% geometry with fastest, <45%
    // with pleasant. Sharing half a route with the direct line is what
    // made scenic feel like "fastest with a wiggle".
    const isDistinctFromBoth = (s: ScoredRoute) => {
      const sb = geometryBuckets(s.route.coordinates);
      return jaccard(sb, fastestB) < 0.35 && jaccard(sb, pleasantB) < 0.45;
    };
    // A route we DELIBERATELY discovered as the scenic "long way" (along-corridor
    // / coastline / scenic-tag, tagged "scenic-corridor") IS the scenic option by
    // construction — it bypasses the computed scenic-score floor (it's only
    // cheap-scored in the background pass, so its `scenic` number understates a
    // genuine coastal drive). It still must be distinct from the other picks and
    // genuinely longer, so we don't show a duplicate or a shorter route.
    const isScenicCorridorSrc = (s: ScoredRoute) => getRouteSource(s.route) === "scenic-corridor";
    // Scenic must actually run BACK ROADS — not just be "pretty on average."
    // scores.scenic does NOT penalize arterials, so a route that touches the
    // river but spends a stretch on North Broadway / Route 9 (both tagged
    // `secondary` in OSM → they land in majorRoadPct, invisible to highwayPct)
    // can win the slot on scenic score alone (Morgan: "why are we taking N
    // Broadway on scenic"). Fold arterial exposure into the ranking so a
    // genuine back-road alternative outranks an arterial one at comparable
    // scenic score — while still degrading gracefully when the corridor truly
    // has no low-arterial option (the adjustment only ever LOWERS arterial
    // routes; it never promotes a junk route).
    // SCENIC = the NOT-HIGHWAY route, ranked by PERCENTAGE. Morgan's rule, in
    // her words: "it's scenic — it has no cap on time — highways should not be
    // part of it. It doesn't even need to be coastal, it just has to be the
    // not-highway mode. Weight percentage of these things: a short stretch on
    // the coast but on highways the whole time is NOT better than all back
    // roads." So Scenic is simply the distinct, real-detour, non-looping
    // candidate with the HIGHEST back-road fraction — i.e. the LOWEST highway %.
    // No scenic-score / coastal / curated bonus. No upper time cap (minScenicDur
    // is only a lower bound so Scenic is a real detour). If the corridor has no
    // genuinely low-highway option, Scenic is OMITTED rather than shown as a
    // highway route.
    // SCENIC = the avoid-highways route. Morgan: "why can't it just be the avoid
    // highway route." So it is: the app already generates the avoid-highways
    // route — Valhalla's "adventure" (max highway-avoidance) costing, plus the
    // exclude=motorway Mapbox variant. Scenic simply IS that route (whichever
    // avoids highways most), as long as it's distinct, a real detour, and not a
    // loop. NO absolute or relative highway cap, no time cap. If the avoid-
    // highways engine returned nothing distinct, we fall back to the lowest-
    // highway route in the pool — still "the most avoid-highways option."
    const highwayPctOf = (s: ScoredRoute) => s.features.highwayPct ?? 0;
    const isAvoidHwySrc = (s: ScoredRoute) => {
      const src = getRouteSource(s.route);
      return src === "valhalla-adventure" || src === "mapbox-pleasant";
    };
    const scenicCandidates2 = deduped
      .filter((s) => s.route !== pleasantPick.route && s.route !== fastest.route)
      .filter(isDistinctFromBoth)
      .filter((s) => s.route.duration >= minScenicDur)
      // No loops / spurs.
      .filter((s) => selfOverlapFraction(s.route.coordinates) < 0.03);
    let scenicPick: ScoredRoute | undefined =
      scenicCandidates2.sort((a, b) => {
        // The dedicated avoid-highways route wins outright.
        const aa = isAvoidHwySrc(a) ? 1 : 0;
        const bb = isAvoidHwySrc(b) ? 1 : 0;
        if (aa !== bb) return bb - aa;
        // Otherwise the route that avoids highways most (lowest %), then longer.
        const h = highwayPctOf(a) - highwayPctOf(b);
        if (Math.abs(h) > 2) return h;
        return b.route.duration - a.route.duration;
      })[0];
    // No relaxed fallback: Scenic is the not-highway route or nothing. If no
    // candidate cleared the highway gate above, we omit Scenic rather than show
    // a highway route as "scenic".
    //
    // Hard guarantee: Adventure (scenic) must be a different physical route
    // than Wander (pleasant) AND meaningfully longer. If our pick fails
    // either test, drop it — better to show no Adventure than to show the
    // same route twice or one that's shorter than Wander.
    if (scenicPick) {
      const sb = geometryBuckets(scenicPick.route.coordinates);
      const sameAsPleasant = jaccard(sb, pleasantB) >= 0.55;
      const notLonger = scenicPick.route.duration < pleasantPick.route.duration * 1.33;
      if (sameAsPleasant || notLonger) scenicPick = undefined;
    }
    if (scenicPick) {
      out.push({ ...scenicPick, classification: "scenic", ...categorizePleasant(scenicPick) });
    }
    // If nothing meaningfully different & longer exists, omit Scenic
    // rather than mislabel a near-direct route.
  }

  // FALLBACK SECOND CARD: the user always wants at least 2 options to
  // compare. If we only surfaced the pleasant card so far, look for ANY
  // geometrically-distinct alternative in the deduped pool and offer it as
  // a "fastest" (shorter than pleasant) or "scenic" (longer than pleasant)
  // companion. Better to show a slightly-imperfect alternative than to
  // leave the user staring at a single card.
  if (out.length === 1 && deduped.length >= 2) {
    const pickedRoute = out[0].route;
    const pickedB = geometryBuckets(pickedRoute.coordinates);
    const alt = deduped
      .filter((s) => s.route !== pickedRoute)
      .filter((s) => jaccard(geometryBuckets(s.route.coordinates), pickedB) < 0.7)
      .sort((a, b) => rankScore(b) - rankScore(a))[0];
    if (alt) {
      const isShorter = alt.route.duration < pickedRoute.duration;
      const altClass: Classification = isShorter ? "fastest" : "scenic";
      const cat = categorizePleasant(alt);
      // Only call it "Fastest" if it actually IS the fastest route in the
      // pool. Otherwise it's just a quicker-than-Wander companion — labeling
      // a slower-than-fastest route "Fastest" is a credibility killer.
      const isTrulyFastest =
        isShorter &&
        (alt.route === fastest.route ||
          jaccard(geometryBuckets(alt.route.coordinates), fastestBuckets) >= 0.8);
      const qo = getRouteCopy().variant.quickerOption;
      const overrides =
        isShorter && !isTrulyFastest
          ? { variantLabel: cat.variantLabel ?? qo.label, variantSub: qo.sub }
          : cat;
      out.push({ ...alt, classification: altClass, ...overrides });
    }
  }

  // SUPER-SCENIC GUARANTEE (Morgan): a route we DELIBERATELY discovered as the
  // scenic "long way" (tagged scenic-corridor — the along-corridor / coastline /
  // scenic-tag passes) must be offerable even when there's NO Pleasant pick.
  // Otherwise a long highway corridor like Brunswick→Boston shows only Direct
  // despite a genuine coastal alternative sitting in the pool. This runs last,
  // independent of every pleasant-dependent gate above: if no Scenic card exists
  // yet, surface the best scenic-corridor route that's distinct from Direct and
  // meaningfully longer.
  // (Scenic guarantee now lives in the ACTIVE per-tolerance block above, via
  // withScenic(). This legacy body is unreachable — see note at its top.)

  return out;
}

/** Inspect a route's features and return a short, human label describing
 *  its dominant landscape character ("Ocean view", "Mountain pass",
 *  "Forest drive", etc). Used to label multiple pleasant variants. */
function categorizePleasant(s: ScoredRoute): { variantLabel?: string; variantSub?: string } {
  const f = s.features;
  const r = s.route;
  // All localized phrasing pulled from the central dictionary so EN/ES/FR
  // labels stay aligned with the explanation sentences and card titles.
  const t = getRouteCopy();
  const v = t.variant;

  // Epic highway always wins the label slot.
  if ((r.epicHighwayMeters ?? 0) > 1000 && r.epicHighwayLabels?.length) {
    return { variantLabel: r.epicHighwayLabels[0], variantSub: v.epicHighway.sub };
  }

  const named = (arr?: string[]) => (arr && arr.length ? arr[0] : undefined);
  const waterName = named(f.waterNames);
  const natureName = named(f.natureNames);
  const historicName = named(f.historicNames);
  const parkName = named(f.parkNames);

  // Score each landscape category; highest wins.
  // Pick a water label honest to what the route actually hugs. Ocean/sea
  // only when the named body really is one — otherwise call it a river,
  // lake, canal, or generic "waterfront". Saying "Ocean view" for a river
  // is a credibility-killer.
  // Keyword detection runs against the ENGLISH name (OSM tags are English),
  // so the lookup stays language-agnostic; only the surfaced label/sub are
  // localized.
  const waterKind = ((): { label: string; sub: string } => {
    const name = waterName ?? "";
    const lc = name.toLowerCase();
    const hasAny = (kw: string[]) => kw.some((k) => lc.includes(k));
    if (hasAny(["ocean", "sea", " bay", "gulf", "strait", "sound", "channel", "harbor", "harbour", "atlantic", "pacific", "mediterranean"])) {
      return { label: v.waterKind.ocean.label, sub: v.waterKind.ocean.sub(name) };
    }
    if (hasAny(["river", "creek", "brook", "stream", "fork", "bayou"])) {
      return { label: v.waterKind.river.label, sub: v.waterKind.river.sub(name) };
    }
    if (hasAny(["lake", "reservoir", "pond", "loch", "lagoon"])) {
      return { label: v.waterKind.lake.label, sub: v.waterKind.lake.sub(name) };
    }
    if (hasAny(["canal", "aqueduct"])) {
      return { label: v.waterKind.canal.label, sub: v.waterKind.canal.sub(name) };
    }
    return { label: v.waterKind.generic.label, sub: v.waterKind.generic.sub(name) };
  })();
  // Helper: turn a long named feature into a short, card-friendly label.
  const shortName = (n: string) => {
    const trimmed = n.replace(/\s+/g, " ").trim();
    return trimmed.length > 26 ? trimmed.slice(0, 24).trimEnd() + "…" : trimmed;
  };
  const scores: { label: string; sub: string; score: number }[] = [
    {
      // Prefer the named body in the LABEL itself when we have one — the
      // user wants to read "Along Kensico Reservoir" on the card, not a
      // generic "Lakeside" tag.
      label: waterName ? v.waterNamed(shortName(waterName)) : waterKind.label,
      sub: waterKind.sub,
      score: (f.waterPct ?? 0) * 0.6 + (f.waterNames?.length ?? 0) * 8,
    },
    {
      label: natureName ? v.forestNamed(shortName(natureName)) : v.forest.label,
      sub: natureName ? v.forestNamedSub(natureName) : v.forest.sub,
      score: (f.natureNames?.length ?? 0) * 6 + (f.parks ?? 0) * 2,
    },
    {
      label: parkName ? v.parksNamed(shortName(parkName)) : v.parks.label,
      sub: parkName ? v.parksNamedSub(parkName) : v.parks.sub,
      score: (f.parkNames?.length ?? 0) * 5 + (f.parks ?? 0) * 3,
    },
    {
      label: historicName ? v.historicNamed(shortName(historicName)) : v.historic.label,
      sub: historicName ? v.historicNamedSub(historicName) : v.historic.sub,
      score: (f.historicNames?.length ?? 0) * 6 + (f.landmarks ?? 0) * 2,
    },
    {
      label: v.backroads.label,
      sub: v.backroads.sub,
      // Only count as Backroads when the ROUTE itself is on quiet roads.
      // Corridor-OSM residential% can be high even on highway routes
      // because nearby side streets fall inside the buffer.
      score: (f.highwayPct >= 25 || (f.majorRoadPct ?? 0) >= 35)
        ? 0
        : Math.max(0, f.residentialPct - 50) * 0.4,
    },
  ];
  scores.sort((a, b) => b.score - a.score);
  const top = scores[0];
  // Lower the bar — even a modest named-feature signal earns a real label
  // (the user explicitly asked for route names that say what you'll see).
  if (top.score < 2) return {};
  return { variantLabel: top.label, variantSub: top.sub };
}

export { bbox };
